import {
  parentPortalMainChapters,
  studentPortalMainChapters,
  portalParentLeavesChapter,
  portalStudentLeavesChapter,
  portalParentAttendanceChapter,
  portalStudentAttendanceChapter,
  teacherDashboardChapter,
  teacherStudentsChapter,
  teacherParentsChapter,
  teacherGuardiansChapter,
  teacherMyLeaveChapter,
  teacherStudentLeavesChapter,
  teacherProfileChapter,
} from './portalGuideChapters.js';
import { tImg } from './teacherPortalGuideMedia.js';

const moduleScreenChapter = ({ number, title, intro, instruction, sections }) => ({
  number,
  title,
  intro,
  instruction,
  sections: sections.map((section, index) => ({
    number: `${number}.${index + 1}`,
    title: section.title,
    path: section.path,
    image: section.image,
    imageAlt: section.imageAlt || `${section.title} callouts — SkoolWeb administrator screen`,
    summary: section.summary,
  })),
});

const listModalGuideChapter = ({
  number,
  title,
  intro,
  instruction,
  listTitle,
  modalTitle,
  imageBase,
  listCallouts,
  modalCallouts,
  listSummary,
  modalSummary,
}) => ({
  number,
  title,
  intro,
  instruction,
  sections: [
    {
      number: `${number}.1`,
      title: listTitle,
      image: `/user-guides/admin/${imageBase}-annotated-1.png?v=designer`,
      imageAlt: `${listTitle} callouts ${listCallouts}`,
      summary: listSummary,
    },
    {
      number: `${number}.2`,
      title: modalTitle,
      image: `/user-guides/admin/${imageBase}-annotated-2.png?v=designer`,
      imageAlt: `${modalTitle} callouts ${modalCallouts}`,
      summary: modalSummary,
    },
  ],
});

export const USER_GUIDE_ROLES = [
  {
    id: 'admin',
    label: 'School Administrator',
    subtitle: 'Headmaster / Admin \u2014 full school management',
    icon: '\u{1F3EB}',
  },
  {
    id: 'teacher',
    label: 'Teacher',
    subtitle: 'Class teacher \u2014 attendance, routine, students',
    icon: '\u{1F469}\u200D\u{1F3EB}',
  },
  {
    id: 'parent',
    label: 'Parent',
    subtitle: 'Parent / guardian \u2014 child progress & notices',
    icon: '\u{1F468}\u200D\u{1F469}\u200D\u{1F467}',
  },
  {
    id: 'student',
    label: 'Student',
    subtitle: 'Student portal \u2014 timetable, notices, profile',
    icon: '\u{1F393}',
  },
];

const loginChapter = {
  number: '1',
  title: 'Login Page',
  intro:
    'Every school user starts here. The institute number selects the school, and the username and password open that person\u2019s account.',
  sections: [
    {
      image: '/user-guides/admin/login-page.png?v=hires',
      imageAlt: 'SkoolWeb login page with institute number, username, password, Remember Me, and Sign In',
      summary:
        'Sign in with your school\u2019s institute number, then your username, phone, or email, and your password. Remember Me keeps this browser signed in and fills the institute number and username next time, but it does not store the password. Sign In opens your home screen only when those details match. A disabled account cannot sign in, and if one phone or email belongs to more than one account, use the username.',
    },
  ],
};

const todoChapter = {
  number: '3',
  title: 'To Do',
  intro:
    'To Do is your personal task manager, like a private note you keep for yourself. It is not connected to the school or to any other module. Only you can see the tasks you add here.',
  sections: [
    {
      number: '3.1',
      title: 'Inbox, Done, Important, and Trash',
      image: '/user-guides/admin/todo-1-4.jpg?v=hires',
      imageAlt: 'To Do callouts 1 to 4: Inbox, Done, Important, and Trash',
      summary:
        'Inbox is the work still open. Finished tasks and deleted tasks are kept out of it. Done holds what you have completed, Important holds the tasks you flagged, and Trash holds deleted tasks until you restore them or remove them for good.',
    },
    {
      number: '3.2',
      title: 'Medium, High, Low, and Add Task',
      image: '/user-guides/admin/todo-5-8.jpg?v=hires',
      imageAlt: 'To Do callouts 5 to 8: Medium, High, Low, and Add Task',
      summary:
        'Medium, High, and Low narrow the folder you already have open, so you see only that priority. Click the same priority again to show every task in that folder. Add Task opens a blank form for a new personal task.',
    },
    {
      number: '3.3',
      title: 'Hide or show tasks, Mark Done, Task menu, and Refresh and Print',
      image: '/user-guides/admin/todo-9-12.jpg?v=hires',
      imageAlt: 'To Do callouts 9 to 12: hide or show tasks, Mark Done, the task menu, and Refresh and Print',
      summary:
        'The arrow beside a date folds that day\u2019s tasks away. Mark Done asks you to confirm, then moves the task to Done; on a finished task the same button sends it back to Inbox. The task menu edits the task, flags it as important, opens it, or moves it to Trash. Refresh reloads the list, and Print prints only what is on screen.',
    },
    {
      number: '3.4',
      title: 'Title, Priority, Status, Due Date, Mark as important, Description, Submit, and Cancel',
      image: '/user-guides/admin/todo-13-20.jpg?v=hires',
      imageAlt: 'Add Task form callouts 13 to 20: Title, Priority, Status, Due Date, Mark as important, Description, Submit, and Cancel',
      summary:
        'Give the task a title and a due date. Both are required, and the due date cannot be in the past. Priority and status describe the task, while marking it important and the description are optional. Submit saves it to your personal list, and Cancel closes the form without saving.',
    },
  ],
};

const academicYearsChapter = {
  number: '4',
  title: 'Academic Years',
  intro:
    'Academic Years are the sessions your school works in. The year marked Current is the one used for new admissions, fees, and attendance. Create a year and mark one as Current before you use the other school modules. If no year exists, or none is Current, you cannot add, edit, or delete records in those modules.',
  sections: [
    {
      number: '4.1',
      title: 'Create new academic year, Status, and View details',
      image: '/user-guides/admin/academic-year-1-3.png?v=hires',
      imageAlt: 'Academic Years callouts 1 to 3: Create new academic year, Status, and View details',
      summary:
        'Create new academic year opens the form for the next session. Active years appear in the year menu at the top of the school. Current is the one session used for new admissions, fees, and attendance, and only one year can be current. View details opens that year so you can edit or delete it, but the current year cannot be deleted until another year is made current, and delete asks for your password.',
    },
    {
      number: '4.2',
      title: 'Year name, dates, current year, Active, and copy from the previous year',
      image: '/user-guides/admin/academic-year-4-8.png?v=hires',
      imageAlt: 'Create academic year callouts 4 to 8: Year name, start and end date, Mark as current, Active, and Copy data from the previous year',
      summary:
        'Name the session, such as 2026-27, and choose a start and end that do not overlap another year. If the latest year has no end date yet, set that date before you create the next one. Marking this year current moves new admissions, fees, and attendance onto it, and it must stay active so people can select it. Copy from the previous year can bring classes, sections, subjects, teacher assignments, and the timetable across, but each later item needs the ones before it.',
    },
  ],
};

const homeworkSections = [
  {
    number: '5.1',
    title: 'Add Home Work, Filter, and row actions',
    image: '/user-guides/admin/homework-1-3.jpg?v=hires',
    imageAlt: 'Home Work list callouts 1 to 3: Add Home Work, Filter, and View and grade, Edit, Delete',
    summary:
      'The list is homework for the year in the header. Filter does not change the list until you press Apply, and choosing another class clears the section and subject. A teacher sees only classes assigned to them and can change or remove only their own homework. An administrator can change any row. Delete takes that homework away from every student who received it.',
  },
  {
    number: '5.2',
    title: 'Class and assignment, who receives it, and Title',
    image: '/user-guides/admin/homework-4-6.png?v=hires',
    imageAlt: 'Add Home Work callouts 4 to 6: Class and assignment, Assign to, and Title',
    summary:
      'The class list is only teacher assignments for the year in the header, so a class with no subject teacher cannot be used. Who receives the work stays locked until that class is chosen. The whole section includes every active student there. Picking names sends it only to the students you tick, and you must tick at least one. The title is required.',
  },
  {
    number: '5.3',
    title: 'Type, dates, description, and Publish',
    image: '/user-guides/admin/homework-7-10.png?v=hires',
    imageAlt: 'Add Home Work callouts 7 to 10: Type, assign and due dates, description and instructions, and Publish immediately',
    summary:
      'Type only labels the work as homework, an assignment, or a project. The assign date cannot be before today, and the due date cannot be before the assign date. Students and parents can read the description and instructions, and both can be left blank. Publish now makes it visible. Left off, it stays a draft, and a draft is hidden from students until you publish it from the homework page.',
  },
  {
    number: '5.4',
    title: 'Attachments, grading, and late work',
    image: '/user-guides/admin/homework-11-14.png?v=hires',
    imageAlt: 'Add Home Work callouts 11 to 14: Attachments, Graded, max marks and attempts, and resubmission',
    summary:
      'Files attached here go out with the homework, up to 20 files of 10MB each. Turn Graded off when the work is not marked, and then max marks is not asked for. A student can send the work only up to the attempt limit, which starts at 1. Resubmission and late submission start allowed, so a student can send it again and can still send it after the due date unless you turn those off.',
  },
  {
    number: '5.5',
    title: 'Close, summary, and attachments',
    image: '/user-guides/admin/homework-15-17.png?v=hires',
    imageAlt: 'Home Work detail callouts 15 to 17: Close, summary, and attachments',
    summary:
      'Close appears only while the homework is published. After you confirm, the status becomes Closed and students can no longer submit. The counts are students who were assigned it, students who have sent work, and work still waiting for marks. A saved file can be opened, and files can be added or removed one at a time.',
  },
  {
    number: '5.6',
    title: 'Submissions, View, Grade, and Return',
    image: '/user-guides/admin/homework-18-19.png?v=hires',
    imageAlt: 'Home Work submissions callouts 18 and 19: the submission list, View, Grade, and Return',
    summary:
      'Each row is one student attempt, including whether it arrived after the due date. View only opens the answer and files. Grade stores the marks, which cannot go above the maximum set on the homework, and marks that attempt evaluated. Return asks the student to send the work again and can include a note about what to correct.',
  },
];

const homeworkChapter = (intro, instruction, sections = homeworkSections) => ({
  number: '5',
  title: 'Home Work',
  intro,
  instruction,
  sections,
});

const adminHomeworkIntro =
  'Home Work is classwork you set for students, for the academic year selected in the header.';

const adminHomeworkInstruction =
  'Set a current academic year, then create the class and its section, assign a subject to that class, and assign a teacher to that class, section, and subject. Without that teacher assignment, Add Home Work stays blocked.';

const teacherHomeworkIntro =
  'Home Work is classwork you set for the classes you teach, for the academic year selected in the header.';

const teacherHomeworkInstruction =
  'You can add homework only for a class, section, and subject already assigned to you. If the list is empty, ask the administrator to assign you, or switch the header to the year where you are assigned.';

const meetingsSections = (introSummary, pageSections, introImage) => [
  {
    number: '7.1',
    title: 'All Meetings, My Meetings, and Assigned Tasks',
    image: introImage || '/user-guides/admin/meetings-1-3.png?v=hires',
    imageAlt: 'Meetings callouts 1 to 3: All Meetings, My Meetings, and Assigned Tasks',
    summary: introSummary,
  },
  ...pageSections,
];

const meetingsChapter = (intro, instruction, introSummary, pageSections, introImage) => ({
  number: '7',
  title: 'Meetings',
  intro,
  instruction,
  sections: meetingsSections(introSummary, pageSections, introImage),
});

const adminAllMeetingsSections = [
  {
    number: '7.2',
    title: 'Create Meeting, filters, and View or Delete',
    image: '/user-guides/admin/all-meetings-1-3.png?v=hires',
    imageAlt: 'All Meetings callouts 1 to 3: Create Meeting, filters, and View or Delete',
    summary:
      'Create Meeting opens the form. This list is every meeting in the school. Search matches the title as you type, and category, status, and audience narrow the list straight away. Reset clears those choices. View opens the meeting. Delete asks you to confirm, then removes it.',
  },
  {
    number: '7.3',
    title: 'Title, category, audience, date, venue, description, and Create',
    image: '/user-guides/admin/create-meeting-1-7.png?v=hires',
    imageAlt: 'Create Meeting form callouts 1 to 7: title, category, audience, date and time, venue, description, and Create',
    summary:
      'The title can use letters, numbers, and spaces, up to 255 characters. Choose one or more categories first. Audience stays locked until a category is chosen, and then you pick who it is for, including a class, a section, students, a department, or staff. The date has to be today or later and inside the year in the header. If the meeting is today, the start time cannot be in the past, and the end has to be after the start. Venue and description can be left blank. Create saves the meeting.',
  },
  {
    number: '7.4',
    title: 'Meeting information and Status',
    image: '/user-guides/admin/view-meeting-5-6.png?v=hires',
    imageAlt: 'Meeting detail callouts 5 and 6: meeting information and Status',
    summary:
      'This page shows the category, audience, date, time, venue, description, and who the meeting is for. Status asks you to confirm, then sets the meeting to Scheduled, Ongoing, Completed, or Cancelled. Edit opens the same details so you can change them.',
  },
  {
    number: '7.5',
    title: 'Meet Conclusions',
    image: '/user-guides/admin/meeting-conclusion-7.png?v=hires',
    imageAlt: 'Meet Conclusions callout: save notes, and what other people see',
    summary:
      'You can type the notes and Save Conclusions on any meeting. People who cannot change the meeting still see those notes, or a line that none are recorded yet.',
  },
  {
    number: '7.6',
    title: 'Assigned tasks on the meeting',
    image: '/user-guides/admin/meeting-tasks-1-8.png?v=hires',
    imageAlt: 'Assigned tasks on a meeting: title, description, due date, assignee, Assign Task, details, status, and Edit',
    summary:
      'A task belongs to this meeting. The title is required, the description can be blank, and the due date cannot be in the past. Assign to lists only a student or a staff member who is part of this meeting. Assign Task adds it. Status moves it from Pending to In Progress or Completed, and Edit changes the saved task.',
  },
  {
    number: '7.7',
    title: 'My Meetings, Create Meeting, filters, and View',
    image: '/user-guides/admin/my-meetings-1-4.png?v=hires',
    imageAlt: 'My Meetings callouts 1 to 4: the list, Create Meeting, filters, and View',
    summary:
      'This page keeps only the meetings you created, and Created By names who added each one. Create Meeting opens the same form as All Meetings. Search matches the title as you type, and category, status, and audience narrow this list. Reset clears them. The title and View both open the meeting. Delete is not on this page.',
  },
  {
    number: '7.8',
    title: 'Assigned Tasks, status, and Edit',
    image: '/user-guides/admin/assigned-tasks-1-3.png?v=hires',
    imageAlt: 'Assigned Tasks callouts 1 to 3: the list, status, and Edit',
    summary:
      'This list is every task the school has given out, with the meeting, the person, the due date, and the status. Search matches the task or the meeting title. Status and meeting narrow the list, and Reset clears them. Changing the status saves it at once. The meeting name opens that meeting. Edit changes the title, description, and due date. The title is required, and a new due date cannot be in the past.',
  },
];

const teacherAllMeetingsSections = [
  {
    number: '7.2',
    title: 'Create Meeting, filters, and View or Delete',
    image: '/user-guides/admin/all-meetings-1-3.png?v=hires',
    imageAlt: 'All Meetings callouts 1 to 3: Create Meeting, filters, and View or Delete',
    summary:
      'Create Meeting opens the form, within the limits in the instruction above. This list is not the whole school. It shows meetings you created and meetings aimed at you or your classes. Search matches the title as you type, and category and status narrow the list. The audience filter is not on your page. View opens the meeting. Delete is not shown here.',
  },
  {
    number: '7.3',
    title: 'Title, category, audience, date, venue, description, and Create',
    image: '/user-guides/admin/create-meeting-1-7.png?v=hires',
    imageAlt: 'Create Meeting form callouts 1 to 7: title, category, audience, date and time, venue, description, and Create',
    summary:
      'The title can use letters, numbers, and spaces, up to 255 characters. You cannot choose the Staff, Department, Management, or Teacher category. Audience stays locked until a category is chosen, and you can invite only a class, section, or student already assigned to you. The date has to be today or later and inside that year. If the meeting is today, the start time cannot be in the past, and the end has to be after the start. Venue and description can be left blank.',
  },
  {
    number: '7.4',
    title: 'Meeting information and Status',
    image: '/user-guides/admin/view-meeting-5-6.png?v=hires',
    imageAlt: 'Meeting detail callouts 5 and 6: meeting information and Status',
    summary:
      'You can read the category, audience, date, time, venue, description, and who the meeting is for. Status and Edit appear only on a meeting you created. On a meeting created by someone else you can read it, but you cannot change the status or the details.',
  },
  {
    number: '7.5',
    title: 'Meet Conclusions',
    image: '/user-guides/admin/meeting-conclusion-7.png?v=hires',
    imageAlt: 'Meet Conclusions callout: save notes, and what other people see',
    summary:
      'You can save conclusions only on a meeting you created. On any other meeting you can open, you see the saved notes, or a line that none are recorded yet.',
  },
  {
    number: '7.6',
    title: 'Assigned tasks on the meeting',
    image: '/user-guides/admin/meeting-tasks-1-8.png?v=hires',
    imageAlt: 'Assigned tasks on a meeting: title, description, due date, assignee, Assign Task, details, status, and Edit',
    summary:
      'You can add a task only on a meeting you created. The person you pick has to be part of that meeting, and the due date cannot be in the past. A task you give a student stays on this meeting. It shows in your Assigned Tasks list only when the task is assigned to you. Status and Edit are available on a meeting you created.',
  },
  {
    number: '7.7',
    title: 'My Meetings, Create Meeting, filters, and View',
    image: '/user-guides/admin/my-meetings-1-4.png?v=hires',
    imageAlt: 'My Meetings callouts 1 to 4: the list, Create Meeting, filters, and View',
    summary:
      'This page keeps only the meetings you created. Create Meeting opens the same form, still limited to a class, section, or student assigned to you. Search matches the title as you type, and category and status narrow the list. The audience filter is not on your page. The title and View both open the meeting. Delete is not on this page.',
  },
  {
    number: '7.8',
    title: 'Assigned Tasks, status, and Edit',
    image: '/user-guides/admin/assigned-tasks-1-3.png?v=hires',
    imageAlt: 'Assigned Tasks callouts 1 to 3: the list, status, and Edit',
    summary:
      'This list is only tasks assigned to you, not tasks you gave to a student. The Assigned To column is not on your page. Search matches the task or the meeting title, and status or meeting narrows the list. Changing the status saves it at once. The meeting name opens that meeting. Edit appears only when you created that meeting. On a task from someone else you can change the status, but not the title or the due date.',
  },
];

const teacherMeetingsChapter = (intro, instruction, introSummary, pageSections) =>
  meetingsChapter(intro, instruction, introSummary, pageSections);

const adminMeetingsIntro =
  'Meetings has three pages: All Meetings, My Meetings, and Assigned Tasks. All Meetings is every meeting in the school. My Meetings is only the ones you created. Assigned Tasks follows the work given out from a meeting.';

const adminMeetingsInstruction =
  'Select the academic year in the header before you set a date. The date has to fall inside that year and cannot be in the past. A class, section, or student audience uses the classes of that year.';

const adminMeetingsSummary =
  'All Meetings opens every meeting, and the title opens that meeting. My Meetings keeps only meetings you created, which are the ones you can change or remove. Assigned Tasks lists every task the school has given out, including tasks assigned to someone else.';

const teacherMeetingsIntro =
  'Meetings has three pages: All Meetings, My Meetings, and Assigned Tasks. You see meetings you created and meetings meant for you or your classes.';

const teacherMeetingsInstruction =
  'Select the academic year where you are assigned. You can invite only a class, section, or student already assigned to you, and the date has to fall inside that year. A meeting for the whole school, a department, or staff is not available to you.';

const teacherMeetingsSummary =
  'All Meetings shows meetings you created and meetings aimed at you or at a class you teach. Opening the title reads that meeting. My Meetings keeps only meetings you created, and those are the only ones you can change. Assigned Tasks lists tasks given to you. A task you gave to a student stays on that meeting, and it appears here only when the task is yours.';

const examDateSection = {
  number: '8.4',
  title: 'Exam date, time, mode, question paper, and room',
  image: '/user-guides/admin/exam-setup-6-10.png?v=hires',
  imageAlt: 'Exam Setup callouts 6 to 10: exam date, start and end, mode, question paper, and room',
  summary:
    'The exam date has to be today or later, and it locks after students have attempted that subject online. Start and end are required with the date. The end has to be after the start, and if the exam is today the start cannot already have passed. Mode is Offline or Online. The checkbox in the header sets every subject to Online, and choosing Offline clears the question paper and the instructions. An online subject needs a question paper. Instructions are optional. An offline subject needs a room, and an online subject does not. Two subjects cannot use the same room at the same time. Save Changes stores the same timetable as Save Schedule.',
};

const examsChapter = (intro, instruction, createSummary, listSummary, setupSummary, timetableSummary, filtersSummary, laterSections) => ({
  number: '8',
  title: 'Examinations',
  intro,
  instruction,
  sections: [
    {
      number: '8.1',
      title: 'Name, type, classes, internal marks, description, and Create',
      image: '/user-guides/admin/create-exam.png?v=hires',
      imageAlt: 'Create exam form: name, type, target classes, internal assessment, description, and the two save buttons',
      summary: createSummary,
    },
    {
      number: '8.2',
      title: 'Result status, marks assigned, Manage, and Delete',
      image: '/user-guides/admin/exams-2-5.png?v=hires',
      imageAlt: 'Exams list callouts 2 to 5: result status, marks assigned, Manage, and Delete',
      summary: listSummary,
    },
    {
      number: '8.3',
      title: 'Exam Setup, copy, save, class, and remove subject',
      image: '/user-guides/admin/exam-setup-1-5.png?v=hires',
      imageAlt: 'Exam Setup callouts 1 to 5: the page, copy and paste, save, exam class and section, and remove subject',
      summary: setupSummary,
    },
    examDateSection,
    {
      number: '8.5',
      title: 'Schedule and Manage Exams',
      image: '/user-guides/admin/exam-timetable-1-2.png?v=examtt1',
      imageAlt: 'Schedule callouts 1 and 2: the timetable view and Manage Exams',
      summary: timetableSummary,
    },
    {
      number: '8.6',
      title: 'Examination, class, section, and View Schedule',
      image: '/user-guides/admin/exam-timetable-3-5.png?v=hires',
      imageAlt: 'Schedule callouts 3 to 5: examination and type, class and section, and View Schedule',
      summary: filtersSummary,
    },
    ...laterSections,
  ],
});

const adminExamsIntro =
  'Examinations here covers Exams, Exam Setup, Schedule, Exam Result, Top Performers, Question Bank, Question Papers, and Online Evaluation. Exams is the list for the year in the header. Exam Setup and Schedule are the subject timetable. Exam Result is the marks and the outcome. Top Performers ranks students for one exam. Question Bank stores questions by subject so a paper can reuse them. Online Evaluation reviews a submitted online exam.';

const adminExamsInstruction =
  'Select the academic year in the header. The exam, its classes, the timetable, and the results use that year. A class needs subjects on its curriculum before Exam Setup can list them. Enter marks after the timetable is saved for that class and section. Publish and delete are done from the Exams page. Question Bank and Question Papers use the school subjects, and a paper can include only questions from its own subject. Online Evaluation lists only exams set to Online for that year.';

const adminExamCreateSummary =
  'The name uses letters, numbers, spaces, and dashes, from 2 to 100 characters. Exam type is required, and at least one class has to be selected for this year. Has Internal Assessment means theory marks and internal marks are entered separately. Description can be left blank, up to 500 characters. Create Exam saves a draft, so the result is not published. Create and Add Subjects saves it and opens Exam Setup.';

const adminExamListSummary =
  'Result Status publishes the exam after you confirm. If no subjects are set up, it asks you to set them up first. If marks are still missing, it warns you, and you can still publish. Marks Assigned shows how many marks are entered against how many are expected. When no subjects are set up, it says so. Manage opens Exam Setup for that exam. Delete asks you to confirm, then removes the exam.';

const adminExamSetupSummary =
  'Exam Setup is the date, time, marks, mode, and room for each subject of one exam, one class, and one section. Copy Schedule stores the max marks, pass marks, exam date, start, and end. Paste writes those onto the matching subjects of another section and asks before it overwrites anything. The room is not copied. Save Schedule stores this class and section. Pick the exam, then the class, then the section. Subjects load from the curriculum, and a new class clears the section. Remove subject asks you to confirm, then takes that subject off the timetable, the results, and the marks. Save keeps it off, and Restore puts it back. A subject students have already attempted online cannot be removed.';

const adminExamTimetableSummary =
  'Schedule shows the dates, times, marks, mode, and rooms already saved for one exam, one class, and one section. Manage Exams goes back to the Exams page. Students, parents, and guardians do not see that button.';

const adminExamFiltersSummary =
  'The examination list follows the year in the header. Choosing an exam fills the exam type, and that type cannot be changed on this page. Pick the class, then the section. A new class clears the section. View Schedule loads the saved timetable. If none was saved, the page says no timetable was found.';

const teacherExamsIntro =
  'Examinations here covers Exams, Exam Setup, Schedule, Exam Result, Top Performers, Question Bank, Question Papers, and Online Evaluation. You see exams for the classes you teach in the year selected in the header. Exam Result and Top Performers stay on a class and section assigned to you. Question Bank and Question Papers show only subjects assigned to you. Online Evaluation is only an online exam you teach.';

const teacherExamsInstruction =
  'Select the academic year where you are assigned. The list shows exams for classes you teach. You can create an exam, save a timetable, and enter marks only for a class and section assigned to you. You cannot publish a result or delete an exam. On Top Performers you start on one of your classes, and All Classes is not shown. Question Bank and Question Papers list only subjects assigned to you. Online Evaluation lists only an online exam for a subject or class assigned to you.';

const teacherExamCreateSummary =
  'The name uses letters, numbers, spaces, and dashes, from 2 to 100 characters. Exam type is required. Choose classes you teach in this year. An exam that includes none of your classes will not appear in your list. Has Internal Assessment means theory marks and internal marks are entered separately. Description can be left blank, up to 500 characters. Create Exam saves a draft. Create and Add Subjects saves it and opens Exam Setup.';

const teacherExamListSummary =
  'Result Status is only a badge. You cannot publish the exam. Marks Assigned shows how many marks are entered against how many are expected, or that no subjects are set up yet. Manage opens Exam Setup for that exam. Delete asks you to confirm, but only a school administrator can remove the exam.';

const teacherExamSetupSummary =
  'Exam Setup is the date, time, marks, mode, and room for each subject of one exam, one class, and one section you teach. Copy Schedule stores the max marks, pass marks, exam date, start, and end. Paste writes those onto the matching subjects of another section and asks before it overwrites anything. The room is not copied. Save Schedule stores this class and section. Pick the exam, then the class, then the section. Subjects load from the curriculum, and a new class clears the section. A class or section that is not assigned to you cannot be saved. Remove subject asks you to confirm, then takes that subject off the timetable, the results, and the marks. Save keeps it off, and Restore puts it back. A subject students have already attempted online cannot be removed.';

const teacherExamTimetableSummary =
  'Schedule shows the dates, times, marks, mode, and rooms already saved for one exam, one class, and one section you can open. Manage Exams goes back to the Exams page. Students, parents, and guardians do not see that button.';

const teacherExamFiltersSummary =
  'The examination list follows the year in the header and only includes exams for classes you teach. Choosing an exam fills the exam type, and that type cannot be changed on this page. Pick a class and section assigned to you. A new class clears the section. View Schedule loads the saved timetable. If none was saved, the page says no timetable was found.';

const adminExamResultSections = [
  {
    number: '8.7',
    title: 'Exam Result, export, View Records, and the catalog',
    image: '/user-guides/admin/results-1-4.png?v=hires',
    imageAlt: 'Exam Result callouts 1 to 4: the page, print and export, View Records, and the results catalog',
    summary:
      'Exam Result is the marks, percentage, grade, and outcome for one exam, one class, and one section. Print All prints this catalog. Export PDF and Export Excel download it, and those two appear only when export is allowed. Tick some students first and those buttons use only those rows. Otherwise they use the whole list. View Records loads the catalog after the exam, class, and section are all chosen. A new class clears the section. The catalog lists each student, registration number, marks out of the maximum, percentage, grade, and pass, fail, or pending. A student name opens that student result.',
  },
  {
    number: '8.8',
    title: 'Assign Marks, enter marks, and Save All Marks',
    image: '/user-guides/admin/result-performance-5-7.png?v=hires',
    imageAlt: 'Exam Result callouts 5 to 7: Assign Marks, entering marks and absent, and Save All Marks',
    summary:
      'Assign Marks lists students down the side and subjects across, with each subject maximum and passing marks. Type a mark between zero and that maximum. A mark under the passing mark is highlighted, and a mark above the maximum is blocked. Tick Absent to clear the mark and lock the box. A subject the student has not chosen stays blank. If the exam uses internal assessment, enter both theory and internal marks, and both are required before that subject is saved. Save All Marks stores the marks for this class and section.',
  },
  {
    number: '8.9',
    title: 'Student Analytics, the summary, and expand or collapse',
    image: '/user-guides/admin/result-performance-8-10.png?v=hires',
    imageAlt: 'Exam Result callouts 8 to 10: Student Analytics, the student summary, and expand or collapse',
    summary:
      'Student Analytics lists each student with obtained marks, grade, and outcome. The first student opens on its own and shows each subject, with maximum marks, passing marks, mode, grade, and result. The summary line is the total obtained marks, grade, and outcome for this exam. The student name or the arrow opens and closes that subject list. Print and Export PDF on an open student are for that student only.',
  },
  {
    number: '8.10',
    title: 'Top Performers, exam, class, and include failed',
    image: '/user-guides/admin/top-performers-1-4.png?v=hires',
    imageAlt: 'Top Performers callouts 1 to 4: the ranking, the exam, class and section, and include failed',
    summary:
      'Top Performers ranks students by percentage for one exam, with obtained marks, total, grade, and status. The exam list follows the year in the header, and choosing another exam reloads the ranking. All Classes covers every class on that exam. Pick one class and the section list opens. All Sections stays locked until a class is chosen, and a new class returns the section to All Sections. Top shortens the list to 3, 5, 10, 15, 20, or everyone. Include failed off keeps only students who passed. Turn it on to add students who failed. Pending results stay off the list either way, and this choice is remembered in the browser.',
  },
  {
    number: '8.11',
    title: 'Question Bank, filters, Bulk Add, Edit, and Add Question',
    image: '/user-guides/admin/question-bank-1-5.png?v=hires',
    imageAlt: 'Question Bank callouts 1 to 5: the list, subject and type, Bulk Add, Edit and Delete, and Add Question',
    summary:
      'Question Bank stores exam questions by subject and type, with marks, so a paper can reuse them. All subjects shows every subject or one subject. All types shows every type, or MCQ, true or false, fill in the blank, short answer, or long answer. The list updates as soon as you change them, and search matches the question text. Bulk Add opens a form for up to 50 questions on one subject, and each question can have its own type, marks, and answer. Add Question opens a blank form. Edit opens that question so you can change it. Delete asks you to confirm, then removes it. A question already on a paper, or one students have answered, cannot be deleted. Bulk Add and Add Question appear when you can add questions. Edit appears when you can change them, and Delete when you can remove them.',
  },
  {
    number: '8.12',
    title: 'Subject, type, marks, question, and Save Question',
    image: '/user-guides/admin/add-question-6-15.png?v=hires',
    imageAlt: 'Add Question form: subject, type, marks, question text, options, and Save Question',
    summary:
      'Subject is required. The type is MCQ, true or false, fill in the blank, short answer, or long answer. Marks must be greater than zero. The question text is required. An MCQ needs at least two different options and exactly one correct answer. True or false uses True and False, and you pick the correct one. Fill in the blank asks for the correct answer. Short answer and long answer need the question and the marks, and the student writes the answer in the exam. Save Question adds it.',
  },
  {
    number: '8.13',
    title: 'Question Papers, subject, View, and Create Paper',
    image: '/user-guides/admin/question-papers-1-4.png?v=hires',
    imageAlt: 'Question Papers callouts 1 to 4: the list, All subjects, View Edit and Delete, and Create Paper',
    summary:
      'Question Papers lists each online paper, its subject, and how many question-bank questions sit on it. All subjects shows every paper, or only the papers for one subject, and the list updates as soon as you change it. View opens the paper so you can read each question and its correct answer. Edit opens the paper so you can change it. The subject stays locked on edit, and the paper cannot be saved after a student has started it. Delete asks you to confirm. If the paper is not on an exam, it is removed. If it is already on an exam, it is switched off and leaves this list. Create Paper opens a blank form. Create, Edit, and Delete appear when you are allowed to do those things.',
  },
  {
    number: '8.14',
    title: 'Paper name, subject, questions, and Save Paper',
    image: '/user-guides/admin/create-question-paper.png?v=hires',
    imageAlt: 'Create Question Paper form: name, subject, instructions, search, selected questions, marks, and Save Paper',
    summary:
      'The paper name is required. It can use letters, numbers, commas, dashes, and spaces, up to 150 characters. Subject is required, and its questions come from the question bank. Changing the subject clears the questions you already picked. On edit the subject stays locked. Instructions can be left blank. Search matches the question text, and you can select every question in that search. Tick the questions to include. A ticked question shows a marks box, and the marks must be greater than zero. At least one question is required. Save Paper stays off until a question is ticked, then it stores the paper.',
  },
  {
    number: '8.15',
    title: 'View Question Paper',
    image: '/user-guides/admin/question-paper-6-13.png?v=hires',
    imageAlt: 'View Question Paper: name, subject, question count, total marks, instructions, questions, and Close',
    summary:
      'View opens the saved paper. It shows the name, subject, how many questions, the total marks, and the instructions. Each question shows its number, type, marks, the question text, the options, and the correct answer. Close leaves the paper unchanged.',
  },
  {
    number: '8.16',
    title: 'Schedule, student, question, and Save Evaluation',
    image: '/user-guides/admin/online-evaluation-1-6.png?v=hires',
    imageAlt: 'Online Evaluation callouts 1 to 6: the page, schedule and student, question type, correct or incorrect, marks, and save',
    summary:
      'Online Evaluation reviews one submitted online exam and sets each question mark, up to that question maximum. The schedule list is online exams for the year in the header, and each choice shows the exam, subject, class, and section. Student stays locked until a schedule is chosen, then lists only students who submitted. A multiple choice or true or false question lists the options, highlights the student choice, and marks the correct option. A fill-in-the-blank question shows the correct key and what the student wrote. A short or long answer shows the written answer. Before you save, Correct means that answer matches the key, and Incorrect means it does not. Saving sets Correct on a multiple choice, true or false, or fill-in-the-blank question only when the mark equals the maximum. A short or long answer often shows neither, and saving does not add one. Type a whole number from zero up to the maximum. Every question needs a mark before Save Evaluation. Saving stores the marks and updates that student exam result. A finalized exam cannot be changed.',
  },
];

const teacherExamResultSections = [
  {
    number: '8.7',
    title: 'Exam Result, export, View Records, and the catalog',
    image: '/user-guides/admin/results-1-4.png?v=hires',
    imageAlt: 'Exam Result callouts 1 to 4: the page, print and export, View Records, and the results catalog',
    summary:
      'Exam Result is the marks, percentage, grade, and outcome for one exam, one class, and one section you teach. Print All prints this catalog. Export PDF and Export Excel download it, and those two appear only when export is allowed. Tick some students first and those buttons use only those rows. Otherwise they use the whole list. View Records loads the catalog after the exam, class, and section are all chosen. A new class clears the section. A class or section that is not assigned to you cannot be opened. The catalog lists each student, registration number, marks out of the maximum, percentage, grade, and pass, fail, or pending. A student name opens that student result.',
  },
  {
    number: '8.8',
    title: 'Assign Marks, enter marks, and Save All Marks',
    image: '/user-guides/admin/result-performance-5-7.png?v=hires',
    imageAlt: 'Exam Result callouts 5 to 7: Assign Marks, entering marks and absent, and Save All Marks',
    summary:
      'Assign Marks lists students down the side and subjects across, with each subject maximum and passing marks. You can enter marks only for a class and section assigned to you. Type a mark between zero and that maximum. A mark under the passing mark is highlighted, and a mark above the maximum is blocked. Tick Absent to clear the mark and lock the box. A subject the student has not chosen stays blank. If the exam uses internal assessment, enter both theory and internal marks, and both are required before that subject is saved. Save All Marks stores the marks for this class and section.',
  },
  {
    number: '8.9',
    title: 'Student Analytics, the summary, and expand or collapse',
    image: '/user-guides/admin/result-performance-8-10.png?v=hires',
    imageAlt: 'Exam Result callouts 8 to 10: Student Analytics, the student summary, and expand or collapse',
    summary:
      'Student Analytics lists each student with obtained marks, grade, and outcome. The first student opens on its own and shows each subject, with maximum marks, passing marks, mode, grade, and result. The summary line is the total obtained marks, grade, and outcome for this exam. The student name or the arrow opens and closes that subject list. Print and Export PDF on an open student are for that student only.',
  },
  {
    number: '8.10',
    title: 'Top Performers, exam, class, and include failed',
    image: '/user-guides/admin/top-performers-1-4.png?v=hires',
    imageAlt: 'Top Performers callouts 1 to 4: the ranking, the exam, class and section, and include failed',
    summary:
      'Top Performers ranks students by percentage for one exam, with obtained marks, total, grade, and status. The exam list follows the year in the header and only includes exams for classes you teach. Choosing another exam reloads the ranking and starts on one of your classes. All Classes and All Sections are not on your page. A new class selects a section you teach. Top shortens the list to 3, 5, 10, 15, 20, or everyone. Include failed off keeps only students who passed. Turn it on to add students who failed. Pending results stay off the list either way, and this choice is remembered in the browser.',
  },
  {
    number: '8.11',
    title: 'Question Bank, filters, Bulk Add, Edit, and Add Question',
    image: '/user-guides/admin/question-bank-1-5.png?v=hires',
    imageAlt: 'Question Bank callouts 1 to 5: the list, subject and type, Bulk Add, Edit and Delete, and Add Question',
    summary:
      'Question Bank stores exam questions by subject and type, with marks, so a paper can reuse them. The subject list is only subjects assigned to you. All types shows every type, or MCQ, true or false, fill in the blank, short answer, or long answer. The list updates as soon as you change them, and search matches the question text. Bulk Add opens a form for up to 50 questions on one of your subjects, and each question can have its own type, marks, and answer. Add Question opens a blank form. Edit opens that question so you can change it. Delete asks you to confirm, then removes it. A question already on a paper, or one students have answered, cannot be deleted.',
  },
  {
    number: '8.12',
    title: 'Subject, type, marks, question, and Save Question',
    image: '/user-guides/admin/add-question-6-15.png?v=hires',
    imageAlt: 'Add Question form: subject, type, marks, question text, options, and Save Question',
    summary:
      'Choose a subject assigned to you. The type is MCQ, true or false, fill in the blank, short answer, or long answer. Marks must be greater than zero. The question text is required. An MCQ needs at least two different options and exactly one correct answer. True or false uses True and False, and you pick the correct one. Fill in the blank asks for the correct answer. Short answer and long answer need the question and the marks, and the student writes the answer in the exam. Save Question adds it.',
  },
  {
    number: '8.13',
    title: 'Question Papers, subject, View, and Create Paper',
    image: '/user-guides/admin/question-papers-1-4.png?v=hires',
    imageAlt: 'Question Papers callouts 1 to 4: the list, All subjects, View Edit and Delete, and Create Paper',
    summary:
      'Question Papers lists each online paper for a subject assigned to you, and how many question-bank questions sit on it. The subject filter shows every such paper, or only the papers for one of your subjects, and the list updates as soon as you change it. View opens the paper so you can read each question and its correct answer. Edit opens the paper so you can change it. The subject stays locked on edit, and the paper cannot be saved after a student has started it. Delete asks you to confirm. If the paper is not on an exam, it is removed. If it is already on an exam, it is switched off and leaves this list. Create Paper opens a blank form.',
  },
  {
    number: '8.14',
    title: 'Paper name, subject, questions, and Save Paper',
    image: '/user-guides/admin/create-question-paper.png?v=hires',
    imageAlt: 'Create Question Paper form: name, subject, instructions, search, selected questions, marks, and Save Paper',
    summary:
      'The paper name is required. It can use letters, numbers, commas, dashes, and spaces, up to 150 characters. Subject is required, and you can choose only a subject assigned to you. Its questions come from the question bank. Changing the subject clears the questions you already picked. On edit the subject stays locked. Instructions can be left blank. Search matches the question text, and you can select every question in that search. Tick the questions to include. A ticked question shows a marks box, and the marks must be greater than zero. At least one question is required. Save Paper stays off until a question is ticked, then it stores the paper.',
  },
  {
    number: '8.15',
    title: 'View Question Paper',
    image: '/user-guides/admin/question-paper-6-13.png?v=hires',
    imageAlt: 'View Question Paper: name, subject, question count, total marks, instructions, questions, and Close',
    summary:
      'View opens the saved paper. It shows the name, subject, how many questions, the total marks, and the instructions. Each question shows its number, type, marks, the question text, the options, and the correct answer. Close leaves the paper unchanged.',
  },
  {
    number: '8.16',
    title: 'Schedule, student, question, and Save Evaluation',
    image: '/user-guides/admin/online-evaluation-1-6.png?v=hires',
    imageAlt: 'Online Evaluation callouts 1 to 6: the page, schedule and student, question type, correct or incorrect, marks, and save',
    summary:
      'Online Evaluation reviews one submitted online exam you teach, and sets each question mark up to that question maximum. The schedule list is only an online exam for a subject or class assigned to you. Each choice shows the exam, subject, class, and section. Student stays locked until a schedule is chosen, then lists only students who submitted. A multiple choice or true or false question lists the options, highlights the student choice, and marks the correct option. A fill-in-the-blank question shows the correct key and what the student wrote. A short or long answer shows the written answer. Before you save, Correct means that answer matches the key, and Incorrect means it does not. Saving sets Correct on a multiple choice, true or false, or fill-in-the-blank question only when the mark equals the maximum. A short or long answer often shows neither, and saving does not add one. Type a whole number from zero up to the maximum. Every question needs a mark before Save Evaluation. Saving stores the marks and updates that student exam result. A finalized exam cannot be changed.',
  },
];

const teacherExamsChapter = examsChapter(
  teacherExamsIntro,
  teacherExamsInstruction,
  teacherExamCreateSummary,
  teacherExamListSummary,
  teacherExamSetupSummary,
  teacherExamTimetableSummary,
  teacherExamFiltersSummary,
  teacherExamResultSections
);

const assembliesSections = (introSummary, pageSections, introImage) => [
  {
    number: '9.1',
    title: 'All Assemblies, Reports, and Trashed',
    image: introImage || '/user-guides/admin/assemblies-module.png?v=hires',
    imageAlt: 'Assemblies menu callouts 1 to 3: All Assemblies, Reports, and Trashed',
    summary: introSummary,
  },
  ...pageSections,
];

const assembliesChapter = (intro, instruction, introSummary, pageSections, introImage) => ({
  number: '9',
  title: 'Assemblies',
  intro,
  instruction,
  sections: assembliesSections(introSummary, pageSections, introImage),
});

const assemblyCreateSummary =
  'The title uses letters, numbers, and spaces, up to 255 characters. The date has to be today or later. Start and end are required, and the end has to be after the start. Venue can be left blank. Audience is required. It can be all students, one or more classes, a class and its section, a house, a group, or chosen students. When a list appears, at least one entry is required. The classes and sections are the ones for the year in the header. Description can be left blank. Create saves the assembly as Scheduled. Cancel closes the form without saving.';

const teacherAssemblyCreateSummary =
  'The title uses letters, numbers, and spaces, up to 255 characters. The date has to be today or later. Start and end are required, and the end has to be after the start. Venue can be left blank. Audience is required. It can be all students, one or more classes, a class and its section, a house, a group, or chosen students. When a list appears, at least one entry is required. The class list is every class of the year in the header, not only classes you teach. You still see the assembly afterward because you created it. Description can be left blank. Create saves the assembly as Scheduled. Cancel closes the form without saving.';

const assemblyAgendaSummary =
  'Agenda lists each item on this assembly, with its title, minutes, and description. The boxes above the list appear when you can add an item. The title is required. It can use letters, numbers, spaces, a dash, and &, up to 255 characters. Minutes can be left blank. If you enter them, use a whole number from 1 to 480. Description can be left blank, up to 5000 characters. Add stores the item on this assembly. Edit changes the title, minutes, and description. Delete asks you to confirm, then removes it. Edit appears when you can change items, and Delete when you can remove them.';

const assemblyAwardSummary =
  'Award House / Group Points records points for one house or group on this assembly. The card appears when you can edit the assembly. Students, parents, and guardians do not see it. Group is required, and a group that already has points for this assembly is left out of the list. Points are a whole number from 1 to 9999. Reason can be left blank. It can use letters, numbers, spaces, a dash, and &. Award saves that group. The button stays off while a save is running, or when every group already has points. Each group total is shown under the form. Edit opens the same group, points, and reason. It appears when you can edit the assembly.';

const assemblyAttendanceSummary =
  'Attendance is the student roster for this assembly. Bulk status is Present or Absent. Changing that choice does not save by itself. A student row can also be set to Late or Excused, and remarks can be left blank. Class and section narrow who is showing, and a new class clears the section. Search matches the name or the admission number. Apply to selected gives the chosen status to the ticked students and the opposite status to the other students still showing. Students hidden by the class, section, or search stay as they are, and the page then saves. That button stays off until at least one student is ticked. Save Attendance stores the status and remarks for every student on this assembly. It stays off when the list is empty or a save is running. Both buttons appear when you can mark attendance.';

const adminAssemblySections = [
  {
    number: '9.2',
    title: 'Trashed, status, View, Create Assembly, and the title',
    image: '/user-guides/admin/assembly-1-4.png?v=asm1',
    imageAlt: 'All Assemblies callouts 1 to 5: Trashed, status, View Attendance and Delete, Create Assembly, and the title',
    summary:
      'This list is every assembly in the school, not only the year in the header. Search matches the title a moment after you stop typing. All statuses shows every assembly, or only Scheduled, Ongoing, Completed, or Cancelled, and the list updates as soon as you change it. The title and View both open the assembly. Attendance opens the roster for that assembly. Delete asks you to confirm, then moves the assembly to Trashed. Trashed opens those deleted assemblies so they can be restored, and it shows when you can delete. Create Assembly opens a blank form when you can add one.',
  },
  {
    number: '9.3',
    title: 'Title, date, time, audience, and Create',
    image: '/user-guides/admin/assembly-5.png?v=hires',
    imageAlt: 'Create Assembly form: title, date, start and end, venue, audience, description, Create, and Cancel',
    summary: assemblyCreateSummary,
  },
  {
    number: '9.4',
    title: 'Agenda, title, Add, and Edit or Delete',
    image: '/user-guides/admin/agenda-6-9.png?v=hires',
    imageAlt: 'Assembly agenda callouts 6 to 9: the list, title minutes and description, Add, and Edit or Delete',
    summary: assemblyAgendaSummary,
  },
  {
    number: '9.5',
    title: 'Award points, group, Award, and Edit',
    image: '/user-guides/admin/award-house-10-13.png?v=hires',
    imageAlt: 'Award House or Group Points callouts 10 to 13: the card, group points and reason, Award, and Edit',
    summary: assemblyAwardSummary,
  },
  {
    number: '9.6',
    title: 'Bulk status, Apply to selected, and Save Attendance',
    image: '/user-guides/admin/assembly-attendance-14-16.png?v=hires',
    imageAlt: 'Assembly attendance callouts 14 to 16: bulk status, Apply to selected, and Save Attendance',
    summary: assemblyAttendanceSummary,
  },
  {
    number: '9.7',
    title: 'Summary, From and To, and Apply',
    image: '/user-guides/admin/assemblies-reports-summary.png?v=hires',
    imageAlt: 'Assembly Reports Summary: the status counts, From and To, and Apply',
    summary:
      'Summary counts every assembly in the year selected in the header, grouped by status, and shows the total. Deleted assemblies are left out. From is the earliest assembly date included, and To is the latest. Leave a date empty and that side has no limit. Summary, Attendance, and House Points all use these dates. Changing a date reloads the report. Apply loads it again. Print, PDF, and Excel download the tab that is open, when export is allowed.',
  },
  {
    number: '9.8',
    title: 'Attendance',
    image: '/user-guides/admin/assemblies-reports-attendance.png?v=hires',
    imageAlt: 'Assembly Reports Attendance: each assembly and how many students were Present, Absent, Late, or Excused',
    summary:
      'Attendance lists each assembly in that date range and how many students were Present, Absent, Late, or Excused. Only assemblies with saved attendance are listed. An assembly with no attendance saved does not appear. The same From and To dates apply, and changing them reloads this list.',
  },
  {
    number: '9.9',
    title: 'House Points, assembly, and the house list',
    image: '/user-guides/admin/assemblies-reports-house-points.png?v=hires',
    imageAlt: 'Assembly Reports House Points: dates, assembly, Apply, and each house with its points',
    summary:
      'House Points lists every house and its points for the year in the header, highest first. A house with no points still appears as zero. All Assemblies adds up every point recorded for that house in the year, not only points given on an assembly. Choose one assembly and the list is only the points awarded on that assembly. From and To limit the dates, and an empty side has no limit. The assembly list follows those dates. If the assembly you picked falls outside them, the choice clears. Changing the assembly reloads the list. Apply loads it again. Row per page only changes how many houses show on one page. Groups that are not houses are left out.',
  },
  {
    number: '9.10',
    title: 'Trashed Assemblies and Restore',
    image: '/user-guides/admin/assemblies-trashed.png?v=hires',
    imageAlt: 'Trashed Assemblies: the deleted list and Restore',
    summary:
      'Trashed lists every deleted assembly, including ones from other years. Each row shows the title, the assembly date, the status, and the day it was removed. Students, parents, and guardians do not see this page. Restore asks you to confirm, then puts that assembly back on All Assemblies. The status stays as it was.',
  },
];

const teacherAssemblySections = [
  {
    number: '9.2',
    title: 'Trashed, status, View, Create Assembly, and the title',
    image: '/user-guides/admin/assembly-1-4.png?v=asm1',
    imageAlt: 'All Assemblies callouts 1 to 5: Trashed, status, View Attendance and Delete, Create Assembly, and the title',
    summary:
      'This list is not the whole school. It shows assemblies you created, assemblies you are in charge of, assemblies for the whole school, and assemblies for a class, section, student, house, or group you teach. It is not limited to the year in the header. Search matches the title a moment after you stop typing. All statuses shows every assembly you can see, or only Scheduled, Ongoing, Completed, or Cancelled, and the list updates as soon as you change it. The title and View both open the assembly. Attendance opens the roster. Create Assembly opens a blank form when you can add one. Delete and Trashed are for an administrator or administrative staff. A teacher cannot move an assembly to Trashed.',
  },
  {
    number: '9.3',
    title: 'Title, date, time, audience, and Create',
    image: '/user-guides/admin/assembly-5.png?v=hires',
    imageAlt: 'Create Assembly form: title, date, start and end, venue, audience, description, Create, and Cancel',
    summary: teacherAssemblyCreateSummary,
  },
  {
    number: '9.4',
    title: 'Agenda, title, Add, and Edit or Delete',
    image: '/user-guides/admin/agenda-6-9.png?v=hires',
    imageAlt: 'Assembly agenda callouts 6 to 9: the list, title minutes and description, Add, and Edit or Delete',
    summary: assemblyAgendaSummary,
  },
  {
    number: '9.5',
    title: 'Award points, group, Award, and Edit',
    image: '/user-guides/admin/award-house-10-13.png?v=hires',
    imageAlt: 'Award House or Group Points callouts 10 to 13: the card, group points and reason, Award, and Edit',
    summary: assemblyAwardSummary,
  },
  {
    number: '9.6',
    title: 'Bulk status, Apply to selected, and Save Attendance',
    image: '/user-guides/admin/assembly-attendance-14-16.png?v=hires',
    imageAlt: 'Assembly attendance callouts 14 to 16: bulk status, Apply to selected, and Save Attendance',
    summary: assemblyAttendanceSummary,
  },
  {
    number: '9.7',
    title: 'Summary, From and To, and Apply',
    image: '/user-guides/admin/assemblies-reports-summary.png?v=hires',
    imageAlt: 'Assembly Reports Summary: the status counts, From and To, and Apply',
    summary:
      'Summary counts the assemblies you can see in the year selected in the header, grouped by status, and shows the total. That is assemblies you created, assemblies you are in charge of, assemblies for the whole school, and assemblies for a class, section, student, house, or group you teach. Deleted assemblies are left out. From is the earliest assembly date included, and To is the latest. Leave a date empty and that side has no limit. Changing a date reloads Summary, Attendance, and House Points. Apply loads them again. Print, PDF, and Excel download the tab that is open, when export is allowed.',
  },
  {
    number: '9.8',
    title: 'Attendance',
    image: '/user-guides/admin/assemblies-reports-attendance.png?v=hires',
    imageAlt: 'Assembly Reports Attendance: each assembly and how many students were Present, Absent, Late, or Excused',
    summary:
      'Attendance lists each assembly you can see in that date range, and how many students were Present, Absent, Late, or Excused. Only assemblies with saved attendance are listed. The same From and To dates apply, and changing them reloads this list.',
  },
  {
    number: '9.9',
    title: 'House Points, assembly, and the house list',
    image: '/user-guides/admin/assemblies-reports-house-points.png?v=hires',
    imageAlt: 'Assembly Reports House Points: dates, assembly, Apply, and each house with its points',
    summary:
      'House Points lists every house and the points from assemblies you can see, highest first. A house with no points still appears as zero. All Assemblies adds up those assembly points for the year. Choose one assembly you can open and the list is only the points awarded on that assembly. From and To limit the dates, and an empty side has no limit. The assembly list is only assemblies you can see in those dates. If the assembly you picked falls outside them, the choice clears. Changing the assembly reloads the list. Apply loads it again. Row per page only changes how many houses show on one page.',
  },
  {
    number: '9.10',
    title: 'Trashed Assemblies and Restore',
    image: '/user-guides/admin/assemblies-trashed.png?v=hires',
    imageAlt: 'Trashed Assemblies: the deleted list and Restore',
    summary:
      'This page is not available to a teacher. Only an administrator or administrative staff can open Trashed or restore an assembly. The list shows every deleted assembly, with the title, the assembly date, the status, and the day it was removed. Restore asks them to confirm, then puts that assembly back on All Assemblies. The status stays as it was. Students, parents, and guardians do not see this page.',
  },
];

const teacherAssembliesChapter = (intro, instruction, introSummary, pageSections) =>
  assembliesChapter(intro, instruction, introSummary, pageSections);

const adminAssembliesIntro =
  'Assemblies has three pages: All Assemblies, Reports, and Trashed. All Assemblies is every assembly in the school. Reports covers status, attendance, and house points. Trashed holds deleted assemblies so they can be restored.';

const adminAssembliesInstruction =
  'Select the academic year in the header before you create an assembly. The new assembly, its classes, and its sections use that year. If no year is selected, the school uses the current year when you save, and Reports use that current year too. The All Assemblies list itself is not limited to that year. Reports are. Delete and restore stay with an administrator or administrative staff.';

const adminAssembliesSummary =
  'All Assemblies opens every assembly, and the title opens that assembly. Reports covers how assemblies went, who attended, and house points. Trashed holds assemblies that were deleted, so they can be restored. Students, parents, and guardians can open All Assemblies and Reports. Trashed is only for someone who can delete an assembly.';

const teacherAssembliesIntro =
  'Assemblies has three pages: All Assemblies, Reports, and Trashed. You see assemblies you created, assemblies you are in charge of, assemblies for the whole school, and assemblies for a class, section, student, house, or group you teach.';

const teacherAssembliesInstruction =
  'Select the academic year in the header before you create an assembly. The new assembly is saved in that year, and the class list is the classes of that year. Reports count only assemblies you can see in that year. You can create an assembly, add an agenda, award house or group points, and mark attendance. You cannot open Trashed, delete an assembly, or restore one.';

const teacherAssembliesSummary =
  'All Assemblies shows assemblies you created, assemblies you are in charge of, assemblies for the whole school, and assemblies for a class, section, student, house, or group you teach. Opening the title reads that assembly. Reports covers status, attendance, and house points. Trashed is for an administrator who can delete an assembly. Students, parents, and guardians can open All Assemblies and Reports, and they do not see Trashed.';

const enquiriesChapter = (intro, instruction, pageSummary, listSummary) => ({
  number: '10',
  title: 'Enquiries',
  intro,
  instruction,
  sections: [
    {
      number: '10.1',
      title: 'Enquiries and Add Enquiry',
      image: '/user-guides/admin/enquiries-1-2.png?v=hires',
      imageAlt: 'Enquiries callouts 1 and 2: the page and the Add Enquiry form',
      summary: pageSummary,
    },
    {
      number: '10.2',
      title: 'Enquiry List, Follow-ups, actions, and Follow-up activity',
      image: '/user-guides/admin/enquiry-list-3-6.png?v=enq1',
      imageAlt: 'Enquiry list callouts 3 to 6: filters, Follow-ups, row actions, and Follow-up activity',
      summary: listSummary,
    },
  ],
});

const adminEnquiriesIntro =
  'Enquiries records admission enquiries for the academic year in the header, with the student, parent, class, and status, so they can be followed up.';

const adminEnquiriesInstruction =
  'Select the academic year in the header. The list, a new enquiry, and follow-up activity use that year. A new enquiry date has to be today or later and inside that year. Students, parents, and guardians do not use this page.';

const adminEnquiriesPageSummary =
  'This page is the admission list for the year in the header. Students, parents, and guardians do not use it. Student name is required, at least 2 letters, and uses letters and spaces. Mobile number is required and must be exactly 10 digits. Parent, enquiry type, previous school, source, and status use letters and spaces. Status starts as Open. Email, gender, date of birth, address, target class, and description can be left blank. Gender is Male, Female, or Other. The date of birth has to be before today. A new enquiry date has to be today or later, and inside this academic year. Add Enquiry saves it. Edit loads the same form, and Update saves the change.';

const adminEnquiriesListSummary =
  'Enquiry List is every admission enquiry for the year in the header. Search matches the student name, parent name, mobile number, or enquiry type, a moment after you stop typing. Month, From, and To narrow the enquiry date, and they work together. Added By can show everyone, only yours, or only those added by a headmaster, administrative staff, or a teacher. Reset clears those choices. Follow-ups opens the notes on that enquiry, and you can open them for any enquiry. Add Student starts a new student with this enquiry already filled in, when you are allowed to add students. Edit loads that enquiry into the form. Delete asks you to confirm, then removes it. Follow-up activity lists every follow-up for this year. Each row shows when it happened, the student, the remarks, the next date, the counselor, who logged it, and who owns the enquiry. You can change the status of any enquiry from that list.';

const teacherEnquiriesIntro =
  'Enquiries records admission enquiries for the academic year in the header. You see every enquiry for that year, and you can add and update them.';

const teacherEnquiriesInstruction =
  'Select the academic year in the header. You see every enquiry for that year. You can add a follow-up, and change the status, only on an enquiry you added. On Follow-up activity you also see a follow-up you logged, or one where you are the counselor. You cannot change the status of an enquiry someone else added. Students, parents, and guardians do not use this page.';

const teacherEnquiriesPageSummary =
  'This page is the admission list for the year in the header, and you see every enquiry, not only the ones you added. Student name is required, at least 2 letters, and uses letters and spaces. Mobile number is required and must be exactly 10 digits. Parent, enquiry type, previous school, source, and status use letters and spaces. Status starts as Open. Email, gender, date of birth, address, target class, and description can be left blank. Gender is Male, Female, or Other. The date of birth has to be before today. A new enquiry date has to be today or later, and inside this academic year. Add Enquiry saves it. Edit loads the same form, and Update saves the change.';

const teacherEnquiriesListSummary =
  'Enquiry List is every admission enquiry for the year in the header. Search matches the student name, parent name, mobile number, or enquiry type, a moment after you stop typing. Month, From, and To narrow the enquiry date, and they work together. Added By can show everyone, only yours, or only those added by a headmaster, administrative staff, or a teacher. Reset clears those choices. Follow-ups opens the notes only on an enquiry you added. Add Student appears only when you are allowed to admit students. Edit loads that enquiry into the form. Delete asks you to confirm, then removes it. Follow-up activity shows a follow-up you logged, an enquiry you added, or one where you are the counselor. You can change the status only for an enquiry you added.';

const studentGroupsChapter = (intro, instruction, listSummary, formSummary, membersSummary, pointsSummary) => ({
  number: '12',
  title: 'Student Groups',
  intro,
  instruction,
  sections: [
    {
      number: '12.1',
      title: 'Student Groups, type, actions, and Create Group',
      image: '/user-guides/admin/student-groups-1-4.png?v=hires',
      imageAlt: 'Student Groups list: the page, All types, View Edit Toggle Delete, and Create Group',
      summary: listSummary,
    },
    {
      number: '12.2',
      title: 'Name, type, color, captain, and save',
      image: '/user-guides/admin/create-group.png?v=hires',
      imageAlt: 'Create Group form: name, type, color, captain, description, status, Cancel, and Create Group',
      summary: formSummary,
    },
    {
      number: '12.3',
      title: 'Members, add member, filters, and Remove',
      image: '/user-guides/admin/group-members-6-9.png?v=hires',
      imageAlt: 'Group members: the member list, Add member, role and class filters, and Remove',
      summary: membersSummary,
    },
    {
      number: '12.4',
      title: 'Points Ledger and Award',
      image: '/user-guides/admin/group-points-10-11.png?v=hires',
      imageAlt: 'Points Ledger: the points list and Award',
      summary: pointsSummary,
    },
  ],
});

const adminStudentGroupsIntro =
  'Student Groups lists houses, clubs, teams, councils, and other groups, with members and points for the academic year in the header.';

const adminStudentGroupsInstruction =
  'Select the academic year in the header. A new group is saved in that year. Members and points use that year. The list shows every group, including inactive ones, and the points column is for that year. Students, parents, and guardians do not manage groups.';

const adminStudentGroupsListSummary =
  'This page lists every group, with the name, type, color, captain, member count, points for the year in the header, and status. Search matches the name a moment after you stop typing. All types shows every group, or you can keep only House, Club, Team, Council, or Other. View opens that group. Edit loads the same form. Toggle switches the group between active and inactive straight away, and it stays on this list. Delete asks you to confirm, then moves it to Trashed so it can be restored. Create Group opens a new group. Students, parents, and guardians do not manage groups.';

const adminStudentGroupsFormSummary =
  'Name is required, up to 50 characters, and uses letters, numbers, and single spaces. A name already in use is rejected. Type is required and starts as House. The choices are House, Club, Team, Council, and Other. Color can be left blank, up to 50 characters, as a color name or a hex code. Captain can be left blank. Search a student by name or admission number, and type at least 3 characters. Description can be left blank, up to 5000 characters. Status starts on, which saves the group as active. Cancel closes the form without saving. Create Group saves it. Edit uses the same fields, and the button becomes Update.';

const adminStudentGroupsMembersSummary =
  'Members lists the students in this group for the year in the header, with the class, role, and the day they joined. Changing the role on a row saves that role. Add member picks a student who is not already in a group this year. A student can belong to only one group. The role starts as Member. The choices are Member, Captain, Vice Captain, Prefect, and Custom. Add saves that member. Bulk add adds several students at once. Role, class, and search only narrow this list, and Reset clears them. Remove asks you to confirm, then takes that student out of the group.';

const adminStudentGroupsPointsSummary =
  'Points Ledger lists points added or removed for this group in the year in the header. Each row shows the date, the points, the reason, the source, and who recorded it. The total is for that year. Points from an assembly also appear here. Award takes a whole number other than zero. A minus number takes points away. Reason can be left blank, up to 2000 characters. Award saves it for this year, and the source is recorded as manual.';

const teacherStudentGroupsIntro =
  'Student Groups lists houses, clubs, teams, councils, and other groups. You can open a group and read its members and points for the academic year in the header.';

const teacherStudentGroupsInstruction =
  'Select the academic year in the header. Members and the points you see use that year. You can open a group and read it. You cannot create a group, edit one, switch it on or off, delete it, add or remove members, or award points if. Students, parents, and guardians do not manage groups.';

const teacherStudentGroupsListSummary =
  'This page lists every group, with the name, type, color, captain, member count, points for the year in the header, and status. Search matches the name a moment after you stop typing. All types shows every group, or you can keep only House, Club, Team, Council, or Other. View opens that group. Create Group, Edit, Toggle, Delete, and Trashed are not on your page.';

const teacherStudentGroupsFormSummary =
  'This form is for an administrator. You do not see Create Group. Name is required, up to 50 characters, and uses letters, numbers, and single spaces. A name already in use is rejected. Type is required and starts as House. The choices are House, Club, Team, Council, and Other. Color can be left blank, up to 50 characters, as a color name or a hex code. Captain can be left blank. Search a student by name or admission number, and type at least 3 characters. Description can be left blank, up to 5000 characters. Status starts on, which saves the group as active. Cancel closes the form without saving. Create Group saves it. Edit uses the same fields, and the button becomes Update.';

const teacherStudentGroupsMembersSummary =
  'Members lists the students in this group for the year in the header, with the class, role, and the day they joined. You can read the list. Role, class, and search only narrow what you see, and Reset clears them. Add member, Bulk add, changing a role, and Remove are not on your page.';

const teacherStudentGroupsPointsSummary =
  'Points Ledger lists points added or removed for this group in the year in the header. Each row shows the date, the points, the reason, the source, and who recorded it. The total is for that year. Points from an assembly also appear here. Award is not on your page.';

const syllabusChapter = (intro, instruction, booksSummary, bookFormSummary, bookFilesSummary, chaptersSummary, chapterFormSummary, filesSummary, progressSummary, progressListsSummary) => ({
  number: '13',
  title: 'Syllabus',
  intro,
  instruction,
  sections: [
    {
      number: '13.1',
      title: 'Syllabus Books, filters, actions, and Add Book',
      image: '/user-guides/admin/syllabus-books-1-4.png?v=hires',
      imageAlt: 'Syllabus Books: the list, class section and subject filters, View Edit Delete, and Add Book',
      summary: booksSummary,
    },
    {
      number: '13.2',
      title: 'Class, sections, subject, book name, and publisher',
      image: '/user-guides/admin/add-book-5-7.png?v=hires',
      imageAlt: 'Add Book form: class, sections, subject, book name, and publisher',
      summary: bookFormSummary,
    },
    {
      number: '13.3',
      title: 'Description, book file, cover, and save',
      image: '/user-guides/admin/add-book-8-11.png?v=hires',
      imageAlt: 'Add Book form: description, book attachment, cover image, Cancel, and Save',
      summary: bookFilesSummary,
    },
    {
      number: '13.4',
      title: 'Chapters, filters, actions, and Add chapter',
      image: '/user-guides/admin/syllabus-chapters-1-4.png?v=hires',
      imageAlt: 'Syllabus Chapters: the list, filters, Files and notes, Book, Edit, Delete, and Add chapter',
      summary: chaptersSummary,
    },
    {
      number: '13.5',
      title: 'Book, chapter number, name, topics, file, and save',
      image: '/user-guides/admin/add-chapter-form.png?v=hires',
      imageAlt: 'Add Chapter form: book, number, name, topics, notes, visibility, file, video link, days, status, and Save chapter',
      summary: chapterFormSummary,
    },
    {
      number: '13.6',
      title: 'Files and notes, and chapter notes',
      image: '/user-guides/admin/chapter-files-6-7.png?v=hires',
      imageAlt: 'Chapter files and notes: the file list, visibility, video link, and chapter notes',
      summary: filesSummary,
    },
    {
      number: '13.7',
      title: 'Progress, filters, pending chapters, and Open',
      image: '/user-guides/admin/syllabus-progress-1-4.png?v=hires',
      imageAlt: 'Syllabus Progress: Books and Chapters, filters, pending chapters, and Open',
      summary: progressSummary,
    },
    {
      number: '13.8',
      title: 'Skipped, Ongoing, and Completed chapters',
      image: '/user-guides/admin/syllabus-progress-5-7.png?v=hires',
      imageAlt: 'Syllabus Progress: skipped, ongoing, and completed chapters',
      summary: progressListsSummary,
    },
  ],
});

const adminSyllabusIntro =
  'Syllabus has three pages: Books, Chapters, and Progress. Books are the textbooks for a class and subject. Chapters sit on those books. Progress groups the chapters by status for the academic year in the header.';

const adminSyllabusInstruction =
  'Select the academic year in the header. Books, chapters, and progress use that year. A book needs a class and a subject from that year. You can add, edit, and delete books and chapters. Students, parents, and guardians see books and chapters for their own class and section, and they do not open Progress.';

const adminSyllabusBooksSummary =
  'This page lists each syllabus book for the year in the header, with the class, section, subject, publisher, how many chapters it has, and completion. Search matches the book name. Class, section, and subject narrow the list, and the section choices follow the class you pick. All classes, all sections, and all subjects show every book. View opens the book and its chapters. Edit loads the same form. Delete asks you to confirm, then removes the book and its chapters. Only an administrator can delete a book. Add Book opens a blank form. Students, parents, and guardians see books for their own class and section, and they do not get the class and section filters.';

const adminSyllabusBookFormSummary =
  'Class is required. Sections can be left unticked, and then the book is for the whole class. Each ticked section is saved as its own book with the same details. On edit, the first ticked section updates this book, and any extra section creates a new book. The red lines under an empty class or subject are prompts, not a failed save. Subject is required. Pick the class first, and the list is the subjects on that class. Book name is required, up to 255 characters. Publisher can be left blank, up to 255 characters.';

const adminSyllabusBookFilesSummary =
  'Description can be left blank. The book file can be left blank. It can be a PDF, a Word or Excel file, text, a CSV, or a photo, up to 10 MB. Choosing a file uploads it straight away, before Save. People who can open the book can see that file. The cover can be left blank. It can be a JPG, JPEG, PNG, GIF, or WEBP, up to 10 MB, and choosing it also uploads straight away. Cancel closes the form without saving. Save stores the book. Edit uses the same fields.';

const adminSyllabusChaptersSummary =
  'This page lists every chapter for the year in the header, with the book, class, section, subject, chapter number, name, estimated days, status, and completion. Class, section, subject, chapter status, and teacher narrow the list. Section choices follow the class. All classes, all sections, all subjects, all teachers, and all statuses show every chapter. Files and notes opens that chapter. Book opens its book. Edit loads the same form. Delete asks you to confirm, then removes the chapter and its files. Add chapter opens a blank form. Students, parents, and guardians see chapters for their own class and section, and they do not get the class and section filters.';

const adminSyllabusChapterFormSummary =
  'Book is required. Chapter number is required, a whole number from 1 up. Chapter name is required, up to 255 characters, and uses letters, numbers, commas, dashes, and spaces. Topics can be left blank. Each topic uses letters and dashes only, with commas between topics and no spaces. Notes can be left blank. File visibility starts at All. The choices are All, Student and parent, Teachers only, and Admin only. The file can be left blank. It can be a PDF, a Word or Excel file, text, a CSV, or a photo, up to 10 MB, and it uploads when you save. A video link can be left blank, and it must start with http:// or https://. Estimated days can be left blank, a whole number from 0 up. Status starts as Pending. The choices are Pending, Skipped, Ongoing, and Completed. Cancel closes the form without saving. Save chapter stores it. Edit uses the same fields.';

const adminSyllabusFilesSummary =
  'Files and notes lists the files on this chapter and who can see each one. Visibility starts as All. Changing it on a listed file saves straight away. Choosing a file uploads it straight away. A video link must start with http:// or https://, and Add link stores it. View opens a file. Remove asks you to confirm, then takes that file off the chapter. Chapter notes are the notes for this chapter, and Save notes stores them. Students, parents, and guardians read those notes inside Files and notes. The notes card and the progress card show for someone who can edit the chapter.';

const adminSyllabusProgressSummary =
  'Books opens the book list, and Chapters opens the chapter list. Class, section, subject, and teacher narrow every card on this page. Section choices follow the class. Teacher shows for someone who manages the school. The counts are how many chapters are Pending, Skipped, Ongoing, or Completed, and the average completion. Pending lists each chapter still marked Pending for this year, with the book, class, section, subject, chapter, and completion. The number beside the title is how many match the filters. Open opens that chapter files and notes. It does not change the chapter. Students, parents, and guardians do not open this page.';

const adminSyllabusProgressListsSummary =
  'Skipped lists each chapter marked Skipped for this year. Ongoing lists each chapter marked Ongoing. Completed lists each chapter marked Completed. Each row shows the book, class, section, subject, chapter, and completion. The number beside the title is how many match the filters. If none match, the card says so. Open on an ongoing chapter opens that chapter files and notes. Print and Export on a card use that card.';

const teacherSyllabusIntro =
  'Syllabus has three pages: Books, Chapters, and Progress. You see books and chapters for a class and section where you are the class teacher. Progress groups those chapters by status.';

const teacherSyllabusInstruction =
  'Select the academic year in the header. You see books, chapters, and progress for a class and section where you are the class teacher. You can add and edit a book or chapter for that class and section. You cannot delete a book. If permission not given to you, you can open a book or chapter and read it. You cannot add or edit a book, a chapter, a file, or progress. Students, parents, and guardians see books and chapters for their own class and section, and they do not open Progress.';

const teacherSyllabusBooksSummary =
  'This page lists syllabus books for the year in the header, only for a class and section where you are the class teacher. Search matches the book name. Class, section, and subject narrow that list, and the section choices follow the class you pick. View opens the book and its chapters. Edit loads the same form when you can edit books. Add Book opens a blank form when you can add books. Delete stays with an administrator, who removes the book and its chapters. Students, parents, and guardians see books for their own class and section, and they do not get the class and section filters.';

const teacherSyllabusBookFormSummary =
  'You can save a book only for a class and section where you are the class teacher. If you cannot add books, this form is not on your page. Class is required. Sections can be left unticked, and then the book is for the whole class. Each ticked section is saved as its own book with the same details. On edit, the first ticked section updates this book, and any extra section creates a new book. Subject is required. Pick the class first, and the list is the subjects on that class. Book name is required, up to 255 characters. Publisher can be left blank, up to 255 characters.';

const teacherSyllabusBookFilesSummary =
  'Description can be left blank. The book file can be left blank. It can be a PDF, a Word or Excel file, text, a CSV, or a photo, up to 10 MB. Choosing a file uploads it straight away, before Save. The cover can be left blank. It can be a JPG, JPEG, PNG, GIF, or WEBP, up to 10 MB, and choosing it also uploads straight away. Cancel closes the form without saving. Save stores the book when you are allowed to add or edit it. Edit uses the same fields.';

const teacherSyllabusChaptersSummary =
  'This page lists chapters for the year in the header, for a class and section where you are the class teacher. Class, section, subject, and chapter status narrow the list. Section choices follow the class. Teacher is not on your page. Files and notes opens that chapter. Book opens its book. Edit loads the same form when you can edit chapters. Delete asks you to confirm, then removes the chapter and its files, when you can delete chapters. Add chapter opens a blank form when you can add chapters. Students, parents, and guardians see chapters for their own class and section, and they do not get the class and section filters.';

const teacherSyllabusChapterFormSummary =
  'If you cannot add chapters, this form is not on your page. Book is required, and it is a book for a class and section where you are the class teacher. Chapter number is required, a whole number from 1 up. Chapter name is required, up to 255 characters, and uses letters, numbers, commas, dashes, and spaces. Topics can be left blank. Each topic uses letters and dashes only, with commas between topics and no spaces. Notes can be left blank. File visibility starts at All. The choices are All, Student and parent, Teachers only, and Admin only. The file can be left blank, up to 10 MB, and it uploads when you save. A video link must start with http:// or https://. Estimated days can be left blank, a whole number from 0 up. Status starts as Pending. The choices are Pending, Skipped, Ongoing, and Completed. Cancel closes without saving. Save chapter stores it. Edit uses the same fields.';

const teacherSyllabusFilesSummary =
  'Files and notes lists the files on this chapter and who can see each one. You can read the files you are allowed to open. If you can edit the chapter, changing visibility saves straight away, choosing a file uploads it straight away, and Add link stores a video link that starts with http:// or https://. View opens a file. Remove asks you to confirm, then takes that file off the chapter. Save notes stores the chapter notes. If permission is not given, you do not get upload, Remove, Save notes, or the progress card. Students, parents, and guardians read the notes inside Files and notes.';

const teacherSyllabusProgressSummary =
  'Books opens the book list, and Chapters opens the chapter list. Class, section, and subject narrow every card. Section choices follow the class. Teacher is not on your page. The counts are how many chapters you can see that are Pending, Skipped, Ongoing, or Completed, and the average completion. Pending lists each of those chapters still marked Pending, with the book, class, section, subject, chapter, and completion. Open opens that chapter files and notes. It does not change the chapter. Students, parents, and guardians do not open this page.';

const teacherSyllabusProgressListsSummary =
  'Skipped, Ongoing, and Completed use the same filters. Each list shows the chapters you can see in that status, with the book, class, section, subject, chapter, and completion. The number beside the title is how many match. If none match, the card says so. Open on an ongoing chapter opens that chapter files and notes. Print and Export on a card use that card.';

const subjectsChapter = {
  number: '14',
  title: 'Subjects',
  intro:
    'Subjects has three pages: Subject List, Class Subjects, and Curriculum Mapping. Subject List is the school catalog. Class Subjects puts those subjects on a class for the year in the header. Curriculum Mapping records which elective each student takes.',
  instruction:
    'Select the academic year in the header. Subject List belongs to the school and does not change with the year. Class Subjects and Curriculum Mapping use that year. A class needs subjects from the list before a student can be given an elective. Students, parents, and guardians do not manage these pages.',
  sections: [
    {
      number: '14.1',
      title: 'Subject List, Edit or Delete, and Add Subject',
      image: '/user-guides/admin/subject-list-1-3.png?v=hires',
      imageAlt: 'Subject List: the catalog, Edit and Delete, and Add Subject',
      summary:
        'This page lists every subject in the school, with the name, code, type, and whether it is active. Search matches the name. Edit loads the same form. Delete asks you to confirm, then moves the subject to Trashed Subjects so it can be restored. Delete is refused while the subject is still on a class, or while a syllabus book still uses it. Add Subject opens a blank form. The list does not change with the year in the header. Students, parents, and guardians do not use this list.',
    },
    {
      number: '14.2',
      title: 'Name, code, type, description, status, and save',
      image: '/user-guides/admin/add-subject.png?v=hires',
      imageAlt: 'Add Subject form: name, code, type, description, active status, Cancel, and Add Subject',
      summary:
        'Subject name is required, up to 100 characters, and uses letters, numbers, spaces, and a dash. The same name cannot be saved twice for the same type. Subject code can be left blank. Use letters and numbers only, up to 20 characters on the form. It is saved in capitals, and only the first 10 characters are kept. A code already in use is rejected. Subject type is required and starts as Theory. The choices are Theory and Practical. Description can be left blank, up to 5000 characters. Active status starts on. Cancel closes the form without saving. Add Subject saves it. Edit uses the same fields.',
    },
    {
      number: '14.3',
      title: 'Class Subjects, periods, Remove, Manage Groups, and Assign Subject',
      image: '/user-guides/admin/class-subjects-1-5.png?v=hires',
      imageAlt: 'Class Subjects: the roster, periods per week, Remove, Manage Groups, and Assign Subject',
      summary:
        'This page lists each subject assigned to a class for the year in the header, with the code, type, category, and periods per week. All classes shows every class, or you can keep one class. Search matches the subject name. Periods per week is a whole number from 0 to 40, and it saves when you leave the box. 0 means the timetable will not place that subject on its own. Remove asks you to confirm, then takes the subject off the class. Remove is refused while a teacher is still assigned to it, or while it is on the timetable. Manage Groups opens the elective groups for a class. Assign Subject opens the form that puts subjects on a class. Students, parents, and guardians do not use this page.',
    },
    {
      number: '14.4',
      title: 'Class, new group, saved groups, delete, and Done',
      image: '/user-guides/admin/manage-elective-groups.png?v=hires',
      imageAlt: 'Manage Elective Groups: class, add group, saved groups, delete, and Done',
      summary:
        'Class is required. Group name is required, up to 100 characters. Max Subjects is the most this group can hold. 0 means no cap. Selectable is how many of those subjects a student may pick. 0 means no pick limit. Add Group saves the group for that class. On a saved group, changing the name, the maximum, or the selectable count saves when you leave the box. Delete asks you to confirm, then removes the group. Delete is refused while subjects are still in the group. Done closes the form.',
    },
    {
      number: '14.5',
      title: 'Class, subjects, periods, elective group, and Assign Subject',
      image: '/user-guides/admin/assign-subject.png?v=hires',
      imageAlt: 'Assign Subject to Class: class, subjects, periods, elective, group, Cancel, and Assign Subject',
      summary:
        'Class is required. Subject is required. Tick at least one, and you can tick more than one. Subjects already on this class for this year are not shown. An inactive subject cannot be assigned. Periods per week is a whole number from 0 to 40 and starts at 0. Elective Subject starts off. Turn it on for an optional subject. Elective Group can be left blank. Choose a saved group, or use New Group. New Group asks for a name, a maximum, and a selectable count, and Create Group saves that group. If the group has a maximum, you cannot assign more subjects than that. Cancel closes without saving. Assign Subject saves the selected subjects for this year.',
    },
    {
      number: '14.6',
      title: 'Curriculum Mapping, filters, the pencil, and Assign Electives',
      image: '/user-guides/admin/curriculum-mapping-1-4.png?v=hires',
      imageAlt: 'Curriculum Mapping: student elective choices, class and section filters, the pencil, and Assign Electives',
      summary:
        'This page lists each student who already has an elective choice for the year in the header, with the class, section, and the subjects they chose. All Classes and All Sections narrow the list. All Sections stays off until a class is chosen, then it lists that class sections. Choosing a class or section does not change any student choices. Search matches the student. The pencil opens that student in the assign form, with their current subjects already ticked. It does not save until Apply Selection. Assign Electives opens a blank form. Students, parents, and guardians do not manage this page.',
    },
    {
      number: '14.7',
      title: 'Target class, electives, students, and Apply Selection',
      image: '/user-guides/admin/bulk-assign-electives.png?v=hires',
      imageAlt: 'Bulk Assign Electives: target class, section, available electives, students, Cancel, and Apply Selection',
      summary:
        'Target Class is required. Target Section follows the class. All Sections shows every student in the class. Available Electives lists the optional subjects already on that class. Each row shows the subject, its type, the group, and how many you may pick. No Limit means the group has no pick cap. Ungrouped means the subject has no group. You cannot tick more subjects than that group allows. Select Students lists students in that class and section, including those with no choice yet. Search matches a name or admission number. Select all ticks only the students you can see. Current Choices shows the subjects they already have, or None. The note counts the electives and students you ticked. Assigning a subject replaces that student choice in the same elective group. Choices in other groups stay. Cancel closes without saving. Apply Selection saves the ticked subjects. You must choose at least one subject and one student.',
    },
  ],
};

const classesChapter = {
  number: '11',
  title: 'Classes',
  intro:
    'Classes has four pages: Class Room, Section, Classes, and Class Sections. Class Room holds the rooms. Section holds the section names. Classes holds the class list. Class Sections assigns those sections to a class, with a room and a student limit.',
  instruction:
    'Select the academic year in the header before you add a section. The section list, and the class, teacher, and room shown on it, use that year. Class rooms and classes belong to the school and are not limited to one year. Class Section Assignments follow the year marked Current. Students, parents, and guardians do not use these pages.',
  sections: [
    {
      number: '11.1',
      title: 'Class Room, sort, Edit or Delete, and Add Class Room',
      image: '/user-guides/admin/classroom-1-4.png?v=hires',
      imageAlt: 'Class Room list: the page, Newest first, Edit or Delete, and Add Class Room',
      summary:
        'This page lists every classroom in the school, with the room number, how many students it holds, the building, the floor, and whether it is active. Newest first is the default, and the list can also run A to Z or Z to A by room number. Edit opens the same form. Delete asks you to confirm, then removes the room. Add Class Room opens a new room. Students, parents, and guardians do not use this page.',
    },
    {
      number: '11.2',
      title: 'Room number, capacity, building, floor, status, and save',
      image: '/user-guides/admin/add-classroom-5.png?v=hires',
      imageAlt: 'Add Class Room form: room number, capacity, building, floor, status, Cancel, and Add Class Room',
      summary:
        'Room number is required, up to 50 characters, and uses letters, numbers, and a hyphen. It has to include a letter or a number, and it cannot start or end with a hyphen. A room number already saved is rejected. Capacity starts at 50 and has to be a whole number of at least 1. Building can be left blank, up to 100 characters, using letters, numbers, and spaces. Floor can be left blank, up to 50 characters, and may also use a hyphen. Status starts on, which saves the room as active. Cancel closes the form without saving. Add Class Room saves it. Edit uses the same fields, and the button becomes Update.',
    },
    {
      number: '11.3',
      title: 'Sections, sort, Edit or Delete, and Add Section',
      image: '/user-guides/admin/sections-1-4.png?v=hires',
      imageAlt: 'Sections list: the page, Newest first, Edit or Delete, and Add Section',
      summary:
        'This page lists sections for the academic year in the header, with the section name, class, section teacher, student limit, room, description, and status. Newest first is the default, and the list can also run A to Z or Z to A by section name. Edit opens the same form. Delete asks you to confirm, then moves the section to Trashed so it can be restored. Add Section opens a new section.',
    },
    {
      number: '11.4',
      title: 'Section name, class, teacher, room, status, and save',
      image: '/user-guides/admin/add-section-5-14.png?v=hires',
      imageAlt: 'Add Section form: name, class, student limit, description, teacher, room, status, Cancel, and Add Section',
      summary:
        'Section name is required, letters and numbers only, up to 10 characters. Class can be left blank, but a class is required if you set a teacher, a room, or a student limit. Max students can be left blank and then saves as 30 when a class is chosen, or you can enter 1 to 10000. Description can be left blank, up to 5000 characters. Section teacher can be left blank, and needs a class when set. The room list is an active classroom, and a room already used by another section this year is rejected. A room also needs a class. Status starts on, and it applies when the section is assigned to a class. Cancel closes without saving. Add Section saves it. Edit uses the same form.',
    },
    {
      number: '11.5',
      title: 'Classes, sort, Edit or Delete, and Add Class',
      image: '/user-guides/admin/classes-1-4.png?v=classeslist',
      imageAlt: 'Classes list: the page, Newest first, Edit or Delete, and Add Class',
      summary:
        'This page lists every class in the school, with the class name, class code, student limit, and status. Newest first is the default, and the list can also run A to Z or Z to A by class name. Edit opens the same form. Delete asks you to confirm, then moves the class to Trashed so it can be restored. Add Class opens a new class.',
    },
    {
      number: '11.6',
      title: 'Class name, class code, student limit, description, status, and save',
      image: '/user-guides/admin/add-class.png?v=hires',
      imageAlt: 'Add Class form: class name, class code, max students, description, status, Cancel, and Add Class',
      summary:
        'Class name is required, up to 50 characters, using letters, numbers, and spaces, and a name already in use is rejected. Class code can be left blank, up to 10 characters, with the same characters, and a code already in use is rejected. Max students can be left blank and then saves as 30, or you can enter 1 to 10000. On edit, clearing that field removes the student limit. Description can be left blank, up to 5000 characters. Status starts on, which saves the class as active. Cancel closes without saving. Add Class saves it. Edit uses the same fields, and the button becomes Update.',
    },
    {
      number: '11.7',
      title: 'Class Section Assignments, sections, and Manage',
      image: '/user-guides/admin/class-sections-1-3.png?v=hires',
      imageAlt: 'Class Section Assignments: the page, assigned sections, and Manage',
      summary:
        'This page lists every class and the sections assigned for the current academic year. Each section shows its name and room, or the class shows that no sections are assigned. Capacity is the student seats added up from those sections. Manage opens the assignment form for that class.',
    },
    {
      number: '11.8',
      title: 'Select sections, room, student limit, and Save Assignments',
      image: '/user-guides/admin/class-sections-assign.png?v=hires',
      imageAlt: 'Manage Sections: select sections, classroom, student limit, general room, Cancel, and Save Assignments',
      summary:
        'The form is for one class. Tick the sections that belong to this class. Each selected section needs a classroom and a student limit. The room list shows the room number, building, and floor. The same room cannot be used twice in this class, or by another class this year. Choosing a room sets the student limit to that room capacity, and the limit cannot go above it. With no room chosen, the limit starts at 30. If no section is ticked, one optional room and one student limit can cover the whole class. Cancel closes without saving. Save Assignments stores the section, the room, and the student limit.',
    },
  ],
};

const studentsChapter = (intro, instruction, listSummary, importSummary, formTopSummary, personalSummary, fatherSummary, guardianSummary, moreSummary) => ({
  number: '15',
  title: 'Students',
  intro,
  instruction,
  sections: [
    {
      number: '15.1',
      title: 'All Students, filters, actions, Add Student, and Import',
      image: '/user-guides/admin/students-annotated-1.png?v=peoples1',
      imageAlt: 'Students grid callouts 1 to 5: Students title, All Time and Sort, Actions menu, Add Student, and Import Students',
      summary: listSummary,
    },
    {
      number: '15.2',
      title: 'Import students',
      image: '/user-guides/admin/students-annotated-2.png?v=peoples1',
      imageAlt: 'Import students callout 6: Import students form',
      summary: importSummary,
    },
    {
      number: '15.3',
      title: 'Photo, year, admission, class, and section',
      image: '/user-guides/admin/students-annotated-3.png?v=peoples1',
      imageAlt: 'Add Student callouts 1 to 11: photo, academic year, admission, GR, dates, roll, status, names, class, and section',
      summary: formTopSummary,
    },
    {
      number: '15.4',
      title: 'Rest of personal information',
      image: '/user-guides/admin/students-annotated-4.png?v=peoples1',
      imageAlt: 'Add Student callout 1: rest of personal information',
      summary: personalSummary,
    },
    {
      number: '15.5',
      title: 'Father',
      image: '/user-guides/admin/students-annotated-5.png?v=peoples1',
      imageAlt: 'Add Student callouts 12 to 17: link father, photo, name, email, phone, and occupation',
      summary: fatherSummary,
    },
    {
      number: '15.6',
      title: 'Mother and guardian',
      image: '/user-guides/admin/students-annotated-6.png?v=peoples1',
      imageAlt: 'Add Student callout 1: mother and guardian sections',
      summary: guardianSummary,
    },
    {
      number: '15.7',
      title: 'Siblings, address, documents, and save',
      image: '/user-guides/admin/students-annotated-7.png?v=peoples1',
      imageAlt: 'Add Student callout 1: siblings, address, documents, medical history, previous school, other details, Cancel, and Add Student',
      summary: moreSummary,
    },
  ],
});

const adminStudentsIntro =
  'Students has two views of the same students for the year in the header. All Students shows cards. Students List shows rows. The list icon opens the table. The grid icon opens the cards. Both use the same filters, sort, and actions.';

const adminStudentsInstruction =
  'Select the academic year in the header. The cards, the table, a new student, and an import use that year. You can add a student, import a class, edit one, mark one inactive, promote one, and delete one. A teacher sees students in a class and section where they are the class teacher, and does not see Add Student, Import Students, Edit, Disable, Promote, or Delete. A student sees only their own card. Parents and guardians do not manage this list.';

const adminStudentsListSummary =
  'All Students and Students List show the same students, as cards or as rows. All Time shows every student. The other choices are Today, Yesterday, Last 7 Days, Last 30 Days, This Month, and Last Month. A chosen range keeps a student when the date they joined, or the date they were added, falls in that range. Sort by A-Z is the starting order. The other orders are Descending, Recently Viewed, and Recently Added. These only change the list on screen. View Student opens that student page. Edit opens the add form with their details filled in. Disable asks you to confirm, then marks them Inactive. Enable does the opposite for an inactive student. Promote Student opens Student Promotion. Delete asks you to confirm, then removes the student. Add Student opens a blank form. Import Students opens the import form.';

const adminStudentsImportSummary =
  'Import is for one class and one section. The academic year is the one selected in the header. Class is required. The red Select class line is an empty prompt. Section is required when that class has sections, and it stays off until a class is chosen. Select class first is the empty prompt. The file is an Excel workbook or a CSV. Download sample Excel template gives a Guide sheet and a Data sheet with a demo row. Class and section are not columns in the file. After you choose a file, a preview shows the admission number, name, roll number, Aadhaar number, and whether the row is OK. Every row must be OK before Import. Import adds the students. Close leaves the form.';

const adminStudentsFormTopSummary =
  'Upload and Remove handle the profile photo. Use a JPG or PNG up to 4MB. Choosing a photo sends it up straight away, before you press Add Student. Remove clears it on this form. Academic Year is required. It shows the year selected in the header, and you cannot type over it. Admission Number is required, letters and numbers only. The box shows ADM001 as an example. The refresh icon fills the next number. It must be unique, and the check runs when you leave the box. GR Number can be left blank, using letters, numbers, and a hyphen, up to 30 characters. Leave it blank and the school assigns one. Admission Date starts as today and must fall inside the academic year. Roll Number can be left blank, using letters, numbers, and a hyphen, up to 50 characters. It is the class roll and can change each year. The box shows 15 as an example. Status starts at Active. The choices are Active and Inactive. First name and last name are required, letters and spaces, up to 100 characters. The boxes show Rahul and Sharma as examples. Class is required. The red Select class line is an empty prompt. Section can be left blank. Choices appear after you choose a class. The red Select section line is an empty prompt. If that class has no section yet, add one under Academic, then Sections.';

const adminStudentsPersonalSummary =
  'Gender is Male or Female. Date of birth, blood group, and religion can be left blank. The red lines are empty prompts. House is the student school house. You manage houses under Academic, then Student Groups. Caste stays off until you choose a religion. Primary contact number is required, 10 digits, and it may match a parent number. This number is the first password the student uses to sign in. The box shows 1234567890 as an example. Email can be left blank, and it must be a valid email if you enter one. The box shows demo@gmail.com as an example. Mother tongue can be left blank. For Language known, type a language and press Enter. Unique Student ID (Saral ID) can be left blank, up to 50 characters. PEN Number (UDISE ID) can be left blank, up to 20 characters. Leave either blank and one is assigned. Aadhaar number is required, exactly 12 digits, and it must be unique.';

const adminStudentsFatherSummary =
  'Link existing father searches a parent already saved. Type at least 2 characters. It matches a mobile number, email, or name. Choosing a match fills the name, phone, email, and occupation. Saving reuses that parent instead of making a second contact. Father photo uses Upload and Remove, a JPG or PNG up to 4MB, and choosing a photo sends it up straight away. Father name can be left blank, letters and spaces, up to 100 characters. Email can be left blank. Enter a valid email if you have one. The box shows demo@gmail.com as an example. Phone number can be left blank. Use 10 digits if you enter one. It may match the student or the mother. This number is the first password the father uses to sign in. Father occupation can be left blank, letters and spaces, up to 100 characters. The box shows Business as an example.';

const adminStudentsGuardianSummary =
  'Mothers Info uses the same search, photo, name, email, phone, and occupation as the father. The search says link existing mother. The occupation box shows Homemaker as an example. Guardian Details uses the same search, photo, name, phone, email, and occupation. It also asks for Guardian Relation With Student, letters only, up to 30 characters, and Address, up to 500 characters. The relation box shows Uncle as an example. The occupation box shows Teacher as an example.';

const adminStudentsMoreSummary =
  'Open a card to fill in that section. Siblings asks whether a brother or sister studies here, then the name, roll number, admission number, class, and section. Add New adds another. Address is the current address and the permanent address. Documents is a medical PDF and a transfer certificate PDF, up to 4MB each. Choosing a PDF sends it up straight away. Medical History is the condition, allergies, and medications. Previous School Details is the school name. Other Details is the bank name, branch, IFSC number, and any other note. Cancel leaves the form. Add Student saves it.';

const teacherStudentsIntro =
  'Students has two views of the same students for the year in the header. All Students shows cards. Students List shows rows. You see students in a class and section where you are the class teacher. The list icon opens the table. The grid icon opens the cards.';

const teacherStudentsInstruction =
  'Select the academic year in the header. You see students in a class and section where you are the class teacher. You can open a student. You do not see Add Student, Import Students, Edit, Disable, Promote, or Delete. A student sees only their own card. Parents and guardians do not manage this list.';

const teacherStudentsListSummary =
  'All Students and Students List show the same students you can see, as cards or as rows. All Time shows every one of them. The other choices are Today, Yesterday, Last 7 Days, Last 30 Days, This Month, and Last Month. A chosen range keeps a student when the date they joined, or the date they were added, falls in that range. Sort by A-Z is the starting order. The other orders are Descending, Recently Viewed, and Recently Added. These only change the list on screen. View Student opens that student page. Add Student, Import Students, Edit, Disable, Promote, and Delete are not on your page.';

const teacherStudentsImportSummary =
  'This form is for an administrator. You do not see Import Students. Import is for one class and one section in the year selected in the header. Class is required, and section is required when that class has sections. The file is an Excel workbook or a CSV. Download sample Excel template gives a Guide sheet and a Data sheet with a demo row. Class and section are not columns in the file. A preview must show every row as OK before Import adds the students. Close leaves the form.';

const teacherStudentsFormTopSummary =
  'This form is for an administrator. You do not see Add Student. The profile photo is a JPG or PNG up to 4MB, and choosing it sends it up straight away. Academic Year shows the year in the header and cannot be typed over. Admission Number is required, letters and numbers only, and it must be unique. GR Number can be left blank and the school assigns one. Admission Date must fall inside the academic year. Roll Number can be left blank. Status starts at Active. First name, last name, and class are required. Section can be left blank, and its choices appear after the class is chosen.';

const teacherStudentsPersonalSummary =
  'You do not open this form. Gender is Male or Female. Date of birth, blood group, religion, house, mother tongue, and language known can be left blank. Caste stays off until a religion is chosen. Primary contact number is required, 10 digits, and it is the first password the student uses to sign in. Email can be left blank. Saral ID and PEN Number can be left blank, and one is assigned. Aadhaar number is required, exactly 12 digits, and it must be unique.';

const teacherStudentsFatherSummary =
  'You do not open this form. Link existing father searches a parent already saved, with at least 2 characters, and saving reuses that parent. The photo is a JPG or PNG up to 4MB. Name, email, phone, and occupation can be left blank. The phone, when entered, is the first password the father uses to sign in.';

const teacherStudentsGuardianSummary =
  'You do not open this form. Mothers Info uses the same search, photo, name, email, phone, and occupation as the father. Guardian Details adds the relation to the student, letters only up to 30 characters, and an address up to 500 characters.';

const teacherStudentsMoreSummary =
  'You do not open this form. The remaining cards are siblings, address, documents, medical history, previous school, and other details. A document is a PDF up to 4MB, and choosing it sends it up straight away. Cancel leaves the form. Add Student saves it.';

const adminStudentPromotionIntro =
  'Student Promotion is under Peoples, Students. It promotes students to another session and class, marks students as school leaving, and shows promotion and leaving history for the academic year in the header.';

const adminStudentPromotionInstruction =
  'Select the academic year in the header before you open the list. Promote needs a future target session and target class. Leaving needs a leaving date. Confirm Promote Students or Mark as Leaving only works for someone with the Promote Students action, or Edit on Student Promotion when Roles & Permissions is on. Export buttons appear only when export is allowed on Student Promotion.';

const adminStudentPromotionSetupSummary =
  'The note explains that promotion updates class, section, and year and writes promotion history. Action Mode is Promote or Leaving. On Promote, Current Session is the header year. Promotion from Class and Section filter the roster; section can stay empty. Promote to Session must be after the current year. Target class is required and target section is optional. Reset Promotion clears the setup and hides the list. Manage Promotion validates the fields and opens the student table.';

const adminStudentPromotionListSummary =
  'Students List shows active students for the header year who match the filters. Sort reorders the table. Tick rows to select them. Exam Result shows Pass, Fail, Pending, or N/A, and View opens the latest exam read-only. Promote Students opens Confirm Promotion. Cancel closes without saving. If selected students still owe fees, a pending-fees message appears before promotion can finish.';

const adminStudentPromotionHistorySummary =
  'Promotion History lists past moves with from and to session, class, section, date, status, and promoter. Export PDF and Export Excel download the table when export is allowed and rows exist.';

const adminStudentPromotionLeavingSetupSummary =
  'Choose Leaving in Action Mode. Leaving from Class and Section filter the list. Leaving Action warns that selected students will be marked as school leaving. Leaving Date is set on the form. Manage Promotion opens Select Students to Mark Leaving.';

const adminStudentPromotionLeavingListSummary =
  'Tick students and use Mark as Leaving. Confirm Leaving stores optional reason and remarks. Leaving Students has Leaving List and Rejoin List. Search, academic year, result, and leaving dates filter Leaving List. Refresh reloads the list. Export PDF and Export Excel appear when export is allowed. Rejoin opens a form for an active leaving row. View on the result column opens the exam read-only.';

const adminParentsIntro =
  'Parents is under Peoples. It has two views of parent families linked to students in the header academic year. All Parents opens the card grid. Parents List opens the table. The list icon opens the table. The grid icon opens the cards.';

const adminParentsInstruction =
  'Select the academic year in the header. The grid and list only show parents whose children are active and enrolled in that year. You can filter, sort, export, add a parent family, view details, edit, and delete when your role allows it. A teacher with view-only access on Parents can open the pages and view a family but does not see Add Parent, Edit, or Delete unless the school grants create, edit, or delete on Parents. Parent and student portal roles do not use this staff list.';

const adminParentsGridSummary =
  'Parents opens from Peoples, Parents, All Parents. The action menu can refresh the list, print the grid, export PDF, or export Excel when export is allowed. Add Parent opens the form. Parents Grid is the card view. All Time shows every family. The other date ranges keep a family when the date a child joined, or the date the record was added, falls in that range. Filter narrows by parent name and by child. Reset clears the filter. Apply runs it. The list icon opens Parents List. The grid icon stays on the cards. Sort by A-Z is the starting order. The other orders are Descending, Recently Viewed, and Recently Added. Each card shows the admission number or family label, father and mother names and photos, contact details, linked children, and View Details. The card menu has View Parent and Edit when edit is allowed. Load More appears at the bottom of the grid.';

const adminParentsListSummary =
  'Parents List is the table view of the same families. It uses the same date range, filter, view toggle, and sort as the grid. Filter also offers class and status. Row Per Page changes how many rows show. Search filters the table. The columns are serial number, parent name with mother name and added date, child with class, phone, email, and action. Click the serial number or name to open View Details. Export, print, and refresh work like on the grid. Add Parent opens the same form as on the grid.';

const adminParentsFormSummary =
  'Add Parent opens a modal. Close or Cancel leaves without saving. Father\'s Details asks for first name, last name, mobile, and email, all required. Occupation is optional. Father photo is a JPG or PNG up to 4MB and uploads when you choose a file. Mobile and email must be unique and are checked when you leave the field. Father and mother must use different mobile numbers and different emails. Mother\'s Details uses the same pattern with mother first name, last name, mobile, email, occupation, and photo. Letters-only rules apply to names. Mobile is 10 digits. Email is used for parent login.';

const adminParentsSaveSummary =
  'Select child is optional. Type at least two characters to search students who do not already have a father or mother linked. Choosing a student links this family when you save. If the student already has parents, a note warns that saving will replace them. Add Parent creates the accounts and links the child when you picked one. You can link a student later from Edit. Edit Parent uses the same fields. Delete Parent asks you to confirm, then removes the parent record when delete is allowed.';

const teacherParentsIntro =
  'Parents is under Peoples. All Parents shows cards. Parents List shows rows. You see parent families for students in the header year who are in a class and section where you are the class teacher.';

const teacherParentsInstruction =
  'Select the academic year in the header. The default teacher role has view on Parents only. You can open the grid or list and view a family. Add Parent, Edit, Delete, and export are not on your page unless the school grants create, edit, delete, or export on Parents under Roles & Permissions.';

const teacherParentsGridSummary =
  'Parents opens from Peoples, Parents. Refresh, print, and export appear only when your role allows export. Add Parent is not on your page unless create is granted. Parents Grid shows cards for families you can see. All Time and the other date ranges work the same as for an administrator. Filter, sort, and the list or grid toggle work the same way. View Details and View Parent open read-only details. Edit and Delete on the card menu appear only when those actions are granted.';

const teacherParentsListSummary =
  'Parents List shows the same families as rows. Class and status filters, search, and Row Per Page work the same way. You can open View Details from the name or serial number. Add Parent and row actions for edit or delete appear only when your role grants them.';

const teacherParentsFormSummary =
  'This form is for staff who can create or edit parents. You do not see Add Parent or Edit unless the school grants it. Father and mother names, mobiles, and emails are required when the form is used. Photos are JPG or PNG up to 4MB. Email is used for login. Father and mother must have different mobiles and emails.';

const teacherParentsSaveSummary =
  'Select child is optional when an administrator adds or edits a parent. It searches students without linked parents. Cancel closes without saving. Add Parent or Save Changes stores the record when you have permission.';

const parentsChapter = (intro, instruction, gridSummary, listSummary, formSummary, saveSummary) => ({
  number: '18',
  title: 'Parents',
  intro,
  instruction,
  sections: [
    {
      number: '18.1',
      title: 'Parents grid',
      image: '/user-guides/admin/parents-annotated-1.png?v=peoples1',
      imageAlt: 'Parents grid callouts 1 to 8: page title, export menu, Add Parent, date range, Filter, list or grid toggle, Sort, and a parent card',
      summary: gridSummary,
    },
    {
      number: '18.2',
      title: 'Parents List',
      image: '/user-guides/admin/parents-annotated-2.png?v=peoples1',
      imageAlt: 'Parents List callouts 9 to 14: page title, list title, Filter, Search, table, and Add Parent',
      summary: listSummary,
    },
    {
      number: '18.3',
      title: 'Add Parent — father and mother',
      image: '/user-guides/admin/parents-annotated-3.png?v=peoples1',
      imageAlt: 'Add Parent callouts 15 to 17: modal title, Father\'s Details, and Mother\'s Details',
      summary: formSummary,
    },
    {
      number: '18.4',
      title: 'Link child, Cancel, and Add Parent',
      image: '/user-guides/admin/parents-annotated-4.png?v=peoples1',
      imageAlt: 'Add Parent callouts 18 to 20: Select child, Cancel, and Add Parent',
      summary: saveSummary,
    },
  ],
});

const portalParentsChapter = {
  number: '18',
  title: 'Parents',
  intro:
    'Parents is a staff page under Peoples. It lists father and mother contact details linked to students for the academic year in the header.',
  instruction:
    'The default parent and student roles do not include the Peoples module, so All Parents and Parents List are not on your menu. Father and mother emails and mobiles on your child\'s record are used for parent login. Ask the school office to update parent details if something is wrong.',
  sections: [],
};

const adminGuardiansIntro =
  'Guardians is under Peoples. It has two views of guardians linked to students in the header academic year. The card page is Guardians Grid at /parent/guardians-grid. The table is Guardian List at /parent/guardians-list. The list icon opens the table. The grid icon opens the cards.';

const adminGuardiansInstruction =
  'Select the academic year in the header. The grid and list only show guardians whose students are active and enrolled in that year. You can filter, sort, export, view details, and edit when your role allows guardian management. With Roles & Permissions enforcement, create, edit, and delete on the guardians submodule apply; without enforcement, Manage Guardians on Peoples controls whether Edit appears on the list. The default teacher role has view on guardians only. Parent and student portal roles do not use this staff list.';

const adminGuardiansGridSummary =
  'Guardians opens from Peoples, Guardians. Refresh, print, export PDF, and export Excel download the grid when export is allowed. Guardians Grid is the card view. All Time and the other date ranges filter by when the guardian or student record was added or joined. Filter narrows by guardian name and by child. Reset and Apply work like on Parents. The list and grid icons switch views. Sort by A-Z, Descending, Recently Viewed, or Recently Added. Each card shows an admission label or guardian id, guardian type badge, name, email, phone, linked children, and View Details. The card menu has View Guardian and Edit. When no guardians match the year, the grid is empty and Load More appears at the bottom.';

const adminGuardiansListSummary =
  'The page title reads Guardian. Guardian List is the table. It uses the same date range, filter, view toggle, and sort as the grid. Filter also offers class. Row Per Page and Search filter the table. Columns are serial number, guardian name, guardian type, child, phone, email, and action. Click the serial number or name to view details. Edit on the row action menu opens the edit form when manage guardians is allowed. Export, print, and refresh work like on the grid. When no rows exist, the table shows No data.';

const adminGuardiansFormSummary =
  'Add Guardian and Edit Guardian use the same fields. Edit opens from the card or row menu when you can manage guardians. Upload and Remove handle the profile photo. JPG or PNG up to 4MB. Name is required. Phone Number is required and must be unique. Email Address is optional but must be unique if you enter one. Inline messages show when phone or email is already in use. Child is required when you save a new guardian. Type at least two characters to search students. If the student already has a guardian, an info note warns that saving will replace them. Cancel closes without saving. Add Guardian saves a new link. Save Changes updates an existing guardian.';

const teacherGuardiansIntro =
  'Guardians is under Peoples. Guardians Grid shows cards. Guardian List shows rows. You see guardians for students in the header year who are in a class and section where you are the class teacher.';

const teacherGuardiansInstruction =
  'Select the academic year in the header. The default teacher role has view on guardians only. You can open the grid or list and view a guardian. Edit, export, and manage actions are not on your page unless the school grants edit, delete, export, or create on guardians, or Manage Guardians on Peoples when enforcement is off.';

const teacherGuardiansGridSummary =
  'Guardians opens from Peoples, Guardians. Refresh, print, and export appear only when export is allowed. Guardians Grid shows cards you can view. Filters, sort, and the list or grid toggle work the same as for an administrator. View Details and View Guardian open read-only details. Edit on the card menu appears only when your role allows guardian management.';

const teacherGuardiansListSummary =
  'Guardian List shows the same guardians as rows. Class filter, search, and Row Per Page work the same way. Open View Details from the name or serial number. Edit on the action menu appears only when manage guardians is allowed.';

const teacherGuardiansFormSummary =
  'This form is for staff who can manage guardians. You do not see Edit unless the school grants it. Name and phone are required. Email is optional. Photo is JPG or PNG up to 4MB. Child links the guardian to a student. Cancel closes without saving. Save Changes stores updates when you have permission.';

const guardiansChapter = (intro, instruction, gridSummary, listSummary, formSummary) => ({
  number: '19',
  title: 'Guardians',
  intro,
  instruction,
  sections: [
    {
      number: '19.1',
      title: 'Guardians grid',
      image: '/user-guides/admin/guardians-annotated-1.png?v=designer',
      imageAlt: 'Guardians grid callouts 1 to 7: page title, export menu, date range, Filter, list or grid toggle, Sort, and the grid area',
      summary: gridSummary,
    },
    {
      number: '19.2',
      title: 'Guardian List',
      image: '/user-guides/admin/guardians-annotated-2.png?v=designer',
      imageAlt: 'Guardian List callouts 8 to 13: page title, list title, Filter, Search, table, and export menu',
      summary: listSummary,
    },
    {
      number: '19.3',
      title: 'Add or edit guardian',
      image: '/user-guides/admin/guardians-annotated-3.png?v=designer',
      imageAlt: 'Guardian form callouts 14 to 19: title, photo upload, name phone and email, Child, Cancel, and Add Guardian',
      summary: formSummary,
    },
  ],
});

const portalGuardiansChapter = {
  number: '19',
  title: 'Guardians',
  intro:
    'Guardians is a staff page under Peoples. It lists guardians linked to students for the academic year in the header.',
  instruction:
    'The default parent and student roles do not include the Peoples module, so Guardians Grid and Guardian List are not on your menu. If you log in as a guardian role, you see your own linked student from the guardian dashboard, not this staff roster. Ask the school office to update guardian details if something is wrong.',
  sections: [],
};

const adminTeachersIntro =
  'Teachers is under Peoples. It has two views of school staff who teach. Teachers Grid shows cards at /teacher/teacher-grid. Teacher List shows rows at /teacher/teacher-list. The list icon opens the table. The grid icon opens the cards. Add Teacher opens the full registration form at /teacher/add-teacher.';

const adminTeachersInstruction =
  'The teacher roster is not scoped to the header academic year; it lists staff teachers for the school. You can filter, sort, export, add a teacher, open Teacher Details, edit, mark active or inactive, and delete when your role allows create, edit, delete, or export on the teachers submodule. If you log in as a teacher role on these pages, you only see your own card and row, not the full staff list. Parent and student portal roles do not manage this list.';

const adminTeachersGridSummary =
  'Teachers opens from Peoples, Teachers. Refresh, print, export PDF, and export Excel download the grid when export is allowed. Add Teacher opens the registration form. Teachers Grid is the card view. All Time and the other date ranges filter by joining or added date. Filter narrows by name contains and by class assignment. Reset clears the filter. The list and grid icons switch views. Sort by A-Z, Descending, Recently Viewed, or Recently Added. Each card shows employee code, Active or Inactive badge, photo, name, email, phone, subject tag, and View Details. The card menu has Edit and Delete when allowed. View Details and the employee code open Teacher Details.';

const adminTeachersListSummary =
  'Teacher List is the table view. Teachers List is the card title on the list page. Filter adds status (Active or Inactive) to name and class filters. Row Per Page and Search filter the table. Columns include serial number, name with photo, class, subject, email, phone, date of join, status, and action. Click the name or serial number to open Teacher Details. The action menu has View Teacher, Edit, Mark as active or inactive, and Delete when allowed. Add Teacher opens the same form as on the grid when create is allowed. Export, print, and refresh work like on the grid.';

const adminTeachersPersonalSummary =
  'Add Teacher opens a long form. Back returns to the list. Personal Information includes optional profile photo (JPG, PNG, WebP, or GIF up to 2MB), optional Teacher ID (auto-generated if blank), required first and last name, required mobile and email, required date of joining, required qualification and work experience, optional gender, blood group, family names, dates, addresses, PAN, status, and notes. Department, designation, class, and subject appear in this section for assignment. Further cards on the same page include Payroll, Bank Account Detail, Salary Components Assignment, Social Media Links, and Transport or Hostel when enabled.';

const adminTeachersSaveSummary =
  'Documents uploads Resume and Joining Letter PDFs up to 4MB each. Password is optional; if blank, the teacher signs in with their phone number as the initial password. Otherwise enter password and confirm password. Cancel leaves without saving. Add Teacher validates required fields and uniqueness of email and phone, then creates the staff record. Update Teacher on edit uses the same sections.';

const teacherStaffTeachersChapter = {
  number: '20',
  title: 'Teachers',
  intro:
    'Teachers under Peoples is the staff directory of all teachers. Teachers Grid and Teacher List show every teacher for administrators.',
  instruction:
    'The default teacher role does not include the teachers submodule under Peoples. You do not see the full staff Teachers menu unless the school grants Peoples → View (or manage actions) on Teachers. When you open Teachers Grid or Teacher List as a teacher, you only see your own profile, not colleagues. Use your teacher dashboard and profile pages for day-to-day work.',
  sections: [],
};

const portalTeachersChapter = {
  number: '20',
  title: 'Teachers',
  intro:
    'Teachers is a staff page under Peoples. It lists teacher staff records, assignments, and contact details.',
  instruction:
    'The default parent and student roles do not include the Peoples module, so Teachers is not on your menu. Ask the school office if you need contact details for a teacher.',
  sections: [],
};

const adminStaffIntro =
  'Staffs is under HRM. It is the staff directory at /hrm/staff for non-teaching staff. Teachers are managed under Peoples, Teachers. The staff list excludes teachers so each person appears in one place. Add Staff opens /hrm/add-staff.';

const adminStaffInstruction =
  'You need view on the HRM staffs submodule to open the directory. Create, edit, delete, and export use the same submodule actions. Headmaster and administrative roles keep directory access even when subscription read-only mode hides create and edit. The list is not scoped to the header academic year.';

const adminStaffListSummary =
  'Staffs opens from HRM, Staffs. Refresh, print, export PDF, and export Excel download the filtered list when export is allowed. Add Staff opens the registration form when create is allowed. Staff List is the table. Filter narrows by name contains, department, designation, status, and join date from or to. Reset clears filters. Sort offers Name A–Z, Name Z–A, join date oldest or newest, and recently added. Row Per Page and Search filter the table. Columns are serial number, name with photo, department, designation, role, phone, email, date of join, status, and action. Click the serial number or name to open Staff Details. The action menu has View Staff, Edit, Mark as active or inactive, and Delete when allowed.';

const adminStaffPersonalSummary =
  'Add Staff opens a multi-section form. Back on the list is via breadcrumb or Cancel. Personal Information includes optional profile photo, employee code, required first and last name, department and designation, role, contact fields, joining date, qualification, experience, addresses, emergency contact, and active status. Payroll, bank account, salary components, and social media sections follow on the same page before documents and password.';

const adminStaffSaveSummary =
  'Documents accepts Resume and Joining Letter PDFs up to 4MB each. Password is optional; skip it to let the system set the initial login password from the phone number. Cancel leaves without saving. Add Staff creates the record when validation passes. Edit Staff uses the same sections with Save changes.';

const portalStaffChapter = {
  number: '22',
  title: 'Staffs',
  intro:
    'Staffs is an HRM page that lists non-teaching staff for the school.',
  instruction:
    'The default parent and student roles do not include HRM, so Staffs is not on your menu. Contact the school office for staff-related questions.',
  sections: [],
};

const teacherStaffChapter = {
  number: '22',
  title: 'Staffs',
  intro:
    'Staffs under HRM is the non-teaching staff directory. Teachers are listed under Peoples, Teachers instead.',
  instruction:
    'The default teacher role does not include the HRM staffs submodule. You do not see Staffs unless the school grants HRM view on Staffs. Use your teacher dashboard and profile for your own employment details.',
  sections: [],
};

const adminAttendanceIntro =
  'Attendance under HRM covers daily student marking, staff marking, and the student attendance analytics report. All three use the academic year in the header for dates and rosters.';

const adminAttendanceInstruction =
  'Open HRM, Attendance. Student Attendance and Staff Attendance need HRM view on the attendance submodule. Saving marks needs Mark Attendance on that submodule. Student Attendance Report needs reports view on attendance report; export uses reports export. Headmaster and administrative roles keep access when subscription read-only mode blocks edits. Do not save demo marks unless you intend to change live data.';

const adminStudentAttendanceSummary =
  'Student Attendance is at /hrm/student-attendance. Summary cards show totals for the loaded class and section. Pick Date, Class, and Section to load students for the header year. Save Attendance writes status and remarks. Search Students filters the table only. Each row has a status list and remarks. Holidays show an info banner and disable save. Users with view only see a monthly report on the same route instead of marking.';

const adminStaffAttendanceSummary =
  'Staff Attendance is at /hrm/staff-attendance. Use Date, Department, and Designation to load non-teaching staff from the HRM staff directory. Save Attendance stores status, optional check-in and check-out times when the status requires them, and remarks. Search Staff filters the visible rows. Export actions use the current filters.';

const adminStudentAttendanceReportSummary =
  'Student Attendance Report is at /hrm/student-attendance-report. Choose Monthly for a day-by-day matrix or Daily for one date. Select Month or Select Date, Academic Class, and Section, then optionally Filter Students. The legend explains cell codes. Export downloads the current report view. Global Overview on class is for school-wide summaries when no class is selected.';

const teacherAttendanceIntro =
  'Attendance under HRM includes Student Attendance, Staff Attendance, and Student Attendance Report. Teachers with attendance access mark students for assigned classes and sections.';

const teacherAttendanceInstruction =
  'The default teacher role includes HRM view and Mark Attendance on the attendance submodule, plus reports view and export on attendance report. Class and section lists follow your section-teacher assignments on Student Attendance and on the report. You only save marks you intend to keep. Staff Attendance appears when the school grants the same attendance submodule.';

const attendanceChapter = (
  intro,
  instruction,
  studentSummary,
  staffSummary,
  reportSummary
) => ({
  number: '23',
  title: 'Attendance',
  intro,
  instruction,
  sections: [
    {
      number: '23.1',
      title: 'Student Attendance',
      image: '/user-guides/admin/attendance-annotated-1.png?v=designer',
      imageAlt:
        'Student Attendance callouts 1 to 9: title, export menu, summary cards, date, class, section, Save Attendance, search, and roster table',
      summary: studentSummary,
    },
    {
      number: '23.2',
      title: 'Staff Attendance',
      image: '/user-guides/admin/attendance-annotated-2.png?v=designer',
      imageAlt:
        'Staff Attendance callouts 10 to 15: title, export menu, filters, Save Attendance, search, and staff table',
      summary: staffSummary,
    },
    {
      number: '23.3',
      title: 'Student Attendance Report',
      image: '/user-guides/admin/attendance-annotated-3.png?v=designer',
      imageAlt:
        'Student Attendance Report callouts 16 to 20: title, export menu, filters, legend, and attendance grid',
      summary: reportSummary,
    },
  ],
});

const adminLeavesIntro =
  'Leaves under HRM covers leave type setup, the school-wide leave list, pending approvals, and leave usage in Reports. Staff apply from My Leave & Attendance; students and parents apply from Leave & Attendance in their portal.';

const adminLeavesInstruction =
  'Open HRM, Leaves. Leave Types, List of leaves, and Approve Request use the HRM leaves submodule. Configure types before applications are submitted. Approve or reject pending rows on Approve Request; approved leave feeds attendance and leave reports. Headmaster and administrative roles keep access when subscription read-only mode hides edits.';

const adminLeaveTypesSummary =
  'Leave Types is at HRM, Leave Types. Breadcrumb Dashboard, HRM, Leave Types. Refresh, print, and export PDF or Excel download the Manage Leave Types table when export is allowed. Add Leave Type opens the modal when create is allowed. Show Only Active and Show Only Inactive switch the list between all types, active only, or inactive only. The table lists SR, Leave Type, Code, Max Days, Gender, Paid, Medical Certificate, Status, Updated, and Action. Edit and Delete show when permitted.';

const adminListLeavesSummary =
  'List of Leaves is at HRM, List of leaves. Breadcrumb Dashboard, HRM, List of Leaves. Refresh reloads applications. Print, export PDF, and export Excel use the current filters when export is allowed. The Leave List card offers a date range preset, Filter for leave type, status, applicant type, department, designation, class, and section, and Sort by A–Z. Row Per Page and Search filter the visible rows only. The table shows Submitted By, Leave Type, Role, Leave Date, No of Days, Applied On, and Status.';

const adminApproveLeaveSummary =
  'Leave Approval Requests is at HRM, Approve Request. Breadcrumb Dashboard, HRM, Leave Approval Requests. Refresh, print, export PDF, and export Excel use the open status tab and date range when export is allowed. The card title reflects the active status. A date range limits rows. Tabs switch Pending Requests, Approved Requests, Rejected Requests, and All History. Sort by A–Z orders the list. Row Per Page and Search narrow the table. Review opens Leave Request Details. Approve Leave or Reject Leave in the modal when you have approval permission.';

const adminLeaveReportSummary =
  'Leave Report is at Reports, Leave Report. Breadcrumb Dashboard, Report, Leave Report. Refresh reloads data. Print, export PDF, and export Excel export the staff leave report for the current filters. The Staff Report card scopes counted leave days with a date range. Filter sets Role, Status, and Leave Type, then Apply. Reset clears filters and the date range. Row Per Page and Search filter staff names. The grid shows Name, Role, Used and Available per leave type with max days in the headers, and Total Leave Days. Rejected leave does not reduce Available.';

const teacherLeavesIntro =
  'Leaves for teachers includes My Leave & Attendance for personal applications and Student Leaves to act on class leave requests when the school grants it.';

const teacherLeavesInstruction =
  'My Leave & Attendance is under MAIN on the teacher menu. Student Leaves appears under Peoples or HRM when your role includes student leave approval. Apply and edit your own pending leave from My Leave & Attendance; approve or reject student requests only for classes you are allowed to manage.';

const teacherMyLeaveSummary =
  'My Leave & Attendance opens from the teacher profile or MAIN menu at /teacher/teacher-leaves. Summary cards show used and available days per leave type. Apply Leave opens the form. The table lists type, dates, days, applied on, status, and edit on pending rows.';

const teacherStudentLeavesSummary =
  'Student Leaves uses four tabs: Pending Requests (act with Review → Approve or Reject), Approved Requests (read-only outcomes), Rejected Requests (declined with reason), and All History (every status together). Only pending rows accept approval actions; export and date range follow the active tab.';

const leavesChapter = (
  intro,
  instruction,
  typesSummary,
  listSummary,
  approveSummary,
  reportSummary
) => ({
  number: '24',
  title: 'Leaves',
  intro,
  instruction,
  sections: [
    {
      number: '24.1',
      title: 'Leave Types',
      image: '/user-guides/admin/leaves-annotated-1.png?v=img23',
      imageAlt:
        'Leave Types callouts 1 to 5: title, export menu, Add Leave Type, status toggles, and leave types table',
      summary: typesSummary,
    },
    {
      number: '24.2',
      title: 'List of Leaves',
      image: '/user-guides/admin/leaves-annotated-2.png?v=img23',
      imageAlt:
        'List of Leaves callouts 6 to 10: title, export menu, list toolbar, table controls, and applications table',
      summary: listSummary,
    },
    {
      number: '24.3',
      title: 'Approve Request',
      image: '/user-guides/admin/leaves-annotated-3.png?v=img23',
      imageAlt:
        'Approve Request callouts 11 to 15: title, export menu, status tabs and date range, table controls, and Review action',
      summary: approveSummary,
    },
    {
      number: '24.4',
      title: 'Leave Report',
      image: '/user-guides/admin/leaves-annotated-4.png?v=img23',
      imageAlt:
        'Leave Report callouts 16 to 20: title, export menu, Staff Report filters, table controls, and leave balance grid',
      summary: reportSummary,
    },
  ],
});

const adminHolidaysIntro =
  'Holidays under HRM records school closures and breaks for the academic year in the header. Marked holidays appear on student and staff attendance when the date falls on a working day.';

const adminHolidaysInstruction =
  'Open HRM, Holidays. Choose Month to load holidays that overlap that calendar month. Add or edit entries when create or edit is allowed on the holidays submodule. You need an academic year selected in the header before Create or Update saves. Edit and Delete on a row show only while the holiday end date has not passed.';

const adminHolidaysSummary =
  'Holidays is at HRM, Holidays. Breadcrumb Dashboard, Holidays. Refresh reloads the list for the selected month. Print opens the print dialog. Export PDF and Export Excel download the visible holidays when export is allowed. Month filters the table to holidays overlapping that month. Add Holiday holds Title, From Date, To Date, Type, and optional Description. Title uses letters, commas, dashes, and spaces. From Date cannot be before today on a new holiday. The span is at most thirty inclusive days. Type choices are Public, School, Custom, and Weekly. Create saves a new row; Update applies when Edit loaded the form. Cancel Edit clears edit mode. The table lists Title, Start, End, Type, and Description. Edit scrolls the form and loads the row. Delete asks for confirmation. An empty month shows No holidays found for selected month.';

const holidaysChapter = (intro, instruction, summary) => ({
  number: '25',
  title: 'Holidays',
  intro,
  instruction,
  sections: [
    {
      number: '25.1',
      title: 'Holidays',
      image: '/user-guides/admin/holidays-annotated-1.png?v=designer',
      imageAlt:
        'Holidays callouts 1 to 5: title, export menu, month filter, add holiday form, and holidays table',
      summary,
    },
  ],
});

const adminDepartmentsIntro =
  'Departments under HRM defines the department list used on staff records, designations, and filters across HRM. Each department can have an optional code and head of department.';

const adminDepartmentsInstruction =
  'Open HRM, Departments. Add Department opens the create modal when create is allowed. Trashed opens soft-deleted departments for restore or permanent cleanup. Edit and Delete on a row use the action menu when permitted. Deletion moves a department to trash and is blocked while staff or designations still reference it.';

const adminDepartmentListSummary =
  'Department is at HRM, Departments. Breadcrumb Dashboard, HRM, Department. Refresh reloads the list. Print, Export PDF, and Export Excel download the filtered Department List when export is allowed. Trashed opens trashed departments. Add Department opens the create modal when create is allowed. The Department List card offers a date range preset, Filter for department and status with Apply and Reset, and Sort by A–Z or recently added. Row Per Page and Search filter visible rows only. The table shows SR, Department, Code, Head of department, Status, and Action. Edit and Delete appear in the action menu when permitted.';

const adminDepartmentAddSummary =
  'Add Department collects Department Name, optional Department Code up to ten unique characters, optional Head of department from active staff, and Status on a toggle. Cancel closes without saving. Add Department stores the row and returns to the list. Edit Department uses the same fields in a modal when edit is allowed.';

const departmentsChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '26',
  title: 'Departments',
  intro,
  instruction,
  sections: [
    {
      number: '26.1',
      title: 'Department List',
      image: '/user-guides/admin/departments-annotated-1.png?v=img23',
      imageAlt:
        'Department List callouts 1 to 6: title, export menu, Trashed, Add Department, list toolbar, and departments table',
      summary: listSummary,
    },
    {
      number: '26.2',
      title: 'Add Department',
      image: '/user-guides/admin/departments-annotated-2.png?v=img23',
      imageAlt:
        'Add Department callouts 7 to 8: modal title and department form fields',
      summary: addSummary,
    },
  ],
});

const adminDesignationIntro =
  'Designation under HRM defines job titles linked to an optional department and optional salary range. Staff pick a designation on their profile; payroll and reports use the same labels.';

const adminDesignationInstruction =
  'Open HRM, Designation. Add Designation opens the create modal when create is allowed. Trashed lists soft-deleted designations. Filter by department and status on the list. Edit and Delete use the row action menu when permitted; delete is blocked while staff still use the designation.';

const adminDesignationListSummary =
  'Designation is at HRM, Designation. Breadcrumb Dashboard, HRM, Designation. Refresh reloads designations. Print, Export PDF, and Export Excel export the filtered list when allowed. Trashed opens trashed designations. Add Designation opens the create modal when create is allowed. The Designation card offers a date range preset, Filter for department and status with Apply and Reset, and Sort newest first or A–Z. Row Per Page and Search filter visible rows. The table shows SR, Designation, Department, Salary min, Salary max, Status, and Action. Edit and Delete appear in the action menu when permitted.';

const adminDesignationAddSummary =
  'Add Designation collects Designation Name, optional Department, optional Salary range min and max, and Status on a toggle. Cancel closes without saving. Add Designation saves the row. Edit Designation uses the same fields when edit is allowed. Salary min must be less than or equal to salary max when both are set.';

const designationChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '27',
  title: 'Designation',
  intro,
  instruction,
  sections: [
    {
      number: '27.1',
      title: 'Designation List',
      image: '/user-guides/admin/designation-annotated-1.png?v=designer',
      imageAlt:
        'Designation List callouts 1 to 6: title, export menu, Trashed, Add Designation, list toolbar, and designations table',
      summary: listSummary,
    },
    {
      number: '27.2',
      title: 'Add Designation',
      image: '/user-guides/admin/designation-annotated-2.png?v=designer',
      imageAlt:
        'Add Designation callouts 7 to 8: modal title and designation form fields',
      summary: addSummary,
    },
  ],
});

const adminPayrollIntro =
  'Payroll under HRM lists monthly payslips generated from staff salary components. Process payroll for a month, review slips, mark payment status, and export the list when export is allowed.';

const adminPayrollInstruction =
  'Open HRM, Payroll. Manage Components opens salary component settings. Generate Payroll creates draft payslips for the chosen month and year when create or process payroll is allowed. Select unpaid rows to bulk mark paid or delete when permitted. Paid rows cannot be selected. View Payslip opens the printable salary slip.';

const adminPayrollListSummary =
  'Payroll is at HRM, Payroll. Breadcrumb Dashboard, HRM, Payroll. Manage Components opens salary component configuration. Refresh reloads payslips. Print, Export PDF, and Export Excel use the active filters when export is allowed. Generate Payroll opens the month and year dialog when generation is allowed. The Payroll List card offers a date range preset and Filter for staff name, month, and year with Reset. Row Per Page and Search filter visible rows. The table shows SR, Name, Month, Department, Designation, Phone, Amount, Status, and Action. View Payslip opens the slip. Mark Paid or Mark Draft updates status when edit is allowed. Delete removes draft payslips when delete is allowed. Bulk actions appear when rows are selected.';

const adminPayrollSlipSummary =
  'Salary Slip opens from View Payslip on the payroll list. Breadcrumb Dashboard, Salary Slip. Back returns to the list. Print opens the slip in a print window. Download PDF saves a PDF copy. The slip shows school header, payslip number, pay period, status, employee details, earnings and deductions, gross and net pay, amount in words, and payment details when marked paid.';

const payrollChapter = (intro, instruction, listSummary, slipSummary) => ({
  number: '28',
  title: 'Payroll',
  intro,
  instruction,
  sections: [
    {
      number: '28.1',
      title: 'Payroll List',
      image: '/user-guides/admin/payroll-annotated-1.png?v=designer',
      imageAlt:
        'Payroll List callouts 1 to 6: title, Manage Components, export menu, Generate Payroll, list toolbar, and payroll table',
      summary: listSummary,
    },
    {
      number: '28.2',
      title: 'Salary Slip',
      image: '/user-guides/admin/payroll-annotated-2.png?v=designer',
      imageAlt:
        'Salary Slip callouts 7 to 9: title, Back Print and Download PDF, and payslip body',
      summary: slipSummary,
    },
  ],
});

const adminFeeGroupsIntro =
  'Fee Groups under Fees Collection defines fee configurations per class for the academic year in the header. Each configuration groups fee items, totals, late fee rules, installments, and due dates before assignments and collection.';

const adminFeeGroupsInstruction =
  'Open Fees Collection, Fees Group. Pick the academic year in the header first. Add Fee Configuration opens the fee group form when create is allowed. Edit, Manage Installments, and Delete use the row action menu when permitted. Export uses the filtered list.';

const adminFeeGroupsListSummary =
  'Fee Configurations is at Fees Collection, Fee Groups. Breadcrumb Dashboard, Fees Collection, Fee Groups. Refresh reloads configurations for the header year. Print, Export PDF, and Export Excel download the filtered table when export is allowed. Add Fee Configuration opens the create form when create is allowed. The card offers a date range preset and Filter for class and fee item with Apply and Reset. Row Per Page and Search filter visible rows. The table shows Group, Class, Fee Items, Total, Late Fee, Installments, Due Date, and Action. Manage Installments, Edit, and Delete appear in the action menu when permitted. An empty year shows No fee configurations found for this academic year and Create First Configuration when create is allowed.';

const adminFeeGroupsAddSummary =
  'Add Fee Configuration collects Basic Information and fee lines for the academic year in the header. Group Name is required and must be unique per class and year. Class is required on a new configuration. Fee items, amounts, optional versus compulsory flags, installments, due date, and late fee rules are set in the modal. Save stores the configuration; Cancel closes without saving. Edit uses the same form from the row action menu.';

const feeGroupsChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '29',
  title: 'Fee Groups',
  intro,
  instruction,
  sections: [
    {
      number: '29.1',
      title: 'Fee Configurations',
      image: '/user-guides/admin/fee-groups-annotated-1.png?v=2',
      imageAlt:
        'Fee Configurations callouts 1 to 5: title, export menu, Add Fee Configuration, list toolbar, and configurations table or empty state',
      summary: listSummary,
    },
    {
      number: '29.2',
      title: 'Add Fee Configuration',
      image: '/user-guides/admin/fee-groups-annotated-2.png?v=designer',
      imageAlt: 'Add Fee Configuration callouts 6 to 7: modal title and fee configuration form',
      summary: addSummary,
    },
  ],
});

const adminCollectFeesIntro =
  'Collect Fees under Fees Collection is the school-wide fees dashboard for the academic year in the header. Summary cards show demanded, collected, and outstanding totals for the filtered student list, plus aging buckets for balances still due.';

const adminCollectFeesInstruction =
  'Open Fees Collection, Collect Fees. Use aging chips and Filter to narrow students. Collect Fee on a row with balance opens the collection modal when collect fee is allowed. View Details opens that student\u2019s fee page. Export uses the filtered list.';

const adminCollectFeesListSummary =
  'Fees Collection is at Fees Collection, Collect Fees. Breadcrumb Dashboard, Fees Collection, Collect Fees. Refresh reloads student fee rows for the header year. Print, Export PDF, and Export Excel export the filtered Fees List when allowed. Summary cards show Total Demanded, Collected with recovery percent, Outstanding with defaulter count, and Aging badges for current and overdue buckets. The Fees List card offers aging chips, Filter for admission number, roll number, class, section, and status, and Search. The table shows Adm No, Roll No, Student, Class, Section, Amount, Due Date, Aging, Balance, Status, and Action. Collect Fees opens payment entry when balance is due and collect is allowed. View Details opens the student fees page.';

const adminCollectFeesModalSummary =
  'Collect Fees opens from Collect Fees on a row with balance. The modal lists outstanding fee lines for the student and academic year used for collection. Enter amounts per line, payment mode, and optional wallet use when configured. Submit records the receipt and refreshes balances on the list. Cancel closes without saving.';

const collectFeesChapter = (intro, instruction, listSummary, modalSummary) => ({
  number: '30',
  title: 'Collect Fees',
  intro,
  instruction,
  sections: [
    {
      number: '30.1',
      title: 'Collect Fees',
      image: '/user-guides/admin/collect-fees-annotated-1.png?v=2',
      imageAlt:
        'Collect Fees callouts 1 to 5: title, export menu, summary cards, fees list toolbar, and student fees table',
      summary: listSummary,
    },
    {
      number: '30.2',
      title: 'Collect Fees modal',
      image: '/user-guides/admin/collect-fees-annotated-2.png?v=designer',
      imageAlt: 'Collect Fees modal callouts 6 to 7: modal title and payment form',
      summary: modalSummary,
    },
  ],
});

const adminDiscountRulesIntro =
  'Discount Rules under Fees Collection defines percentage, fixed, or full-waiver rules that reduce student charges. Rules can apply to all charges, a fee head, a category, or a student-specific target for the academic year in the header.';

const adminDiscountRulesInstruction =
  'Open Fees Collection, Discount Rules. Create Rule opens the rule form when create is allowed. Assign Discount applies an active rule to a student or to classes and sections when assign fee is allowed. Edit and Delete use the row action menu when permitted.';

const adminDiscountRulesListSummary =
  'Discount and Scholarship Rules is at Fees Collection, Discount Rules. Breadcrumb Dashboard, Fees Collection, Discount Rules. Refresh reloads rules. Print, Export PDF, and Export Excel export the rules table when allowed. Assign Discount opens assign to student or class and section when assign fee is allowed. Create Rule opens the create or edit modal when create is allowed. The Rules table shows Name, Type, Value, Applies To, Valid period, Status, and Action. Edit and Delete appear when permitted.';

const adminDiscountRulesCreateSummary =
  'Create Discount Rule sets Rule name, Type as Percentage, Fixed amount, or Full waiver, Value when applicable, Applies To for all charges, fee head, category, or student target, Valid from and Valid to, and Status. Save creates the rule; Cancel closes. Edit loads the same fields from the row action menu.';

const adminDiscountRulesAssignSummary =
  'Assign Discount applies an active rule for the header academic year. Assign to student selects one student and a rule. Assign to class and section selects classes, optional sections, and a rule, then Assign and Apply. Cancel closes without assigning.';

const discountRulesChapter = (intro, instruction, listSummary, createSummary, assignSummary) => ({
  number: '31',
  title: 'Discount Rules',
  intro,
  instruction,
  sections: [
    {
      number: '31.1',
      title: 'Discount Rules',
      image: '/user-guides/admin/discount-rules-annotated-1.png?v=2',
      imageAlt:
        'Discount Rules callouts 1 to 5: title, export menu, Assign Discount, Create Rule, and rules table',
      summary: listSummary,
    },
    {
      number: '31.2',
      title: 'Create Discount Rule',
      image: '/user-guides/admin/discount-rules-annotated-2.png?v=designer',
      imageAlt: 'Create Discount Rule callouts 6 to 7: modal title and rule form',
      summary: createSummary,
    },
    {
      number: '31.3',
      title: 'Assign Discount',
      image: '/user-guides/admin/discount-rules-annotated-3.png?v=designer',
      imageAlt: 'Assign Discount callouts 8 to 9: modal title and assign form',
      summary: assignSummary,
    },
  ],
});

const adminFeesAssignIntro =
  'Assign Fees under Fees Collection links fee configurations to students for the academic year in the header. Assign New creates assignments; Re-sync refreshes charges from the current fee group when assign fee is allowed.';

const adminFeesAssignInstruction =
  'Open Fees Collection, Assign / Sync. Pick the academic year in the header first. Assign New opens the assignment wizard when assign fee is allowed. Filter by class, section, gender, and category. Re-sync and Delete use the row action menu when permitted.';

const adminFeesAssignListSummary =
  'Fees Collection is at Fees Collection, Assign Fees. Breadcrumb Dashboard, Fees Collection, Assign Fees. Refresh reloads assignments for the header year. Print, Export PDF, and Export Excel export the filtered Fees Assignment List when allowed. Assign New opens the assign fees flow when assign fee is allowed. Filter narrows by class, section, gender, and category. Row Per Page and Search filter visible rows. The table shows student, roll number, fee types, class, section, amount, gender, category, and Action. Re-sync updates the assignment from the fee configuration. Delete removes the assignment when delete is allowed.';

const adminFeesAssignModalSummary =
  'Assign New Fees selects students and a fee configuration for the header academic year. Choose class, section, and fee group, then confirm assignment. The modal creates or updates student charge rows. Cancel closes without assigning.';

const feesAssignChapter = (intro, instruction, listSummary, modalSummary) => ({
  number: '32',
  title: 'Assign / Sync',
  intro,
  instruction,
  sections: [
    {
      number: '32.1',
      title: 'Assign Fees',
      image: '/user-guides/admin/fees-assign-annotated-1.png?v=2',
      imageAlt:
        'Assign Fees callouts 1 to 5: title, export menu, Assign New, list filter toolbar, and assignment table',
      summary: listSummary,
    },
    {
      number: '32.2',
      title: 'Assign New Fees',
      image: '/user-guides/admin/fees-assign-annotated-2.png?v=designer',
      imageAlt: 'Assign New Fees callouts 6 to 7: modal title and assignment form',
      summary: modalSummary,
    },
  ],
});

const adminFeeWalletIntro =
  'Wallet under Fees Collection holds prepaid balances per student for the academic year in the header. Credits add manual wallet money; collections can apply wallet balance at payment time.';

const adminFeeWalletInstruction =
  'Open Fees Collection, Wallet. Credit Wallet selects a student, amount, and optional remarks when create is allowed on wallet. View on a row opens wallet transactions. Export uses the wallet list when allowed.';

const adminFeeWalletSummary =
  'Wallet Management is at Fees Collection, Wallet. Breadcrumb Dashboard, Fees, Wallet. Refresh reloads wallets for the header year. Print, Export PDF, and Export Excel export the list when allowed. Credit Wallet selects Student, Amount, and Remarks, then Credit when create is allowed. The table shows Student, Adm No, Class, Balance, and View for transaction history. The transactions panel lists type, amount, balance after, reference, remarks, and date.';

const feeWalletChapter = (intro, instruction, summary) => ({
  number: '33',
  title: 'Wallet',
  intro,
  instruction,
  sections: [
    {
      number: '33.1',
      title: 'Wallet',
      image: '/user-guides/admin/fee-wallet-annotated-1.png?v=designer',
      imageAlt:
        'Wallet callouts 1 to 4: title, export menu, Credit Wallet form, and wallet balances table',
      summary,
    },
  ],
});

const adminFeeAdjustmentsIntro =
  'Adjustments under Fees Collection records discounts, waivers, and fines applied to student charges. The list is scoped to the academic year in the header; Apply Waiver posts a manual waiver on an open charge when approve fee adjustment is allowed.';

const adminFeeAdjustmentsInstruction =
  'Open Fees Collection, Adjustments. Use the type dropdown to show All, Discount, Waiver, or Fine rows. Apply Waiver picks a student, open charge, amount, and reason when permitted. Export uses the filtered list when allowed.';

const adminFeeAdjustmentsSummary =
  'Fee Adjustments is at Fees Collection, Adjustments. Breadcrumb Dashboard, Fees, Adjustments. The type filter switches All, Discount, Waiver, or Fine. Refresh reloads rows. Print, Export PDF, and Export Excel export the list when allowed. Apply Waiver selects Student, open charge, Amount, and Reason, then Apply Waiver when approve fee adjustment is allowed. The table shows Date, Type, Student, Class, Section, Roll No, Fee head, Amount, Reason, and Rule name.';

const feeAdjustmentsChapter = (intro, instruction, summary) => ({
  number: '34',
  title: 'Adjustments',
  intro,
  instruction,
  sections: [
    {
      number: '34.1',
      title: 'Adjustments',
      image: '/user-guides/admin/fee-adjustments-annotated-1.png?v=designer',
      imageAlt:
        'Adjustments callouts 1 to 4: title, type filter and export, Apply Waiver form, and adjustments table',
      summary,
    },
  ],
});

const adminReceiptHistoryIntro =
  'Receipt History under Fees Collection lists fee receipts for the academic year in the header. Confirmed receipts can be printed; void reverses allocations and restores wallet amounts used when delete on receipt history is allowed.';

const adminReceiptHistoryInstruction =
  'Open Fees Collection, Receipt History. Set the date range and status filter to load receipts. Print opens the receipt PDF for a row. Void asks for an optional reason and reverses a confirmed receipt when permitted.';

const adminReceiptHistorySummary =
  'Receipt History is at Fees Collection, Receipts. Breadcrumb Dashboard, Fees, Receipts. Refresh reloads receipts. Print, Export PDF, and Export Excel export the filtered list when allowed. The filter card offers a date range and status Confirmed only, Voided only, or All statuses. The table shows Receipt No, Date, Student, Adm No, Class, Mode, Amount, Wallet Used, Status, and Action. Print generates the receipt document. Void confirms reversal on confirmed rows when delete is allowed.';

const receiptHistoryChapter = (intro, instruction, summary) => ({
  number: '35',
  title: 'Receipt History',
  intro,
  instruction,
  sections: [
    {
      number: '35.1',
      title: 'Receipt History',
      image: '/user-guides/admin/fee-receipts-annotated-1.png?v=designer',
      imageAlt:
        'Receipt History callouts 1 to 4: title, export menu, date and status filters, and receipts table',
      summary,
    },
  ],
});

const adminLibraryMembersIntro =
  'Library Members under Management registers students and staff who may borrow books. Each member needs a library card number and active status before Issue Books can lend copies.';

const adminLibraryMembersInstruction =
  'Open Management, Library, Library Members. Add Member opens the form when create is allowed. Filter by member type, person, and status. Edit and Delete use the row action menu when permitted. Export uses the filtered list.';

const adminLibraryMembersListSummary =
  'Library Members is at Management, Library Members. Breadcrumb Dashboard, Management, Library Members. Refresh reloads members. Export PDF and Export Excel, and Print use the list when export is allowed. Add Member opens the modal when create is allowed. The Library Members List card offers Filter for member type, member, and status with Apply and Reset, and Sort. Row Per Page and Search filter visible rows. The table shows SR, Member, Card No, Type, Email, Mobile, Status, and Action. Edit and Delete appear when permitted.';

const adminLibraryMembersAddSummary =
  'Add Member registers a student or staff borrower. Member Type selects Student or Staff, then pick the person and set Library Card Number and Status. Save creates the member; Cancel closes. Edit reopens the same fields from the row action menu.';

const libraryMembersChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '36',
  title: 'Library Members',
  intro,
  instruction,
  sections: [
    {
      number: '36.1',
      title: 'Library Members',
      image: '/user-guides/admin/library-members-annotated-1.png?v=2',
      imageAlt:
        'Library Members callouts 1 to 5: title, export menu, Add Member, list toolbar, and members table',
      summary: listSummary,
    },
    {
      number: '36.2',
      title: 'Add Member',
      image: '/user-guides/admin/library-members-annotated-2.png?v=designer',
      imageAlt: 'Add Member callouts 6 to 7: modal title and member form',
      summary: addSummary,
    },
  ],
});

const adminLibraryCategoriesIntro =
  'Library Categories groups books by subject or genre. Categories must exist before cataloguing books and appear in book filters.';

const adminLibraryCategoriesInstruction =
  'Open Management, Library, Categories. Add Category opens the form when create is allowed. Trashed opens soft-deleted categories. Edit and Delete use the row action menu when permitted.';

const adminLibraryCategoriesListSummary =
  'Library Categories is at Management, Categories. Breadcrumb Dashboard, Management, Categories. Refresh reloads categories. Export, Print, PDF, and Excel when allowed. Trashed opens trashed categories when delete is allowed. Add Category opens the modal when create is allowed. The Category List table shows SR, Category Name, Description, Status, and Action. Filter and Sort narrow the list. Edit and Delete appear when permitted.';

const adminLibraryCategoriesAddSummary =
  'Add Category sets Category Name, optional Description, and Status. Save adds the category to the catalogue filters; Cancel closes. Edit uses the same modal from the row menu.';

const libraryCategoriesChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '37',
  title: 'Categories',
  intro,
  instruction,
  sections: [
    {
      number: '37.1',
      title: 'Library Categories',
      image: '/user-guides/admin/library-categories-annotated-1.png?v=2',
      imageAlt:
        'Library Categories callouts 1 to 5: title, export menu, Trashed, Add Category, and category list',
      summary: listSummary,
    },
    {
      number: '37.2',
      title: 'Add Category',
      image: '/user-guides/admin/library-categories-annotated-2.png?v=designer',
      imageAlt: 'Add Category callouts 6 to 7: modal title and category form',
      summary: addSummary,
    },
  ],
});

const adminLibraryPoliciesIntro =
  'Library Policies sets borrowing rules by audience: issue duration, max books, renewals, fines, grace days, and fine caps. Members inherit the policy that matches their audience when books are issued.';

const adminLibraryPoliciesInstruction =
  'Open Management, Library, Policies. Add Policy opens the form when create is allowed. Trashed lists removed policies. Edit and Delete use the row menu when permitted.';

const adminLibraryPoliciesListSummary =
  'Library Policies is at Management, Policies. Breadcrumb Dashboard, Management, Policies. Refresh reloads policies. Export, Print, PDF, and Excel when allowed. Trashed opens trashed policies when delete is allowed. Add Policy opens the modal when create is allowed. The Policy List shows SR, Policy Name, Audience, Issue Duration Days, Max Books Allowed, Max Renewals, Per Day Fine, Grace Period Days, Max Fine Limit, Status, and Action. Edit and Delete when permitted.';

const adminLibraryPoliciesAddSummary =
  'Add Policy defines borrowing rules: Policy Name, Audience for students or staff, issue duration, max books, renewals, per-day fine, grace days, max fine cap, and Status. Save creates the policy; Cancel closes. Edit loads the same fields when permitted.';

const libraryPoliciesChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '38',
  title: 'Policies',
  intro,
  instruction,
  sections: [
    {
      number: '38.1',
      title: 'Library Policies',
      image: '/user-guides/admin/library-policies-annotated-1.png?v=2',
      imageAlt:
        'Library Policies callouts 1 to 5: title, export menu, Trashed, Add Policy, and policy list',
      summary: listSummary,
    },
    {
      number: '38.2',
      title: 'Add Policy',
      image: '/user-guides/admin/library-policies-annotated-2.png?v=designer',
      imageAlt: 'Add Policy callouts 6 to 7: modal title and policy form',
      summary: addSummary,
    },
  ],
});

const adminLibraryBooksIntro =
  'Books is the library catalogue for the school. Each title can have multiple copies with barcodes; Book Copies manages individual copy records. Import books bulk-loads titles when create is allowed.';

const adminLibraryBooksInstruction =
  'Open Management, Library, Books. Add Book catalogues a new title. Book Copies opens copy management. Trashed holds deleted titles. Filter by category and status. Edit, Create Copies, and Delete use the row menu when permitted.';

const adminLibraryBooksListSummary =
  'Books is at Management, Books. Breadcrumb Dashboard, Management, Books. Refresh reloads the catalogue. Import books bulk-imports when create is allowed. Export PDF and Excel, and Print when export is allowed. Book Copies opens copy records. Trashed opens deleted books. Add Book opens the form when create is allowed. The Books card offers Filter for category and status with Apply and Reset, and Sort. The table shows Book Name, ISBN, Publisher, Author, Category, Edition, Copies, Available, Post Date, and Action. Edit, copies, and Delete when permitted.';

const adminLibraryBooksAddSummary =
  'Add Book catalogues a title with Book Name, ISBN, Publisher, Author, Category, Edition, optional cover, and initial copy count when offered. Save adds the title; Cancel closes. Create Copies and Edit use related flows from the list row when permitted.';

const libraryBooksChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '39',
  title: 'Books',
  intro,
  instruction,
  sections: [
    {
      number: '39.1',
      title: 'Books',
      image: '/user-guides/admin/library-books-annotated-1.png?v=books1',
      imageAlt:
        'Books callouts 1 to 5: Books, Import books, Trashed, Book Copies, and Action',
      summary: listSummary,
    },
    {
      number: '39.2',
      title: 'Add Book',
      image: '/user-guides/admin/library-books-annotated-2.png?v=books1',
      imageAlt: 'Add Book callout 6: book name, ISBN, edition, language, category, publisher, author, and publication year',
      summary: addSummary,
    },
    {
      number: '39.3',
      title: 'Add Book Copy',
      image: '/user-guides/admin/library-books-annotated-3.png?v=books1',
      imageAlt:
        'Add Book Copy callout 10: book, accession number, number of copies, location, price, condition, Cancel, and Save',
      summary:
        'Add Book Copy adds physical copies of the book you select. Book is required. Accession number is required after you enter how many copies to make, and the form suggests the next serial. Number of copies is required and must be from 1 to 100. Book location, price, and condition show only when the number of copies is 1. Condition is New, Good, Damaged, Lost, or Maintenance. Price can stay blank. When the number of copies is more than 1, each copy gets its own accession number, condition, location, and price. Cancel closes the form and does not save. Save creates the copies.',
    },
    {
      number: '39.4',
      title: 'Book Copies',
      image: '/user-guides/admin/library-books-annotated-4.png?v=books1',
      imageAlt: 'Book Copies callouts 7 to 9: the copy list, Edit and Delete, and Add Copy',
      summary:
        'Book Copies lists each physical copy, with its book, accession number, location, condition, price, and whether it is available. The three dots open Edit and Delete. Edit opens that copy. Delete asks you to confirm, then deletes the copy. People who cannot edit or delete do not see this menu. Add Copy opens the Add Book Copy form.',
    },
  ],
});

const adminIssueBooksIntro =
  'Issue Books records loans from the catalogue to library members. Issue Book selects book, copy, member, and due date; open issues appear until Return marks them back or lost or damaged.';

const adminIssueBooksInstruction =
  'Open Management, Library, Issue Book. Issue Book opens the issue modal when issue permission is allowed. Filter by status, member, and book. Return from the row action when return is allowed. Export uses the filtered issue list.';

const adminIssueBooksListSummary =
  'Issue Books is at Management, Issue Books. Breadcrumb Dashboard, Management, Issue Books. Refresh reloads issues. Export, Print, PDF, and Excel when allowed. Issue Book opens the loan form when issue is allowed. The list card offers Filter for status, library member, and book with Apply and Reset. The table shows SR, Date of Issue, Due Date, Issue To, Book, Returned status, Fine, and Action. Return and View Details when permitted. Open reservations links to reservations when shown.';

const adminIssueBooksModalSummary =
  'Issue Book selects Library Member, Book, available Copy, and Due Date according to the member policy. Confirm issues the loan and reduces available copies. Cancel closes without issuing. Return from the list completes an open loan when return is allowed.';

const issueBooksChapter = (intro, instruction, listSummary, modalSummary) => ({
  number: '40',
  title: 'Issue Book',
  intro,
  instruction,
  sections: [
    {
      number: '40.1',
      title: 'Issue Books',
      image: '/user-guides/admin/library-issue-annotated-1.png?v=2',
      imageAlt:
        'Issue Books callouts 1 to 5: title, export menu, Issue Book, list toolbar, and issues table',
      summary: listSummary,
    },
    {
      number: '40.2',
      title: 'Issue Book modal',
      image: '/user-guides/admin/library-issue-annotated-2.png?v=designer',
      imageAlt: 'Issue Book modal callouts 6 to 7: modal title and issue form',
      summary: modalSummary,
    },
  ],
});

const adminReturnBooksIntro =
  'Return Books closes open loans. Only books still issued appear here. Return opens the same return form used on Issue Books when you record returned, lost, or damaged copies and any fine.';

const adminReturnBooksInstruction =
  'Open Management, Library, Return Books. Filter by book, member, and issue date range. Return on a row opens the return modal when return is allowed. Export uses the filtered open-issue list.';

const adminReturnBooksListSummary =
  'Return Books is at Management, Return Books. Breadcrumb Dashboard, Management, Return Books. Refresh reloads open issues. Print, Export PDF, and Export Excel when export is allowed. The Open issues card offers Filter for book, member, issue from, and issue to with Apply and Reset. The table lists current loans with Return when permitted. An empty list shows No books currently issued.';

const returnBooksChapter = (intro, instruction, listSummary) => ({
  number: '41',
  title: 'Return Books',
  intro,
  instruction,
  sections: [
    {
      number: '41.1',
      title: 'Return Books',
      image: '/user-guides/admin/library-return-annotated-1.png?v=designer',
      imageAlt:
        'Return Books callouts 1 to 4: title, export menu, list toolbar, and open issues table or empty state',
      summary: listSummary,
    },
  ],
});

const adminLibraryReservationsIntro =
  'Book reservations holds a place on the wait list when every copy is on loan. Issue Books can prefer wait-listed members when a copy becomes available.';

const adminLibraryReservationsInstruction =
  'Open Management, Library, Book reservations. Add reservation selects book, member, and hold expiry. Filter by status, book, and member. Cancel or fulfil rows from the action menu when permitted.';

const adminLibraryReservationsListSummary =
  'Book reservations is at Management, Book reservations. Breadcrumb Dashboard, Management, Book reservations. Refresh reloads reservations for the header academic year. Export when allowed. Add reservation opens the form when create is allowed. The wait list card offers Filter for status, book, and member with Apply and Reset. The table shows reservation status, book, member, and actions when permitted.';

const adminLibraryReservationsAddSummary =
  'Add reservation picks Book, Library member, and Expiration date for the hold. Save adds the reservation; Cancel closes. Status moves to fulfilled or expired when the loan cycle completes or staff cancel the hold.';

const libraryReservationsChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '42',
  title: 'Reservations',
  intro,
  instruction,
  sections: [
    {
      number: '42.1',
      title: 'Book reservations',
      image: '/user-guides/admin/library-reservations-annotated-1.png?v=designer',
      imageAlt:
        'Book reservations callouts 1 to 5: title, export menu, Add reservation, list toolbar, and reservations table',
      summary: listSummary,
    },
    {
      number: '42.2',
      title: 'Add reservation',
      image: '/user-guides/admin/library-reservations-annotated-2.png?v=designer',
      imageAlt: 'Add reservation callouts 6 to 7: modal title and reservation form',
      summary: addSummary,
    },
  ],
});

const adminHostelListIntro =
  'Hostel List registers school hostels with category, gender type, warden contact, and status. Floors, rooms, beds, and assignments build on hostels defined here.';

const adminHostelListInstruction =
  'Open Management, Hostel, Hostel List. Add Hostel opens the form when create is allowed. Trashed restores deleted hostels. Filter by category, gender, and search. Edit and Delete use the row menu when permitted.';

const adminHostelListListSummary =
  'Hostel is at Management, Hostel List. Breadcrumb Dashboard, Management, Hostel. Refresh reloads hostels. Print, Export PDF, and Export Excel when export is allowed. Trashed Hostels opens soft-deleted hostels. Add Hostel opens the modal when create is allowed. The list card offers Filter for category and gender with Apply and Reset, and Search. The table shows code, name, category, gender, warden, contact, status, and Action.';

const adminHostelListAddSummary =
  'Add Hostel captures hostel name, code, category for student or staff, gender type, warden and contact details, address, amenities, description, and status. Save creates the hostel; Cancel closes. Edit reopens the same fields from the row menu.';

const hostelListChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '43',
  title: 'Hostel List',
  intro,
  instruction,
  sections: [
    {
      number: '43.1',
      title: 'Hostel List',
      image: '/user-guides/admin/hostel-list-annotated-1.png?v=designer',
      imageAlt:
        'Hostel List callouts 1 to 6: title, export menu, Trashed Hostels, Add Hostel, list toolbar, and hostels table',
      summary: listSummary,
    },
    {
      number: '43.2',
      title: 'Add Hostel',
      image: '/user-guides/admin/hostel-list-annotated-2.png?v=designer',
      imageAlt: 'Add Hostel callouts 7 to 8: modal title and hostel form',
      summary: addSummary,
    },
  ],
});

const adminHostelAssignmentsIntro =
  'Hostel assignments places students or staff on a bed for the academic year in the header. Active assignments block the bed until checkout or cancel.';

const adminHostelAssignmentsInstruction =
  'Open Management, Hostel, Hostel Assignments. New assignment opens the assign form when create is allowed. Filter by hostel and assignment status. Checkout and Cancel use the row menu when permitted.';

const adminHostelAssignmentsListSummary =
  'Hostel assignments is at Management, Hostel Assignments. Breadcrumb Dashboard, Management, Hostel assignments. Refresh reloads assignments for the header academic year. Export when allowed. New assignment opens the wizard when create is allowed. Filter for hostel and status with Apply and Reset. The table shows occupant, hostel room and bed, dates, fees, status, and Action.';

const adminHostelAssignmentsAddSummary =
  'New assignment selects user type student or staff, the person, hostel, floor, room, and available bed, assigned and expected checkout dates, and optional fees or notes. Save creates the assignment; Cancel closes. Edit and checkout update active stays when permitted.';

const hostelAssignmentsChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '44',
  title: 'Hostel Assignments',
  intro,
  instruction,
  sections: [
    {
      number: '44.1',
      title: 'Hostel Assignments',
      image: '/user-guides/admin/hostel-assignments-annotated-1.png?v=designer',
      imageAlt:
        'Hostel Assignments callouts 1 to 5: title, export menu, New assignment, list toolbar, and assignments table',
      summary: listSummary,
    },
    {
      number: '44.2',
      title: 'New assignment',
      image: '/user-guides/admin/hostel-assignments-annotated-2.png?v=designer',
      imageAlt: 'New assignment callouts 6 to 7: modal title and assignment form',
      summary: addSummary,
    },
  ],
});

const adminTransportRoutesIntro =
  'Routes defines transport paths with distance, pickup stops, and status. Vehicles and student allocations reference routes configured here.';

const adminTransportRoutesInstruction =
  'Open Management, Transport, Routes. Add Route Stop opens the route form when create is allowed. Trashed restores deleted routes. Filter by pickup point and status. Edit and Delete use the row menu when permitted.';

const adminTransportRoutesListSummary =
  'Routes is at Management, Routes. Breadcrumb Dashboard, Management, Routes. Refresh reloads routes. Print, Export PDF, and Export Excel when export is allowed. Trashed Routes opens deleted routes. Add Route Stop opens the form when create is allowed. The Routes List card offers Filter for pickup point and status. The table shows route name, distance, stops summary, status, and Action.';

const adminTransportRoutesAddSummary =
  'Add Route Stop sets route name, distance, ordered pickup stops with sequence, and status. Save creates the route; Cancel closes. Edit loads the same fields when permitted.';

const transportRoutesChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '45',
  title: 'Transport Routes',
  intro,
  instruction,
  sections: [
    {
      number: '45.1',
      title: 'Routes',
      image: '/user-guides/admin/transport-routes-annotated-1.png?v=designer',
      imageAlt:
        'Routes callouts 1 to 6: title, export menu, Trashed Routes, Add Route Stop, list toolbar, and routes table',
      summary: listSummary,
    },
    {
      number: '45.2',
      title: 'Add Route Stop',
      image: '/user-guides/admin/transport-routes-annotated-2.png?v=designer',
      imageAlt: 'Add Route Stop callouts 7 to 8: modal title and route form',
      summary: addSummary,
    },
  ],
});

const adminTransportAllocationIntro =
  'Transport Allocation assigns students or staff to a vehicle, route, pickup point, and fee plan for the header academic year.';

const adminTransportAllocationInstruction =
  'Open Management, Transport, Transport Allocation. Add Allocation opens the form when create is allowed. Filter by status, user type, and vehicle. Edit and Delete use the row menu when permitted.';

const adminTransportAllocationListSummary =
  'Transport Allocation is at Management, Transport Allocation. Breadcrumb Dashboard, Management, Transport Allocation. Refresh reloads allocations for the header academic year. Export when allowed. Add Allocation opens the modal when create is allowed. Filter for status, user type, and vehicle with Apply and Reset. The table shows user, route, vehicle, pickup, fee plan, status, and Action.';

const adminTransportAllocationAddSummary =
  'Add Allocation selects user type, student or staff, vehicle, route, pickup point, transport fee plan, and effective dates. Save creates the allocation; Cancel closes. Bulk allocate and edit use related flows when shown and permitted.';

const transportAllocationChapter = (intro, instruction, listSummary, addSummary) => ({
  number: '46',
  title: 'Transport Allocation',
  intro,
  instruction,
  sections: [
    {
      number: '46.1',
      title: 'Transport Allocation',
      image: '/user-guides/admin/transport-allocation-annotated-1.png?v=designer',
      imageAlt:
        'Transport Allocation callouts 1 to 5: title, export menu, Add Allocation, list toolbar, and allocations table',
      summary: listSummary,
    },
    {
      number: '46.2',
      title: 'Add Allocation',
      image: '/user-guides/admin/transport-allocation-annotated-2.png?v=designer',
      imageAlt: 'Add Allocation callouts 6 to 7: modal title and allocation form',
      summary: addSummary,
    },
  ],
});

const bookCopiesChapter = listModalGuideChapter({
  number: '47',
  title: 'Book Copies',
  intro: 'Book Copies manages accession numbers and barcodes for each physical copy of catalogue titles.',
  instruction: 'Open Management, Library, Book Copies, or use Book Copies from the Books list. Add Copy creates a copy row when create is allowed.',
  listTitle: 'Book Copies',
  modalTitle: 'Add Copy',
  imageBase: 'library-copies',
  listCallouts: '1 to 6',
  modalCallouts: '7 to 8',
  listSummary:
    'Book Copies is at Management, Book Copies. Refresh and export when allowed. Trashed opens deleted copies. Add Copy opens the form. Filter and search narrow the copy list. The table shows book, accession, barcode, status, and Action.',
  modalSummary:
    'Add Copy selects the book title, accession or barcode fields, and status. Save adds the copy; Cancel closes. Edit uses the same form from the row menu.',
});

const hostelFloorsChapter = listModalGuideChapter({
  number: '48',
  title: 'Hostel Floors',
  intro: 'Hostel Floors organises each building by floor before rooms and beds are created.',
  instruction: 'Open Management, Hostel, Hostel Floors. Filter by hostel. Add floor opens the modal when create is allowed.',
  listTitle: 'Hostel Floors',
  modalTitle: 'Add floor',
  imageBase: 'hostel-floors',
  listCallouts: '1 to 4',
  modalCallouts: '5 to 6',
  listSummary:
    'Hostel Floors lists floors per hostel with filter, search, and export when allowed. Add floor opens the modal. The table shows floor name, number, wing, hostel, and Action.',
  modalSummary: 'Add floor sets hostel, floor name, floor number, optional wing, and status. Save and Cancel.',
});

const hostelRoomTypeChapter = listModalGuideChapter({
  number: '49',
  title: 'Room Type',
  intro: 'Room Type defines sharing capacity and amenities used when creating hostel rooms.',
  instruction: 'Open Management, Hostel, Room Type. Trashed restores deleted types. Add Room Type opens the form when create is allowed.',
  listTitle: 'Room Type',
  modalTitle: 'Add Room Type',
  imageBase: 'hostel-room-type',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Room Type lists types with export, Trashed, Add Room Type, filters, and the type table with capacity and status.',
  modalSummary: 'Add Room Type sets name, sharing capacity, amenities, description, and status. Save and Cancel.',
});

const hostelRoomsChapter = listModalGuideChapter({
  number: '50',
  title: 'Hostel Rooms',
  intro: 'Hostel Rooms links room numbers to a hostel, floor, and room type.',
  instruction: 'Open Management, Hostel, Hostel Rooms. Filter by hostel and status. Add Hostel Rooms opens the modal when create is allowed.',
  listTitle: 'Hostel Rooms',
  modalTitle: 'Add Hostel Rooms',
  imageBase: 'hostel-rooms',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Hostel Rooms lists rooms with Trashed, Add Hostel Rooms, filters, and columns for hostel, floor, type, occupancy, and status.',
  modalSummary: 'Add Hostel Rooms picks hostel, floor, room type, room number, capacity, and status. Save and Cancel.',
});

const hostelBedsChapter = listModalGuideChapter({
  number: '51',
  title: 'Hostel Beds',
  intro: 'Hostel Beds registers bed codes within a room for assignments.',
  instruction: 'Open Management, Hostel, Hostel Beds. Filter hostel, floor, and room. Add bed opens the modal when create is allowed.',
  listTitle: 'Hostel Beds',
  modalTitle: 'Add bed',
  imageBase: 'hostel-beds',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Hostel Beds lists beds with Trashed, Add bed, cascading filters, and bed status per room.',
  modalSummary: 'Add bed selects hostel, floor, room, bed number, optional position label, and status. Suggest fills the next free code when offered.',
});

const transportPickupChapter = listModalGuideChapter({
  number: '52',
  title: 'Pickup Points',
  intro: 'Pickup Points are stops used by routes and student allocations.',
  instruction: 'Open Management, Transport, Pickup Points. Add Pickup Point opens the form when create is allowed.',
  listTitle: 'Pickup Points',
  modalTitle: 'Add Pickup Point',
  imageBase: 'transport-pickup',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Pickup Points lists stops with export, Trashed, Add Pickup Point, filters, and distance or landmark columns.',
  modalSummary: 'Add Pickup Point sets name, landmark, distance from school, sequence, and status. Save and Cancel.',
});

const transportVehiclesChapter = listModalGuideChapter({
  number: '53',
  title: 'Vehicles',
  intro: 'Vehicles registers buses and vans available for route assignment and allocations.',
  instruction: 'Open Management, Transport, Vehicle. Add Vehicle opens the form when create is allowed.',
  listTitle: 'Vehicles',
  modalTitle: 'Add Vehicle',
  imageBase: 'transport-vehicles',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Vehicles lists fleet records with Trashed, Add Vehicle, filters, and capacity, registration, and status columns.',
  modalSummary: 'Add Vehicle captures registration, model, capacity, insurance dates, and status. Save and Cancel.',
});

const transportStaffChapter = listModalGuideChapter({
  number: '54',
  title: 'Transport Staff',
  intro: 'Transport Staff lists drivers and conductors with license details for assign vehicle.',
  instruction: 'Open Management, Transport, Transport Staff. Add Staff opens the form when create is allowed.',
  listTitle: 'Transport Staff',
  modalTitle: 'Add Staff',
  imageBase: 'transport-staff',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Transport Staff lists drivers and conductors with export, Trashed, Add Staff, and license document actions.',
  modalSummary: 'Add Staff sets name, role, contact, license number and expiry, and optional license upload. Save and Cancel.',
});

const transportAssignVehicleChapter = listModalGuideChapter({
  number: '55',
  title: 'Assign Vehicle',
  intro: 'Assign Vehicle links a vehicle to a route with driver and conductor for the year.',
  instruction: 'Open Management, Transport, Assign Vehicle. Assign Vehicle opens the form when create is allowed.',
  listTitle: 'Assign Vehicle',
  modalTitle: 'Assign Vehicle',
  imageBase: 'transport-assign-vehicle',
  listCallouts: '1 to 4',
  modalCallouts: '5 to 6',
  listSummary:
    'Assign Vehicle lists current route-vehicle links with export, Assign Vehicle, filters, and unassign actions.',
  modalSummary: 'Assign Vehicle selects vehicle, route, driver, conductor, and effective dates. Save assigns; Cancel closes.',
});

const transportFeesChapter = listModalGuideChapter({
  number: '56',
  title: 'Transport Fees',
  intro: 'Transport Fees defines fee plans by route or pickup used when allocating students.',
  instruction: 'Open Management, Transport, Transport Fees. Add Transport Fee opens the plan form when create is allowed.',
  listTitle: 'Transport Fees',
  modalTitle: 'Add Transport Fee',
  imageBase: 'transport-fees',
  listCallouts: '1 to 5',
  modalCallouts: '6 to 7',
  listSummary:
    'Transport Fees lists plans with Trashed, Add Transport Fee, filters, and student or staff amounts.',
  modalSummary: 'Add Transport Fee sets plan name, route or pickup scope, amounts, duration, and status. Save and Cancel.',
});

const financeAccountsChapter = moduleScreenChapter({
  number: '57',
  title: 'Finance & Accounts',
  intro:
    'Finance & Accounts records school income and expenses outside the student fee engine. Categories classify entries; Expenses and Income capture individual records; Transactions shows the combined ledger.',
  instruction:
    'Open Finance & Accounts, Accounts, then Categories, Expenses, Income, or Transactions. Pick the academic year or date filters on each page before you add or export. Create and edit need accounts permissions on the matching submodule. List screens use callouts 1–5; Add Category modal uses 6–7 (see ADMIN_CHAPTERS_56-63_CALLOUTS.md).',
  sections: [
    {
      title: 'Category List',
      path: 'Finance & Accounts → Accounts → Categories',
      image: '/user-guides/admin/finance-categories-annotated-1.png?v=callouts4',
      imageAlt:
        'Account Categories callouts 1 to 5: title, export menu, Add Category, Category List toolbar, and categories table',
      summary:
        'Account Categories is at Finance & Accounts, Categories. Breadcrumb Dashboard, Finance & Accounts, Categories. Export menu refreshes, prints, or exports PDF or Excel when allowed. Add Category opens the modal when create is allowed. Category List toolbar filters by type with Apply and Reset, row per page, and search. Categories table shows ID, Category, Type, Description, Status, and Action.',
    },
    {
      title: 'Add Category',
      path: 'Finance & Accounts → Accounts → Categories → Add Category',
      image: '/user-guides/admin/finance-categories-annotated-2.png?v=callouts4',
      imageAlt: 'Add Category callouts 6 to 7: modal title and category form fields',
      summary:
        'Add Category modal title Add Category or Edit Category. Category form collects name, type Income or Expense, optional description, and active status. Save stores the row; Cancel closes without saving.',
    },
    {
      title: 'Expenses',
      path: 'Finance & Accounts → Accounts → Expenses',
      image: '/user-guides/admin/finance-expenses-annotated-1.png?v=callouts4',
      imageAlt:
        'Expense callouts 1 to 5: title, export menu, Add Expenses, list toolbar, and expenses table',
      summary:
        'Expense is the expenses list. Export menu uses the active filters. Add Expenses opens entry when create is allowed. List toolbar sets date range, category, Apply, Reset, and search. Expenses table shows date, category, amount, payment mode, reference, and row actions.',
    },
    {
      title: 'Income',
      path: 'Finance & Accounts → Accounts → Income',
      image: '/user-guides/admin/finance-income-annotated-1.png?v=callouts4',
      imageAlt:
        'Income callouts 1 to 5: title, export menu, Add Income, list toolbar, and income table',
      summary:
        'Income records non-fee receipts. Export menu exports filtered rows. Add Income opens the form when permitted. List toolbar filters and searches the list. Income table shows posted lines with category, amount, date, and actions.',
    },
    {
      title: 'Transactions',
      path: 'Finance & Accounts → Accounts → Transactions',
      image: '/user-guides/admin/finance-transactions-annotated-1.png?v=callouts4',
      imageAlt:
        'Transactions callouts 1 to 5: title, export menu, filters, list toolbar, and ledger table',
      summary:
        'Transactions is the read-only ledger. Export menu prints or exports the filtered ledger. Filters set category, type, and dates with Apply and Reset. List toolbar has row per page and search. Transactions table lists date, description, category, and amounts; add or edit from Expenses or Income.',
    },
  ],
});

const announcementsChapter = moduleScreenChapter({
  number: '58',
  title: 'Announcements',
  intro:
    'Announcements publishes school-wide Events and Notice Board posts. Published items appear on dashboards and portals for the chosen audience.',
  instruction:
    'Open Announcements, Events or Notice Board. Select the academic year in the header when the form requires it. Create, edit, and delete need announcements permissions on the matching submodule.',
  sections: [
    {
      title: 'Events',
      path: 'Announcements → Events',
      image: '/user-guides/admin/announcements-events-annotated-1.png?v=callouts4',
      imageAlt: 'Events callouts 1 to 5: title, export menu, Add Event, list toolbar, and events table',
      summary:
        'Events lists school events. Export menu refreshes and exports when allowed. Add Event opens create or edit when create is allowed. List toolbar filters, sets row per page, and searches. Events table shows title, dates, venue, audience, status, and Action.',
    },
    {
      title: 'Notice Board',
      path: 'Announcements → Notice Board',
      image: '/user-guides/admin/announcements-notice-annotated-1.png?v=callouts4',
      imageAlt: 'Notice Board callouts 1 to 5: title, export menu, Add Notice, list toolbar, and notices table',
      summary:
        'Notice Board holds published notices. Export menu exports the filtered list. Add Notice opens the editor when permitted. List toolbar filters by audience or status and searches. Notices table shows title, audience, publish date, status, and Action. Dashboard Notice Board links View All here.',
    },
  ],
});

const reportsChapter = moduleScreenChapter({
  number: '59',
  title: 'Reports',
  intro:
    'Reports under the sidebar holds analytics that are separate from day-to-day lists. Each report opens with filters for class, dates, or academic year, then tables or charts you can print or export when export is allowed.',
  instruction:
    'Open Reports and choose the report type. Set filters and click Apply or Search before you export. Student Attendance Report also lives under HRM; Leave Report is detailed in chapter 24.4 of this guide. Callout numbers match ADMIN_CHAPTERS_56-63_CALLOUTS.md.',
  sections: [
    {
      title: 'Attendance Report',
      path: 'Reports → Attendance Report',
      image: '/user-guides/admin/report-attendance-annotated-1.png?v=callouts4',
      imageAlt: 'Attendance Report callouts 1 to 5: title, export, filters, toolbar, report output',
      summary:
        'Attendance Report title opens under Reports. Export menu prints or exports PDF or Excel for applied filters. Report filters set class, section, and date range with Apply and Reset. Toolbar has row per page and search when shown. Report output table or chart lists attendance aggregates.',
    },
    {
      title: 'Class Report',
      path: 'Reports → Class Report',
      image: '/user-guides/admin/report-class-annotated-1.png?v=callouts4',
      imageAlt: 'Class Report callouts 1 to 5: title, export, filters, toolbar, class summary table',
      summary:
        'Class Report title under Reports. Export menu exports the filtered class summary. Filters set academic year and class. Toolbar searches or paginates. Class report table shows class-wise metrics.',
    },
    {
      title: 'Student Report',
      path: 'Reports → Student Report',
      image: '/user-guides/admin/report-student-annotated-1.png?v=callouts4',
      imageAlt: 'Student Report callouts 1 to 5: title, export, filters, toolbar, student grid',
      summary:
        'Student Report title. Export menu exports student analytics. Filters narrow class, section, and status. Toolbar searches rows. Student report grid lists per-student columns.',
    },
    {
      title: 'Teacher Report',
      path: 'Reports → Teacher Report',
      image: '/user-guides/admin/report-teacher-annotated-1.png?v=callouts4',
      imageAlt: 'Teacher Report callouts 1 to 5: title, export, filters, toolbar, teacher table',
      summary:
        'Teacher Report title. Export menu exports visible rows. Filters set department or date scope. Toolbar paginates and searches. Teacher report table summarises activity or assignments.',
    },
    {
      title: 'Grade Report',
      path: 'Reports → Grade Report',
      image: '/user-guides/admin/report-grade-annotated-1.png?v=callouts4',
      imageAlt: 'Grade Report callouts 1 to 5: title, export, filters, toolbar, grade output',
      summary:
        'Grade Report title. Export menu exports grade summary. Filters pick exam, class, and subject. Toolbar sorts or searches. Grade report output shows performance bands or marks.',
    },
    {
      title: 'Leave Report',
      path: 'Reports → Leave Report',
      image: '/user-guides/admin/report-leave-annotated-1.png?v=callouts4',
      imageAlt: 'Leave Report callouts 1 to 5: title, export, Staff Report filters, toolbar, balance grid',
      summary:
        'Leave Report title. Export menu exports staff leave balances. Staff Report filters set date range, role, and leave type with Apply. Toolbar has row per page and search. Leave balance grid shows used and available days per type. See also chapter 24.4 for the designer annotated Leave Report.',
    },
    {
      title: 'Fees Report',
      path: 'Reports → Fees Report',
      image: '/user-guides/admin/report-fees-annotated-1.png?v=callouts4',
      imageAlt: 'Fees Report callouts 1 to 5: title, export, filters, toolbar, fees analytics table',
      summary:
        'Fees Report title. Export menu exports fee analytics. Filters set year, class, and status. Toolbar searches and paginates. Fees report table shows collection and outstanding columns.',
    },
    {
      title: 'Fee Engine Insights',
      path: 'Reports → Fee Engine Insights',
      image: '/user-guides/admin/report-fee-engine-annotated-1.png?v=callouts4',
      imageAlt: 'Fee Engine Insights callouts 1 to 5: title, actions, filters, KPI cards, detail tables',
      summary:
        'Fee Engine Insights title. Actions refresh summary or run fee-engine jobs when allowed. Filters scope academic year, class, or dates. KPI cards show outstanding, collections, and installment health. Detail tables break down charges or students below the cards.',
    },
  ],
});

const userManagementChapter = moduleScreenChapter({
  number: '60',
  title: 'User Management',
  intro:
    'User Management controls who can sign in and what each role may do. Users holds accounts; Roles & Permission defines module access; Delete Account Request reviews GDPR-style removal requests.',
  instruction:
    'Open User Management. Changes to roles affect every user with that role—verify in a test account before you save production permissions.',
  sections: [
    {
      title: 'Users',
      path: 'User Management → Users',
      image: '/user-guides/admin/users-manage-annotated-1.png?v=callouts4',
      imageAlt: 'Users callouts 1 to 5: title, export menu, Add User, list toolbar, users table',
      summary:
        'Users title under User Management. Export menu refreshes and exports the user list. Add User opens the form when create is allowed. List toolbar filters by role and searches usernames. Users table shows username, linked person, role, status, and Action.',
    },
    {
      title: 'Roles & Permission',
      path: 'User Management → Roles & Permission',
      image: '/user-guides/admin/roles-permission-annotated-1.png?v=callouts4',
      imageAlt: 'Roles & Permission callouts 1 to 5: title, export, role list, permission matrix',
      summary:
        'Roles & Permission title. Export menu refreshes when shown. Role list selects a role to edit. Permission matrix expands modules with toggles. Save applies permissions to every user with that role.',
    },
    {
      title: 'Delete Account Request',
      path: 'User Management → Delete Account Request',
      image: '/user-guides/admin/delete-account-annotated-1.png?v=callouts4',
      imageAlt: 'Delete Account Request callouts 1 to 5: title, export, filters, toolbar, requests table',
      summary:
        'Delete Account Request title. Export menu refreshes pending requests. Filters narrow status or date. Toolbar searches requesters. Requests table shows user, requested on, status, and Approve or Reject when allowed.',
    },
  ],
});

const membershipChapter = moduleScreenChapter({
  number: '61',
  title: 'Membership',
  intro:
    'Membership is your school SkoolWeb subscription—current plan, upgrades, and platform payment history. It is separate from student fee collection under Fees Collection.',
  instruction:
    'Open Membership from the sidebar or the dashboard Membership Plans shortcut. Plan changes may require super-admin billing; use Payment History to reconcile past platform invoices.',
  sections: [
    {
      title: 'My Subscription',
      path: 'Membership → My Subscription',
      image: '/user-guides/admin/membership-subscription-annotated-1.png?v=callouts4',
      imageAlt: 'My Subscription callouts 1 to 5: title, plan summary, usage, actions, status',
      summary:
        'My Subscription title shows the current SkoolWeb plan. Plan summary lists name, renewal or expiry, and module limits. Usage shows seats or storage when displayed. Actions link to upgrade or support. Status badge shows active, trial, or expired.',
    },
    {
      title: 'Available Plans',
      path: 'Membership → Available Plans',
      image: '/user-guides/admin/membership-plans-annotated-1.png?v=callouts4',
      imageAlt: 'Available Plans callouts 1 to 5: title, plan cards, pricing, select plan, compare',
      summary:
        'Available Plans title. Plan cards list tier names and features. Pricing shows monthly or yearly amounts. Select plan starts checkout when enabled. Compare highlights current plan versus upgrades.',
    },
    {
      title: 'Payment History',
      path: 'Membership → Payment History',
      image: '/user-guides/admin/membership-payments-annotated-1.png?v=callouts4',
      imageAlt: 'Payment History callouts 1 to 5: title, export, filters, toolbar, payments table',
      summary:
        'Payment History title. Export menu exports platform payments. Filters set date range or status. Toolbar searches and paginates. Payments table shows date, amount, mode, and receipt reference.',
    },
  ],
});

const settingsChapter = moduleScreenChapter({
  number: '62',
  title: 'Settings',
  intro:
    'Settings configures school-wide master data and defaults used across academic, people, and fees modules. Changes here affect every user—confirm values before you save.',
  instruction:
    'Open Settings and pick the screen you need. School Settings and Storage are school-level; Religion, Blood Groups, Mother Tongues, Castes, and Payment Modes are master lists; Fee Settings opens fee category, head, type, and master screens under the fees engine.',
  sections: [
    {
      title: 'School Settings',
      path: 'Settings → School Settings',
      image: '/user-guides/admin/settings-school-annotated-1.png?v=callouts5',
      imageAlt: 'School Settings callouts 1 to 5: title, profile, branding, regional, Save',
      summary:
        'School Settings title. School profile fields hold name, address, and contact. Branding uploads the logo for certificates and receipts. Regional options cover time zone or date format when shown. Save applies tenant-wide changes.',
    },
    {
      title: 'Storage',
      path: 'Settings → Storage',
      image: '/user-guides/admin/settings-storage-annotated-1.png?v=callouts5',
      imageAlt: 'Storage callouts 1 to 5: title, usage summary, breakdown, actions, alerts',
      summary:
        'Storage title. Usage summary shows total used versus quota. Breakdown lists uploads by module when available. Actions open cleanup or folder management when allowed. Alerts warn when storage is near the limit.',
    },
    {
      title: 'Religion',
      path: 'Settings → Religion',
      image: '/user-guides/admin/settings-religion-annotated-1.png?v=callouts5',
      imageAlt: 'Religion callouts 1 to 5: title, export, Add, toolbar, master table',
      summary:
        'Religion master list title. Export menu refreshes and exports. Add opens a new religion row when create is allowed. List toolbar searches entries. Master table shows name, status, and Edit or Delete.',
    },
    {
      title: 'Blood Groups',
      path: 'Settings → Blood Groups',
      image: '/user-guides/admin/settings-blood-groups-annotated-1.png?v=callouts5',
      imageAlt: 'Blood Groups callouts 1 to 5: title, export, Add, toolbar, master table',
      summary:
        'Blood Groups title. Export menu exports the list. Add opens a new blood group when permitted. List toolbar searches. Master table lists options used on student medical fields.',
    },
    {
      title: 'Mother Tongues',
      path: 'Settings → Mother Tongues',
      image: '/user-guides/admin/settings-mother-tongues-annotated-1.png?v=callouts5',
      imageAlt: 'Mother Tongues callouts 1 to 5: title, export, Add, toolbar, master table',
      summary:
        'Mother Tongues title. Export menu refreshes. Add creates a language option when allowed. List toolbar searches. Master table feeds student profile dropdowns.',
    },
    {
      title: 'Castes',
      path: 'Settings → Castes',
      image: '/user-guides/admin/settings-castes-annotated-1.png?v=callouts5',
      imageAlt: 'Castes callouts 1 to 5: title, export, Add, toolbar, master table',
      summary:
        'Castes title. Export menu exports entries. Add opens a new caste row when permitted. List toolbar searches. Master table lists values used on student records where enabled.',
    },
    {
      title: 'Payment Modes',
      path: 'Settings → Payment Modes',
      image: '/user-guides/admin/settings-payment-modes-annotated-1.png?v=callouts5',
      imageAlt: 'Payment Modes callouts 1 to 5: title, export, Add, toolbar, master table',
      summary:
        'Payment Modes title. Export menu exports modes. Add creates cash, card, UPI, or other methods when allowed. List toolbar searches. Master table lists modes offered during fee collection and accounts entry.',
    },
    {
      title: 'Fee Settings',
      path: 'Settings → Fee Settings → Fee Categories, Fee Heads, Fees Type, Fees Master',
      image: '/user-guides/admin/settings-fee-categories-annotated-1.png?v=callouts5',
      imageAlt:
        'Fee Categories (Fee Settings) callouts 1 to 5: title, export, Add, list toolbar, master table — same pattern on Fee Heads, Fees Type, and Fees Master',
      summary:
        'Fee Settings submenu opens Fee Categories, Fee Heads, Fees Type, and Fees Master. Each screen uses the same five callouts: title, export, Add, list toolbar, and master table. Screenshot shows Fee Categories; configure the fee catalog before Fee Groups and Collect Fees.',
    },
  ],
});

const helpSupportChapter = moduleScreenChapter({
  number: '63',
  title: 'Help & Support',
  intro:
    'Help & Support inside the signed-in app links to the Help Center, FAQs, and support tickets. The public marketing help site at skoolwebsite.f5universe.com/help is separate and hosts these User Guides.',
  instruction:
    'Open Help & Support, Help Center from the sidebar when you need in-app articles or to raise a ticket to the platform team.',
  sections: [
    {
      title: 'Help Center',
      path: 'Help & Support → Help Center',
      image: '/user-guides/admin/help-center-annotated-1.png?v=callouts5',
      imageAlt: 'Help Center callouts 1 to 5: title, categories, search, articles, support',
      summary:
        'Help Center title inside the signed-in app. Categories browse platform help topics. Search finds articles and FAQs. Articles open full guidance in a card or modal. Support links raise a ticket when enabled. Public User Guides live on the marketing /help site separately.',
    },
  ],
});

const pagesProfileChapter = moduleScreenChapter({
  number: '64',
  title: 'Pages',
  intro:
    'Pages holds personal account screens. Profile is where each signed-in user updates their own contact details and password.',
  instruction:
    'Open Pages, Profile. You can always edit your own profile; you do not need Peoples or HRM permissions for this menu.',
  sections: [
    {
      title: 'Profile',
      path: 'Pages → Profile',
      image: '/user-guides/admin/pages-profile-annotated-1.png?v=callouts5',
      imageAlt: 'Profile callouts 1 to 5: title, photo, contact fields, change password, Save',
      summary:
        'Profile title for the signed-in user. Photo upload or remove updates the avatar. Contact fields edit name, email, and phone. Change password sets current and new passwords. Save applies profile updates before you leave the page.',
    },
  ],
});

const teacherLeavesChapter = {
  number: '24',
  title: 'Leaves',
  intro: teacherLeavesIntro,
  instruction: teacherLeavesInstruction,
  sections: [
    {
      number: '24.1',
      title: 'My Leave & Attendance',
      image: '/user-guides/teacher/005-my-leave-attendance.png?v=portal5',
      imageAlt:
        'Teacher My Leave & Attendance: leave balances, Apply Leave, and pending earned leave rows',
      summary: teacherMyLeaveSummary,
    },
    {
      number: '24.2',
      title: 'Student Leaves',
      image: '/user-guides/teacher/028-student-leaves.png?v=portal5',
      imageAlt: 'Student Leaves: class student leave requests awaiting approval',
      summary: teacherStudentLeavesSummary,
    },
  ],
};

const teacherAssignmentsChapter = {
  number: '21',
  title: 'Teacher Assignments',
  intro:
    'Teacher Assignments is under Classes. It assigns class teachers and subject teachers for the academic year in the header.',
  instruction:
    'Open People ---> teachers then Teacher assignments. Use Class Teacher Assignments to set a class teacher for each section. Use Subject Teacher Assignments to link a teacher to a subject on a class and section. Pick the academic year in the header before you add or remove rows.',
  sections: [
    {
      number: '21.1',
      title: 'Class Teacher Assignments',
      image: '/user-guides/admin/teacher-assignments-1-5.png?v=ta1',
      imageAlt:
        'Teacher Assignments callouts 1 to 5: page title, Class Teacher Assignments tab, add form, current class assignments, and Remove',
      summary:
        'Teacher assignments opens from People ---> Teachers ---> Teacher assignments. Class Teacher Assignments is the first tab. Add Class Teacher Assignment picks Teacher, Class, Section, and Role, then Add saves the row. Current Class Assignments lists each class teacher with Class, Section, and Role filters and search. Remove deletes that assignment after you confirm.',
    },
    {
      number: '21.2',
      title: 'Subject Teacher Assignments',
      image: '/user-guides/admin/teacher-assignments-6-9.png?v=ta1',
      imageAlt:
        'Teacher Assignments callouts 6 to 9: Subject Teacher Assignments tab, add form, current subject assignments, and Remove',
      summary:
        'Subject Teacher Assignments assigns a teacher to a subject on a class and section. Add Subject Teacher Assignment picks Teacher, Class, Section, and Subject, then Add saves the row. Current Subject Assignments lists Teacher, Class, Section, Subject, and Type, with Class, Section, Subject, and Type filters and search. Remove deletes that assignment after you confirm.',
    },
  ],
};

const staffChapter = (intro, instruction, listSummary, personalSummary, saveSummary) => ({
  number: '22',
  title: 'Staffs',
  intro,
  instruction,
  sections: [
    {
      number: '22.1',
      title: 'Staff List',
      image: '/user-guides/admin/staff-annotated-1.png?v=designer',
      imageAlt: 'Staff List callouts 1 to 8: Staffs title, export menu, Add Staff, list title, Filter, Sort, Search, and table',
      summary: listSummary,
    },
    {
      number: '22.2',
      title: 'Add Staff — personal information',
      image: '/user-guides/admin/staff-annotated-2.png?v=designer',
      imageAlt: 'Add Staff callouts 9 and 10: page title and Personal Information',
      summary: personalSummary,
    },
    {
      number: '22.3',
      title: 'Documents, password, and save',
      image: '/user-guides/admin/staff-annotated-3.png?v=designer',
      imageAlt: 'Add Staff callouts 11 to 14: Documents, Password, Cancel, and Add Staff',
      summary: saveSummary,
    },
  ],
});

const teachersChapter = (intro, instruction, gridSummary, listSummary, personalSummary, saveSummary) => ({
  number: '20',
  title: 'Teachers',
  intro,
  instruction,
  sections: [
    {
      number: '20.1',
      title: 'Teachers grid',
      image: '/user-guides/admin/teachers-annotated-1.png?v=peoples1',
      imageAlt: 'Teachers grid callouts 1 to 8: page title, export menu, Add Teacher, date range, Filter, list or grid toggle, Sort, and a teacher card',
      summary: gridSummary,
    },
    {
      number: '20.2',
      title: 'Teacher List',
      image: '/user-guides/admin/teachers-annotated-2.png?v=peoples1',
      imageAlt: 'Teacher List callouts 9 to 14: page title, list title, Filter, Search, table, and Add Teacher',
      summary: listSummary,
    },
    {
      number: '20.3',
      title: 'Add Teacher — personal information',
      image: '/user-guides/admin/teachers-annotated-3.png?v=peoples1',
      imageAlt: 'Add Teacher callouts 15 and 16: page title and Personal Information',
      summary: personalSummary,
    },
    {
      number: '20.4',
      title: 'Documents, password, and save',
      image: '/user-guides/admin/teachers-annotated-4.png?v=peoples1',
      imageAlt: 'Add Teacher callouts 17 to 20: Documents, Password, Cancel, and Add Teacher',
      summary: saveSummary,
    },
  ],
});

/** Default teacher Peoples subs: students, parents, guardians only (no student_promotion or bonafide). */
const teacherStudentPromotionChapter = {
  number: '16',
  title: 'Student Promotion',
  intro:
    'Student Promotion is under Peoples, Students in the staff menu. It promotes students, marks school leaving, and shows promotion and leaving history.',
  instruction:
    'The default teacher role does not include this page. You only get Peoples → View on Students, Parents, and Guardians. Student Promotion is not in the menu unless the school adds it under Roles & Permissions. If it is added, you still cannot promote or mark leaving without the Promote Students action; export needs Export on that page.',
  sections: [],
};

const teacherBonafideChapter = {
  number: '17',
  title: 'Bonafide',
  intro:
    'Bonafide is under Peoples, Students. It searches by GR number and previews, prints, or downloads a bonafide certificate for the header academic year.',
  instruction:
    'The default teacher role does not include Bonafide. Peoples → View on Students does not open this generator. The school must grant Bonafide under Roles & Permissions before the menu item appears.',
  sections: [],
};

const portalStudentPromotionChapter = {
  number: '16',
  title: 'Student Promotion',
  intro:
    'Student Promotion is a staff page for moving students to another session or marking them as leaving school.',
  instruction:
    'The default parent and student roles do not include the Peoples module, so Student Promotion is not on your menu. Only school staff with Student Promotion access use it. You manage your child or your own profile from the student portal instead.',
  sections: [],
};

const portalBonafideChapter = {
  number: '17',
  title: 'Bonafide',
  intro:
    'Bonafide is a staff tool that builds a school bonafide certificate from a student GR number.',
  instruction:
    'The default parent and student roles do not include Bonafide. It is not on your menu. Ask the school office if you need a bonafide certificate.',
  sections: [],
};

const adminBonafideIntro =
  'Bonafide is under Peoples, Students. It builds a bonafide certificate from a student GR number for the academic year in the header.';

const adminBonafideInstruction =
  'Select the academic year in the header before you search. The student must be active and enrolled in that year. Generate only loads a preview; nothing is saved to the student record. Close returns to Students List.';

const adminBonafideFormSummary =
  'Enter the GR number and choose Generate. A blank GR number or a missing header year shows a warning. A wrong GR number for that year shows student not found.';

const adminBonafidePreviewSummary =
  'After a match, Print opens the browser print dialog. Download PDF saves the certificate in your current language. The preview shows the school logo, certificate title, student and parent names, GR and admission numbers, date of birth, religion, caste, class and section, academic year, school name, principal signature line, issue date, and school seal area. The school logo comes from school settings.';

const bonafideChapter = (intro, instruction, formSummary, previewSummary) => ({
  number: '17',
  title: 'Bonafide',
  intro,
  instruction,
  sections: [
    {
      number: '17.1',
      title: 'GR search, Close, and Generate',
      image: '/user-guides/admin/bonafide-annotated-1.png?v=designer',
      imageAlt: 'Bonafide callouts 1 to 5: title, search hint, GR Number, Close, and Generate',
      summary: formSummary,
    },
    {
      number: '17.2',
      title: 'Print, Download PDF, and certificate preview',
      image: '/user-guides/admin/bonafide-annotated-2.png?v=designer',
      imageAlt: 'Bonafide callouts 6 to 8: Print, Download PDF, and the certificate preview',
      summary: previewSummary,
    },
  ],
});

const studentPromotionChapter = (intro, instruction, setupSummary, listSummary, historySummary, leavingSetupSummary, leavingListSummary) => ({
  number: '16',
  title: 'Student Promotion',
  intro,
  instruction,
  sections: [
    {
      number: '16.1',
      title: 'Note, action mode, and promotion setup',
      image: '/user-guides/admin/student-promotion-annotated-1.png?v=designer',
      imageAlt: 'Student Promotion callouts 1 to 6: the page, note, action mode, setup, Reset Promotion, and Manage Promotion',
      summary: setupSummary,
    },
    {
      number: '16.2',
      title: 'Students list and Promote Students',
      image: '/user-guides/admin/student-promotion-annotated-2.png?v=designer',
      imageAlt: 'Student Promotion callouts 7 and 8: Students List and Promote Students',
      summary: listSummary,
    },
    {
      number: '16.3',
      title: 'Promotion history and export',
      image: '/user-guides/admin/student-promotion-annotated-3.png?v=designer',
      imageAlt: 'Student Promotion callouts 9 to 11: Promotion History, export, and the history table',
      summary: historySummary,
    },
    {
      number: '16.4',
      title: 'Leaving setup',
      image: '/user-guides/admin/student-promotion-annotated-4.png?v=designer',
      imageAlt: 'Student Promotion callout 12: Leaving setup',
      summary: leavingSetupSummary,
    },
    {
      number: '16.5',
      title: 'Mark leaving and leaving students',
      image: '/user-guides/admin/student-promotion-annotated-5.png?v=designer',
      imageAlt: 'Student Promotion callouts 13 to 15: select leaving, Mark as Leaving, and Leaving Students',
      summary: leavingListSummary,
    },
  ],
});

const withoutSection = (chapter, title) => ({
  ...chapter,
  sections: chapter.sections.filter((section) => section.title !== title),
});

const teacherAttendanceChapter = withoutSection(
  attendanceChapter(
    teacherAttendanceIntro,
    teacherAttendanceInstruction,
    adminStudentAttendanceSummary,
    adminStaffAttendanceSummary,
    adminStudentAttendanceReportSummary
  ),
  'Staff Attendance'
);

const numberChapters = (chapters) =>
  chapters.map((chapter, index) => {
    const number = String(index + 1);
    return {
      ...chapter,
      number,
      sections: (chapter.sections || []).map((section, sectionIndex) => ({
        ...section,
        number: `${number}.${sectionIndex + 1}`,
      })),
    };
  });

export const USER_GUIDES = {
  admin: {
    title: 'School Administrator',
    chapters: [
      loginChapter,
      {
        number: '2',
        title: 'Dashboard',
        intro:
          'After sign-in, this is the Headmaster Dashboard. The screenshots follow the numbered callouts in order.',
        sections: [
          {
            number: '2.1',
            title: "Dashboard, calendar, attendance, links, events, and performance",
            image: '/user-guides/admin/dashboard-1-6.png?v=dash1',
            imageAlt: "Headmaster Dashboard callouts 1 to 6: Dashboard, calendar, attendance, Quick Links, Upcoming Events, and Performance",
            caption: "Figure 2.1 — Points 1 to 6",
            summary:
              "Dashboard in the side menu opens this page, the overview after sign-in. The four cards count students, teachers, staff, and subjects, each split into active and inactive. The cards do not open another page. The calendar shows the current month. The arrows move to the previous or next month, and the highlighted date is today. Attendance has a Students tab and a Staff tab. The range can be Today, Yesterday, This week, This month, or All time. It shows Present, Absent, and Late, and it does not mark attendance. View All on Students opens student attendance, and View All on Staff opens staff attendance. Quick Links shows four links at a time. Meetings, Exam Result, Fees, and Home Works come first. Slide to reach Attendance and Reports. Each link opens that page. Upcoming Events lists what is still to come. If there is nothing, it says there are no upcoming events. Add Event opens the events page so you can add one. Performance counts students by exam marks for the class you pick, and for the year in the header. Strong is 75 percent or more, Satisfactory is from 40 percent up to 75, and Below 40 is under 40 percent. It also shows the school average score and the pass rate.",
          },
          {
            number: '2.2',
            title: "Total Teachers, Total Staff, Total Subjects, and Calendar",
            image: '/user-guides/admin/dashboard-5-8.jpg?v=hires',
            imageAlt: "Headmaster Dashboard callouts 5 to 8: Total Teachers, Total Staff, Total Subjects, and Calendar",
            caption: "Figure 2.2 — Points 5 to 8",
            summary:
              "Total Teachers, Total Staff, and Total Subjects are school counts, each split into active and inactive. These three cards do not open another page. The calendar moves month by month, and from it you can see upcoming events or add a new one.",
          },
          {
            number: '2.3',
            title: "Attendance, Quick Links, Upcoming Events, and Performance",
            image: '/user-guides/admin/dashboard-9-12.jpg?v=hires',
            imageAlt: "Headmaster Dashboard callouts 9 to 12: Attendance, Quick Links, Upcoming Events, and Performance",
            caption: "Figure 2.3 — Points 9 to 12",
            summary:
              "The attendance card summarises who is present, absent, or late for students or staff. It does not mark attendance. View All opens the real attendance page. Quick Links jump to meetings, results, fees, homework, and reports. Performance groups students by their exam marks for the class and the academic year selected in the header.",
          },
          {
            number: '2.4',
            title: "Top teachers, Top students, Fees Collection, and Leave Requests",
            image: '/user-guides/admin/dashboard-13-16.jpg?v=hires',
            imageAlt: "Headmaster Dashboard callouts 13 to 16: Top teachers, Top students, Fees Collection, and Leave Requests",
            caption: "Figure 2.4 — Points 13 to 16",
            summary:
              "Top teachers are the staff with the most timetable periods, and Top students are the highest exam averages. A name opens that person\u2019s record. Fees Collection compares money already collected with the fee due for the period you pick, and that same period also filters income and expenses lower on the page. Leave Requests only lists recent leave. It does not approve or reject it.",
          },
          {
            number: '2.5',
            title: "View Attendance, New Events, Membership Plans, and Finance & Fees",
            image: '/user-guides/admin/dashboard-17-20.png?v=hires',
            imageAlt: "Headmaster Dashboard callouts 17 to 20: View Attendance, New Events, Membership Plans, and Finance and Fees",
            caption: "Figure 2.5 — Points 17 to 20",
            summary:
              "These four shortcuts leave the dashboard. View Attendance opens student attendance, and New Events opens the events list. Membership Plans opens the school\u2019s current plan. Finance & Fees opens Collect Fees, the same page as Fees Details.",
          },
          {
            number: '2.6',
            title: "Total Income, Notice Board, Total Fees Collected, and Library fines",
            image: '/user-guides/admin/dashboard-21-24.jpg?v=hires',
            imageAlt: "Headmaster Dashboard callouts 21 to 24: Total Income, Notice Board, Total Fees Collected, and Library fines",
            caption: "Figure 2.6 — Points 21 to 24",
            summary:
              "Total Income adds confirmed fee receipts, library fines on returned books, and other income for the academic year and the fee period selected above. Notice Board shows the latest notices, and View All opens the full board. Total Fees Collected is the live amount already received. Library fines count only books that have been returned, not the fee period.",
          },
          {
            number: '2.7',
            title: "Total Expenses, pending fees, Top Subjects, and Student Activity",
            image: '/user-guides/admin/dashboard-25-29.png?v=hires',
            imageAlt: "Dashboard selected items callouts 25 to 29: Total Expenses, Student Pending Amount, Student Not Paid, Top Subjects, and Student Activity",
            caption: "Figure 2.7 — Points 25 to 29",
            summary:
              "Total Expenses are the costs recorded for the selected year and fee period. The pending-fee cards show how much is still unpaid and how many students have not paid. Top Subjects ranks exam averages for the class you choose, including subjects that have a timetable but no marks yet. Student Activity lists the latest school actions, such as a new admission or a leave request.",
          }
        ],
      },
      todoChapter,
      academicYearsChapter,
      homeworkChapter(adminHomeworkIntro, adminHomeworkInstruction),
      {
        number: '6',
        title: 'Timetable',
        intro:
          'Timetable has three pages: Time slots, Create timetable, and Section routine. Time slots are the periods and breaks for the school day, and the same list is used for every class.',
        instruction:
          'You can add time slots before any class exists. Create timetable needs those active slots, plus a current academic year, a class and its section, a subject on that class, and a teacher assigned to that class and subject. Section routine uses the same setup. A new slot cannot overlap a slot already in the list.',
        sections: [
          {
            number: '6.1',
            title: 'The list, Bulk Generate, Add Time Slot, and Edit or Delete',
            image: '/user-guides/admin/time-slots-1-3.jpg?v=hires',
            imageAlt: 'Time slots callouts: the list, Bulk Generate, Add Time Slot, and Edit or Delete',
            summary:
              'Inactive slots stay on this list, but Create timetable and Section routine use only the active ones. Bulk Generate fills the day from a start, an end, and a period length, and it skips any time that would overlap a slot already there. One slot is added on its own. Edit can also turn a slot off, and Delete removes it after you confirm.',
          },
          {
            number: '6.2',
            title: 'Slot name, times, break, and save',
            image: '/user-guides/admin/time-slots-form.png?v=hires',
            imageAlt: 'Add time slot form: slot name, start time, end time, break, and save',
            summary:
              'The end has to be later than the start, and the times cannot overlap a slot already in the list. Two slots may touch, so one can end at the same minute the next one starts. Mark it as a break when no subject should be taught then. A break still shows on the week, but Generate will not place a subject on it. A new slot is saved as active, so it appears on the timetable straight away.',
          },
          {
            number: '6.3',
            title: 'Create timetable, copy, feasibility, generate, and reset',
            image: '/user-guides/admin/create-timetable-1-5.png?v=hires',
            imageAlt: 'Create timetable callouts 1 to 5: the page, Copy and Paste, Check feasibility, Generate, and Reset Routine',
            summary:
              'Copy keeps this class routine so it can be pasted onto a different class. Periods already on that class stay, and a period is skipped when it clashes or when that class does not have the same subject and teacher. Check feasibility only reports whether a timetable can be built. It does not save anything. Generate replaces unlocked periods and keeps locked ones. Reset deletes every period for this class and section, including locked ones, after you confirm.',
          },
          {
            number: '6.4',
            title: 'Class and section, and Add Class',
            image: '/user-guides/admin/create-timetable-6-7.png?v=hires',
            imageAlt: 'Create timetable callouts 6 and 7: class and section, and Add Class',
            summary:
              'The week stays closed until a class and then its section are chosen, and choosing another class clears the section. A class with no sections is scheduled as the whole class. Add Class is how a subject is placed on one day and period. A locked period has to be unlocked before it can be changed or removed.',
          },
          {
            number: '6.5',
            title: 'Subject, teacher, room, and Apply to Timetable',
            image: '/user-guides/admin/create-timetable-8-9.jpg?v=hires',
            imageAlt: 'Schedule slot callouts 8 and 9: subject, teacher, room, parallel elective, and Apply to Timetable',
            summary:
              'The teacher list is only people already assigned to that subject on this class, and both subject and teacher are required. Room can stay empty for a single subject. A parallel elective is another subject in the same period, and each one needs a different room. A compulsory subject cannot sit in that parallel slot. Apply to Timetable only places the choice on the grid. It is stored when you save.',
          },
          {
            number: '6.6',
            title: 'Save Assignments',
            image: '/user-guides/admin/create-timetable-10.png?v=hires',
            imageAlt: 'Create timetable callout 10: Save Assignments',
            summary:
              'Save Assignments writes this class and section week into the school record. The button stays off until a period has actually changed, so an unchanged week is not saved again. Locked periods are left as they are until you unlock them.',
          },
          {
            number: '6.7',
            title: 'Section routine, class and section, and the weekly timetable',
            image: '/user-guides/admin/section-routine.png?v=tutorial',
            imageAlt: 'Section routine callouts 1 to 3: the view-only page, class and section, and the weekly timetable',
            summary:
              'This page reads the week already saved in Create timetable. Nothing here can add or change a period. The week stays hidden until both the class and the section are chosen, and a new class clears the section. Breaks remain in the columns. A taught period shows the subject, teacher, and room, and an empty one shows a dash. Export and print work only after both are selected.',
          },
        ],
      },
      meetingsChapter(adminMeetingsIntro, adminMeetingsInstruction, adminMeetingsSummary, adminAllMeetingsSections),
      examsChapter(
        adminExamsIntro,
        adminExamsInstruction,
        adminExamCreateSummary,
        adminExamListSummary,
        adminExamSetupSummary,
        adminExamTimetableSummary,
        adminExamFiltersSummary,
        adminExamResultSections
      ),
      assembliesChapter(
        adminAssembliesIntro,
        adminAssembliesInstruction,
        adminAssembliesSummary,
        adminAssemblySections
      ),
      enquiriesChapter(
        adminEnquiriesIntro,
        adminEnquiriesInstruction,
        adminEnquiriesPageSummary,
        adminEnquiriesListSummary
      ),
      classesChapter,
      studentGroupsChapter(
        adminStudentGroupsIntro,
        adminStudentGroupsInstruction,
        adminStudentGroupsListSummary,
        adminStudentGroupsFormSummary,
        adminStudentGroupsMembersSummary,
        adminStudentGroupsPointsSummary
      ),
      syllabusChapter(
        adminSyllabusIntro,
        adminSyllabusInstruction,
        adminSyllabusBooksSummary,
        adminSyllabusBookFormSummary,
        adminSyllabusBookFilesSummary,
        adminSyllabusChaptersSummary,
        adminSyllabusChapterFormSummary,
        adminSyllabusFilesSummary,
        adminSyllabusProgressSummary,
        adminSyllabusProgressListsSummary
      ),
      subjectsChapter,
      studentsChapter(
        adminStudentsIntro,
        adminStudentsInstruction,
        adminStudentsListSummary,
        adminStudentsImportSummary,
        adminStudentsFormTopSummary,
        adminStudentsPersonalSummary,
        adminStudentsFatherSummary,
        adminStudentsGuardianSummary,
        adminStudentsMoreSummary
      ),
      studentPromotionChapter(
        adminStudentPromotionIntro,
        adminStudentPromotionInstruction,
        adminStudentPromotionSetupSummary,
        adminStudentPromotionListSummary,
        adminStudentPromotionHistorySummary,
        adminStudentPromotionLeavingSetupSummary,
        adminStudentPromotionLeavingListSummary
      ),
      bonafideChapter(
        adminBonafideIntro,
        adminBonafideInstruction,
        adminBonafideFormSummary,
        adminBonafidePreviewSummary
      ),
      parentsChapter(
        adminParentsIntro,
        adminParentsInstruction,
        adminParentsGridSummary,
        adminParentsListSummary,
        adminParentsFormSummary,
        adminParentsSaveSummary
      ),
      guardiansChapter(
        adminGuardiansIntro,
        adminGuardiansInstruction,
        adminGuardiansGridSummary,
        adminGuardiansListSummary,
        adminGuardiansFormSummary
      ),
      teachersChapter(
        adminTeachersIntro,
        adminTeachersInstruction,
        adminTeachersGridSummary,
        adminTeachersListSummary,
        adminTeachersPersonalSummary,
        adminTeachersSaveSummary
      ),
      teacherAssignmentsChapter,
      staffChapter(
        adminStaffIntro,
        adminStaffInstruction,
        adminStaffListSummary,
        adminStaffPersonalSummary,
        adminStaffSaveSummary
      ),
      attendanceChapter(
        adminAttendanceIntro,
        adminAttendanceInstruction,
        adminStudentAttendanceSummary,
        adminStaffAttendanceSummary,
        adminStudentAttendanceReportSummary
      ),
      leavesChapter(
        adminLeavesIntro,
        adminLeavesInstruction,
        adminLeaveTypesSummary,
        adminListLeavesSummary,
        adminApproveLeaveSummary,
        adminLeaveReportSummary
      ),
      holidaysChapter(
        adminHolidaysIntro,
        adminHolidaysInstruction,
        adminHolidaysSummary
      ),
      departmentsChapter(
        adminDepartmentsIntro,
        adminDepartmentsInstruction,
        adminDepartmentListSummary,
        adminDepartmentAddSummary
      ),
      designationChapter(
        adminDesignationIntro,
        adminDesignationInstruction,
        adminDesignationListSummary,
        adminDesignationAddSummary
      ),
      payrollChapter(
        adminPayrollIntro,
        adminPayrollInstruction,
        adminPayrollListSummary,
        adminPayrollSlipSummary
      ),
      feeGroupsChapter(
        adminFeeGroupsIntro,
        adminFeeGroupsInstruction,
        adminFeeGroupsListSummary,
        adminFeeGroupsAddSummary
      ),
      collectFeesChapter(
        adminCollectFeesIntro,
        adminCollectFeesInstruction,
        adminCollectFeesListSummary,
        adminCollectFeesModalSummary
      ),
      discountRulesChapter(
        adminDiscountRulesIntro,
        adminDiscountRulesInstruction,
        adminDiscountRulesListSummary,
        adminDiscountRulesCreateSummary,
        adminDiscountRulesAssignSummary
      ),
      feesAssignChapter(
        adminFeesAssignIntro,
        adminFeesAssignInstruction,
        adminFeesAssignListSummary,
        adminFeesAssignModalSummary
      ),
      feeWalletChapter(
        adminFeeWalletIntro,
        adminFeeWalletInstruction,
        adminFeeWalletSummary
      ),
      feeAdjustmentsChapter(
        adminFeeAdjustmentsIntro,
        adminFeeAdjustmentsInstruction,
        adminFeeAdjustmentsSummary
      ),
      receiptHistoryChapter(
        adminReceiptHistoryIntro,
        adminReceiptHistoryInstruction,
        adminReceiptHistorySummary
      ),
      libraryMembersChapter(
        adminLibraryMembersIntro,
        adminLibraryMembersInstruction,
        adminLibraryMembersListSummary,
        adminLibraryMembersAddSummary
      ),
      libraryCategoriesChapter(
        adminLibraryCategoriesIntro,
        adminLibraryCategoriesInstruction,
        adminLibraryCategoriesListSummary,
        adminLibraryCategoriesAddSummary
      ),
      libraryPoliciesChapter(
        adminLibraryPoliciesIntro,
        adminLibraryPoliciesInstruction,
        adminLibraryPoliciesListSummary,
        adminLibraryPoliciesAddSummary
      ),
      libraryBooksChapter(
        adminLibraryBooksIntro,
        adminLibraryBooksInstruction,
        adminLibraryBooksListSummary,
        adminLibraryBooksAddSummary
      ),
      issueBooksChapter(
        adminIssueBooksIntro,
        adminIssueBooksInstruction,
        adminIssueBooksListSummary,
        adminIssueBooksModalSummary
      ),
      returnBooksChapter(
        adminReturnBooksIntro,
        adminReturnBooksInstruction,
        adminReturnBooksListSummary
      ),
      libraryReservationsChapter(
        adminLibraryReservationsIntro,
        adminLibraryReservationsInstruction,
        adminLibraryReservationsListSummary,
        adminLibraryReservationsAddSummary
      ),
      hostelListChapter(
        adminHostelListIntro,
        adminHostelListInstruction,
        adminHostelListListSummary,
        adminHostelListAddSummary
      ),
      hostelAssignmentsChapter(
        adminHostelAssignmentsIntro,
        adminHostelAssignmentsInstruction,
        adminHostelAssignmentsListSummary,
        adminHostelAssignmentsAddSummary
      ),
      transportRoutesChapter(
        adminTransportRoutesIntro,
        adminTransportRoutesInstruction,
        adminTransportRoutesListSummary,
        adminTransportRoutesAddSummary
      ),
      transportAllocationChapter(
        adminTransportAllocationIntro,
        adminTransportAllocationInstruction,
        adminTransportAllocationListSummary,
        adminTransportAllocationAddSummary
      ),
      bookCopiesChapter,
      hostelFloorsChapter,
      hostelRoomTypeChapter,
      hostelRoomsChapter,
      hostelBedsChapter,
      transportPickupChapter,
      transportVehiclesChapter,
      transportStaffChapter,
      transportAssignVehicleChapter,
      transportFeesChapter,
      financeAccountsChapter,
      announcementsChapter,
      reportsChapter,
      userManagementChapter,
      membershipChapter,
      settingsChapter,
      helpSupportChapter,
      pagesProfileChapter,
    ],
  },
  teacher: {
    title: 'Teacher',
    chapters: numberChapters([
      loginChapter,
      teacherDashboardChapter,
      todoChapter,
      homeworkChapter(teacherHomeworkIntro, teacherHomeworkInstruction),
      teacherMeetingsChapter(
        teacherMeetingsIntro,
        teacherMeetingsInstruction,
        teacherMeetingsSummary,
        teacherAllMeetingsSections
      ),
      teacherExamsChapter,
      teacherAssembliesChapter(
        teacherAssembliesIntro,
        teacherAssembliesInstruction,
        teacherAssembliesSummary,
        teacherAssemblySections
      ),
      enquiriesChapter(
        teacherEnquiriesIntro,
        teacherEnquiriesInstruction,
        teacherEnquiriesPageSummary,
        teacherEnquiriesListSummary
      ),
      studentGroupsChapter(
        teacherStudentGroupsIntro,
        teacherStudentGroupsInstruction,
        teacherStudentGroupsListSummary,
        teacherStudentGroupsFormSummary,
        teacherStudentGroupsMembersSummary,
        teacherStudentGroupsPointsSummary
      ),
      syllabusChapter(
        teacherSyllabusIntro,
        teacherSyllabusInstruction,
        teacherSyllabusBooksSummary,
        teacherSyllabusBookFormSummary,
        teacherSyllabusBookFilesSummary,
        teacherSyllabusChaptersSummary,
        teacherSyllabusChapterFormSummary,
        teacherSyllabusFilesSummary,
        teacherSyllabusProgressSummary,
        teacherSyllabusProgressListsSummary
      ),
      teacherStudentsChapter,
      teacherParentsChapter,
      teacherGuardiansChapter,
      teacherAttendanceChapter,
      teacherMyLeaveChapter,
      teacherStudentLeavesChapter,
      teacherProfileChapter,
    ]),
  },
  parent: {
    title: 'Parent',
    chapters: numberChapters([
      loginChapter,
      ...parentPortalMainChapters,
      portalParentLeavesChapter,
      portalParentAttendanceChapter,
    ]),
  },
  student: {
    title: 'Student',
    chapters: numberChapters([
      loginChapter,
      ...studentPortalMainChapters,
      portalStudentLeavesChapter,
      portalStudentAttendanceChapter,
    ]),
  },
};
